// ============================================
// Pi.insure - Complete React Frontend
// AI-Powered Insurance Management System
// ============================================

import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Shield, Users, Building2, FileText, FolderOpen, Upload, Download, 
  LogOut, Menu, X, ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, Bell,
  Calendar, DollarSign, Clock, Search, Plus, Edit, Trash2, Eye,
  MessageSquare, Send, RefreshCw, Settings, Home, BarChart3, 
  CheckCircle, XCircle, AlertCircle, Info, Lock, Mail, User, Activity, LayoutDashboard, Filter
} from 'lucide-react';

// ============================================
// API Configuration
// ============================================
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return;
    }
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Request failed');
    return data;
  },
  
  get: (endpoint) => api.request(endpoint),
  post: (endpoint, body) => api.request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body) => api.request(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (endpoint) => api.request(endpoint, { method: 'DELETE' }),
  
  async upload(endpoint, formData) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData
    });
    return response.json();
  }
};

// ============================================
// Auth Context
// ============================================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);
  
  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };
  
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };
  
  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// Protected Route
// ============================================
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  
  



  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

// ============================================
// Loading Screen
// ============================================
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Shield className="w-16 h-16 text-blue-600 mx-auto animate-pulse" />
        <p className="mt-4 text-slate-600">Loading Pi.insure...</p>
      </div>
    </div>
  );
}

// ============================================
// Toast Notifications
// ============================================
function Toast({ message, type = 'info', onClose }) {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <XCircle className="w-5 h-5 text-red-500" />,
    warning: <AlertCircle className="w-5 h-5 text-amber-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />
  };
  
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border p-4 flex items-center gap-3 z-50 animate-slide-up">
      {icons[type]}
      <p className="text-slate-700">{message}</p>
      <button onClick={onClose} className="text-[#888888] hover:text-slate-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================
// Login Page
// ============================================


// Status Badge Component for installment/survey status

function StatusBadge({ status, type = "installment" }) {

  const getStatusStyles = () => {

    const s = (status || "").toLowerCase();

    if (type === "installment") {

      if (s === "paid") return "bg-green-100 text-green-700";

      if (s === "pending") return "bg-yellow-100 text-yellow-700";

      if (s === "overdue") return "bg-red-100 text-red-700";

      return "bg-slate-100 text-slate-500";

    } else if (type === "survey") {

      if (s === "completed") return "bg-green-100 text-green-700";

      if (s === "scheduled") return "bg-blue-100 text-blue-700";

      if (s === "pending") return "bg-yellow-100 text-yellow-700";

      return "bg-slate-100 text-slate-500";

    } else if (type === "compliance") {

      if (s === "compliant") return "bg-green-100 text-green-700";

      if (s === "non-compliant") return "bg-red-100 text-red-700";

      if (s === "pending") return "bg-yellow-100 text-yellow-700";

      return "bg-slate-100 text-slate-500";

    }

    return "bg-slate-100 text-slate-500";

  };

  return (

    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusStyles()}`}>

      {status || "N/A"}

    </span>

  );

}


function LoginPage() {

  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const { login, user } = useAuth();

  const navigate = useNavigate();

  

  useEffect(() => {

    if (user) navigate('/dashboard');

  }, [user, navigate]);

  

  const handleSubmit = async (e) => {

    e.preventDefault();

    setError('');

    setLoading(true);

    

    try {

      await login(email, password);

      navigate('/dashboard');

    } catch (err) {

      setError(err.message || 'Login failed');

    } finally {

      setLoading(false);

    }

  };

  

  return (

    <div className="min-h-screen flex" style={{ backgroundColor: '#1a1a1a' }}>

      {/* Left Side - Branding */}

      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>

        <div className="text-center">

          <img src="/logos/pi-logo.svg" alt="Pi.insure" className="w-32 h-32 mx-auto mb-8" />

          <h1 className="text-4xl font-bold text-white mb-4">Pi.insure</h1>

          <p className="text-xl mb-2" style={{ color: '#C45C26' }}>AI-Powered Insurance Management</p>

          <p className="text-gray-400 max-w-md mx-auto mt-6">

            Streamline your insurance portfolio with intelligent document processing, automated renewals tracking, and comprehensive policy management.

          </p>

        </div>

        <div className="mt-16 grid grid-cols-3 gap-8 text-center">

          <div>

            <p className="text-3xl font-bold" style={{ color: '#C45C26' }}>100+</p>

            <p className="text-gray-500 text-sm">Policies Managed</p>

          </div>

          <div>

            <p className="text-3xl font-bold" style={{ color: '#C45C26' }}>$50M+</p>

            <p className="text-gray-500 text-sm">Coverage Tracked</p>

          </div>

          <div>

            <p className="text-3xl font-bold" style={{ color: '#C45C26' }}>24/7</p>

            <p className="text-gray-500 text-sm">AI Monitoring</p>

          </div>

        </div>

      </div>



      {/* Right Side - Login Form */}

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ backgroundColor: '#F5F0E8' }}>

        <div className="w-full max-w-md">

          {/* Mobile Logo */}

          <div className="lg:hidden text-center mb-8">

            <img src="/logos/pi-logo.svg" alt="Pi.insure" className="w-20 h-20 mx-auto mb-4" />

            <h1 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Pi.insure</h1>

          </div>



          <div className="bg-white rounded-2xl shadow-xl p-8 border" style={{ borderColor: '#E5E5E5' }}>

            <div className="text-center mb-8">

              <h2 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Welcome Back</h2>

              <p className="mt-2" style={{ color: '#6B6B6B' }}>Sign in to your account</p>

            </div>



            {error && (

              <div className="mb-6 p-4 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>

                <div className="flex items-center gap-2">

                  <AlertCircle className="w-5 h-5" />

                  {error}

                </div>

              </div>

            )}



            <form onSubmit={handleSubmit} className="space-y-5">

              <div>

                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D2D2D' }}>Email Address</label>

                <div className="relative">

                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#9CA3AF' }} />

                  <input

                    type="email"

                    value={email}

                    onChange={(e) => setEmail(e.target.value)}

                    className="w-full pl-12 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"

                    style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAF9' }}

                    placeholder="you@company.com"

                    required

                  />

                </div>

              </div>



              <div>

                <label className="block text-sm font-semibold mb-2" style={{ color: '#2D2D2D' }}>Password</label>

                <div className="relative">

                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#9CA3AF' }} />

                  <input

                    type="password"

                    value={password}

                    onChange={(e) => setPassword(e.target.value)}

                    className="w-full pl-12 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all"

                    style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAF9' }}

                    placeholder="Enter your password"

                    required

                  />

                </div>

              </div>



              <button

                type="submit"

                disabled={loading}

                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50"

                style={{ backgroundColor: '#C45C26' }}

              >

                {loading ? (

                  <span className="flex items-center justify-center gap-2">

                    <RefreshCw className="w-5 h-5 animate-spin" />

                    Signing in...

                  </span>

                ) : (

                  'Sign In'

                )}

              </button>

            </form>



            <p className="mt-6 text-center text-sm" style={{ color: '#6B6B6B' }}>

              Forgot password? <a href="/forgot-password" className="font-semibold hover:underline" style={{ color: '#C45C26' }}>Click here to reset</a>

            </p>

          </div>



          <p className="mt-8 text-center text-xs" style={{ color: '#9CA3AF' }}>

            © 2026 Pi.insure. All rights reserved.

          </p>

        </div>

      </div>

    </div>

  );

}



// ============================================
// Dashboard Layout
// ============================================



// ============================================

// Forgot Password Page

// ============================================

function ForgotPasswordPage() {

  const [email, setEmail] = useState('');

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);

  const [error, setError] = useState('');



  const handleSubmit = async (e) => {

    e.preventDefault();

    setError('');

    setLoading(true);

    try {

      await api.post('/auth/forgot-password', { email });

      setSuccess(true);

    } catch (err) {

      setError(err.message || 'Failed to send reset email');

    } finally {

      setLoading(false);

    }

  };



  return (

    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#F5F0E8' }}>

      <div className="w-full max-w-md">

        <div className="text-center mb-8">

          <img src="/logos/pi-logo.svg" alt="Pi.insure" className="w-20 h-20 mx-auto mb-4" />

          <h1 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Pi.insure</h1>

        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border" style={{ borderColor: '#E5E5E5' }}>

          {success ? (

            <div className="text-center">

              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#D1FAE5' }}>

                <CheckCircle className="w-8 h-8" style={{ color: '#059669' }} />

              </div>

              <h2 className="text-xl font-bold mb-2" style={{ color: '#2D2D2D' }}>Check Your Email</h2>

              <p className="mb-6" style={{ color: '#6B6B6B' }}>If an account exists for {email}, we've sent a password reset link.</p>

              <a href="/login" className="font-semibold hover:underline" style={{ color: '#C45C26' }}>Back to Login</a>

            </div>

          ) : (

            <>

              <div className="text-center mb-6">

                <h2 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Forgot Password?</h2>

                <p className="mt-2" style={{ color: '#6B6B6B' }}>Enter your email to receive a reset link</p>

              </div>

              {error && <div className="mb-4 p-4 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>{error}</div>}

              <form onSubmit={handleSubmit}>

                <div className="mb-6">

                  <label className="block text-sm font-semibold mb-2" style={{ color: '#2D2D2D' }}>Email Address</label>

                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}

                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"

                    style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAFA' }}

                    placeholder="Enter your email" required />

                </div>

                <button type="submit" disabled={loading}

                  className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"

                  style={{ backgroundColor: '#C45C26' }}>

                  {loading ? 'Sending...' : 'Send Reset Link'}

                </button>

              </form>

              <div className="mt-6 text-center">

                <a href="/login" className="text-sm font-medium hover:underline" style={{ color: '#6B6B6B' }}>Back to Login</a>

              </div>

            </>

          )}

        </div>

      </div>

    </div>

  );

}



// ============================================

// Reset Password Page

// ============================================

function ResetPasswordPage() {

  const [password, setPassword] = useState('');

  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);

  const [error, setError] = useState('');

  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);

  const token = searchParams.get('token');



  const handleSubmit = async (e) => {

    e.preventDefault();

    setError('');

    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }

    setLoading(true);

    try {

      await api.post('/auth/reset-password', { token, password });

      setSuccess(true);

      setTimeout(() => navigate('/login'), 3000);

    } catch (err) {

      setError(err.message || 'Failed to reset password');

    } finally {

      setLoading(false);

    }

  };



  if (!token) {

    return (

      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#F5F0E8' }}>

        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">

          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#DC2626' }} />

          <h2 className="text-xl font-bold mb-2" style={{ color: '#2D2D2D' }}>Invalid Reset Link</h2>

          <p className="mb-6" style={{ color: '#6B6B6B' }}>This password reset link is invalid or has expired.</p>

          <a href="/forgot-password" className="font-semibold hover:underline" style={{ color: '#C45C26' }}>Request a new link</a>

        </div>

      </div>

    );

  }



  return (

    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#F5F0E8' }}>

      <div className="w-full max-w-md">

        <div className="text-center mb-8">

          <img src="/logos/pi-logo.svg" alt="Pi.insure" className="w-20 h-20 mx-auto mb-4" />

          <h1 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Pi.insure</h1>

        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border" style={{ borderColor: '#E5E5E5' }}>

          {success ? (

            <div className="text-center">

              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#D1FAE5' }}>

                <CheckCircle className="w-8 h-8" style={{ color: '#059669' }} />

              </div>

              <h2 className="text-xl font-bold mb-2" style={{ color: '#2D2D2D' }}>Password Reset!</h2>

              <p className="mb-6" style={{ color: '#6B6B6B' }}>Your password has been reset. Redirecting to login...</p>

            </div>

          ) : (

            <>

              <div className="text-center mb-6">

                <h2 className="text-2xl font-bold" style={{ color: '#2D2D2D' }}>Reset Password</h2>

                <p className="mt-2" style={{ color: '#6B6B6B' }}>Enter your new password</p>

              </div>

              {error && <div className="mb-4 p-4 rounded-xl text-sm" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>{error}</div>}

              <form onSubmit={handleSubmit}>

                <div className="mb-4">

                  <label className="block text-sm font-semibold mb-2" style={{ color: '#2D2D2D' }}>New Password</label>

                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}

                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"

                    style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAFA' }}

                    placeholder="Enter new password" required minLength={8} />

                </div>

                <div className="mb-6">

                  <label className="block text-sm font-semibold mb-2" style={{ color: '#2D2D2D' }}>Confirm Password</label>

                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}

                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"

                    style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAFA' }}

                    placeholder="Confirm new password" required />

                </div>

                <button type="submit" disabled={loading}

                  className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"

                  style={{ backgroundColor: '#C45C26' }}>

                  {loading ? 'Resetting...' : 'Reset Password'}

                </button>

              </form>

            </>

          )}

        </div>

      </div>

    </div>

  );

}



function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [notifications, setNotifications] = useState([]);

  const [showNotifDropdown, setShowNotifDropdown] = useState(false);

  const [notifCount, setNotifCount] = useState(0);

  const navigate = useNavigate();



  useEffect(() => {

    if (user?.role === 'admin') {

      const loadNotifs = async () => {

        try {

          const data = await api.get('/admin/notifications');

          const allNotifs = [...(data.notifications || []), ...(data.alerts || []).map(a => ({ ...a, notification_type: 'renewal_alert', subject: a.policy_number + ' expires in ' + a.days_until_renewal + ' days' }))];

          setNotifications(allNotifs.slice(0, 10));

          setNotifCount(data.unread_count || 0);

        } catch (err) { console.error(err); }

      };

      loadNotifs();

      const interval = setInterval(loadNotifs, 60000);

      return () => clearInterval(interval);

    }

  }, [user]);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const navigation = {
    admin: [
      { name: 'Dashboard', icon: Home, path: '/dashboard' },
      { name: 'Clients', icon: Building2, path: '/clients' },
      { name: 'Vendors', icon: Users, path: '/vendors' },
      { name: 'All Policies', icon: FileText, path: '/policies' },
      { name: 'Documents', icon: FolderOpen, path: '/documents' },
      { name: 'Users', icon: User, path: '/users' },
      { name: 'Activity Log', icon: BarChart3, path: '/activity' },
      { name: 'Settings', icon: Settings, path: '/settings' }
    ],
    client: [
      { name: 'Dashboard', icon: Home, path: '/dashboard' },
      { name: 'My Vendors', icon: Users, path: '/my-vendors' },
      { name: 'Adam', icon: MessageSquare, path: '/assistant' },
      { name: 'Settings', icon: Settings, path: '/settings' }
    ],
    clientOLD: [
      { name: 'Dashboard', icon: Home, path: '/dashboard' },
      { name: 'My Policies', icon: FileText, path: '/policies' },
      { name: 'Documents', icon: FolderOpen, path: '/documents' },
      { name: 'My Vendors', icon: Users, path: '/my-vendors' },
      { name: 'Adam', icon: MessageSquare, path: '/assistant' },
      { name: 'Settings', icon: Settings, path: '/settings' }
    ],
    vendor: [
      { name: 'Dashboard', icon: Home, path: '/dashboard' },
      { name: 'My Clients', icon: Building2, path: '/my-clients' },
      { name: 'Assigned Policies', icon: FileText, path: '/policies' },
      { name: 'Documents', icon: FolderOpen, path: '/documents' },
      { name: 'Settings', icon: Settings, path: '/settings' }
    ]
  };
  
  const navItems = navigation[user?.role] || [];
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1a1a1a] transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center gap-3 px-6 py-5 border-b" style={{ borderColor: '#333333' }}>

          <img src="/logos/pi-logo.svg" alt="Pi" className="h-14 w-auto" />

          <span className="text-2xl font-bold text-white">Pi.insure</span>

        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-4 py-3 text-[#aaaaaa] hover:bg-[#2a2a2a] hover:text-white rounded-lg transition-colors"
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#333333]">
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="w-10 h-10 bg-[#C45C26] rounded-full flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[#888888] text-xs capitalize">{user?.role}</p>
            </div>
            <button onClick={handleLogout} className="text-[#888888] hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className={`lg:ml-64 min-h-screen transition-all duration-200`}>
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
            <Menu className="w-6 h-6 text-slate-600" />
          </button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-4">
            <button className="relative text-[#888888] hover:text-slate-600">
              <Bell className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">3</span>
            </button>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4">
          {children}
        </main>
      </div>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
}

// ============================================
// Admin Dashboard
// ============================================
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadStats();
  }, []);
  
  const loadStats = async () => {
    try {
      const data = await api.get('/admin/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };
  


  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;
  
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Admin Dashboard</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Clients" value={stats?.stats?.clients || 0} icon={Building2} color="blue" />
        <StatCard title="Total Vendors" value={stats?.stats?.vendors || 0} icon={Users} color="green" />
        <StatCard title="Total Policies" value={stats?.stats?.policies || 0} icon={FileText} color="purple" />
        <StatCard title="Documents" value={stats?.stats?.documents || 0} icon={FolderOpen} color="amber" />
      </div>


      {/* Financial Stats */}

      <div className="grid grid-cols-1 gap-6">

        <div className="bg-gradient-to-r from-[#C45C26] to-[#A34A1E] rounded-xl shadow-lg p-6 text-white">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-orange-100 text-sm font-medium">Total Annual Premium</p>

              <p className="text-3xl font-bold mt-2">${parseFloat(stats?.stats?.totalPremium || 0).toLocaleString()}</p>

            </div>

            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">

              <DollarSign className="w-7 h-7" />

            </div>

          </div>

        </div>

      </div>

      
      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue Premiums */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Overdue Premiums
          </h3>
          <div className="space-y-3">
            {stats?.overduePremiums?.length > 0 ? (
              stats.overduePremiums.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800">{p.policy_number}</p>
                    <p className="text-sm text-slate-500">{p.client_name} - {p.category_name}</p>
                  </div>
                  <span className="text-red-600 font-medium">${p.annual_premium?.toLocaleString()}</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">No overdue premiums</p>
            )}
          </div>
        </div>
        
        {/* Upcoming Renewals */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            Upcoming Renewals
          </h3>
          <div className="space-y-3">
            {stats?.renewals?.length > 0 ? (
              stats.renewals.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800">{p.policy_number}</p>
                    <p className="text-sm text-slate-500">{p.client_name}</p>
                  </div>
                  <span className="text-amber-600 text-sm">{new Date(p.renewal_date).toLocaleDateString()}</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">No upcoming renewals</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {stats?.recentActivity?.slice(0, 5).map((activity, i) => (
            <div key={i} className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-slate-800">
                  <span className="font-medium">{activity.user_name || 'System'}</span>
                  {' '}{activity.action?.replace('_', ' ')}
                </p>
                <p className="text-sm text-slate-500">
                  {new Date(activity.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Client Dashboard
// ============================================
function ClientDashboard() {

  const { user } = useAuth();

  const [alerts, setAlerts] = useState([]);

  const [policies, setPolicies] = useState([]);

  const [vendors, setVendors] = useState([]);

  const [documents, setDocuments] = useState([]);

  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('policies');

  const [policyDetail, setPolicyDetail] = useState({ open: false, policy: null });

  const [filters, setFilters] = useState({ category: '', policyYear: '', vendor: '' });

  const [lastUpdated, setLastUpdated] = useState(null);

  const [aiSummary, setAiSummary] = useState({ open: false, loading: false, content: '', docName: '', policyId: null, chatMessages: [], chatInput: '', chatLoading: false });

  const [policyModal, setPolicyModal] = useState({ open: false, mode: 'add', policy: null });

  const [clientUsers, setClientUsers] = useState([]);

  const [showAddUserForm, setShowAddUserForm] = useState(false);

  const [userFormData, setUserFormData] = useState({ name: '', email: '', password: '', phone: '' });

  const [savingUser, setSavingUser] = useState(false);
  const [pendingDocs, setPendingDocs] = useState([]);

  const [policyForm, setPolicyForm] = useState({

    policy_number: '', policy_year: '', subject_matter: '', category_id: '', 

    insurer: '', sum_insured: '', annual_premium: '', annual_premium_usd: '', prorata_premium: '', prorata_premium_usd: '', deductible_1: '', deductible_2: '', start_date: '', expiry_date: '', 

    status: 'active', notes: '', broker_name: '',

    num_installments: '1', installment_premium: '', 

    installment_due_1: '', installment_due_2: '', installment_due_3: '', installment_due_4: '',

    installment_status_1: 'outstanding', installment_status_2: 'outstanding', 

    installment_status_3: 'outstanding', installment_status_4: 'outstanding',

    survey_deadline: '', subjectivity_deadline: '',

    survey_status: 'outstanding', subjectivity_status: 'outstanding'

  });

  const [allCategories, setAllCategories] = useState([]);



  const policyYears = ['2024-2025', '2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'];

  const currencyOptions = ['USD', 'AED', 'EUR', 'GBP', 'INR', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF', 'SGD', 'HKD', 'NZD', 'ZAR', 'MXN', 'BRL', 'KRW', 'THB', 'MYR', 'IDR', 'PHP', 'VND', 'PKR', 'BDT', 'LKR', 'EGP', 'TRY', 'RUB', 'PLN', 'SEK', 'NOK', 'DKK', 'CZK', 'HUF', 'ILS'];



  useEffect(() => { loadData(); }, []);



  const loadData = async () => {

    try {

      const clientId = user.client?.id || user.clientId;

      const [alertsData, policiesData, vendorsData, docsData, catsData] = await Promise.all([

        api.get(`/policies/alerts/${clientId}`).catch(() => ({ alerts: [] })),

        api.get(`/policies?client_id=${clientId}`),

        api.get(`/clients/${clientId}/vendors`).catch(() => ({ vendors: [] })),

        api.get(`/documents?client_id=${clientId}`).catch(() => ({ documents: [] })),

        api.get('/policies/categories/list').catch(() => [])

      ]);

      setAlerts(alertsData.alerts || []);

      setPolicies(Array.isArray(policiesData?.policies) ? policiesData.policies : Array.isArray(policiesData) ? policiesData : []);

      setVendors(vendorsData.vendors || []);

      setDocuments(docsData.documents || docsData || []);

      setAllCategories(catsData.categories || catsData || []);

      setLastUpdated(new Date());

    } catch (error) {

      console.error('Failed to load data:', error);

    } finally {

      setLoading(false);

    }

  };



  const getAISummary = async (doc) => {

    setAiSummary({ open: true, loading: true, content: '', docName: doc.original_name || doc.file_name });

    try {

      const result = await api.post('/extraction/extract-document', { 

        documentId: doc.id, 

        clientId: user.client?.id || user.clientId 

      });

      const data = result.data?.extracted || {};

      const summary = `Policy Number: ${data.policyNumber || 'N/A'}

Category: ${data.category || 'N/A'}

Insurer: ${data.insurer || 'N/A'}

Sum Insured: ${data.currency || 'USD'} ${data.sumInsured?.toLocaleString() || 'N/A'}

Annual Premium: ${data.currency || 'USD'} ${data.annualPremium?.toLocaleString() || 'N/A'}

Period: ${data.startDate || 'N/A'} to ${data.expiryDate || 'N/A'}

Broker: ${data.brokerName || 'N/A'}

Assured: ${data.assuredName || 'N/A'}`;

      setAiSummary({ open: true, loading: false, content: summary, docName: doc.original_name || doc.file_name });

    } catch (error) {

      setAiSummary({ open: true, loading: false, content: 'Failed to generate summary: ' + error.message, docName: doc.original_name });

    }

  };



  const getPolicySummary = async (policy) => {

    setAiSummary({ open: true, loading: true, content: '', docName: 'Policy: ' + policy.policy_number });

    try {

      const policyDocs = documents.filter(d => d.policy_id === policy.id);

      

      if (policyDocs.length === 0) {

        setAiSummary({ 

          open: true, 

          loading: false, 

          content: 'No documents found for this policy.\n\nPolicy Details:\n• Policy Number: ' + policy.policy_number + '\n• Category: ' + (policy.category_name || policy.category || 'N/A') + '\n• Insurer: ' + (policy.insurer || 'N/A') + '\n• Premium: $' + (parseFloat(policy.annual_premium) || 0).toLocaleString() + '\n• Status: ' + (policy.status || 'active'),

          docName: 'Policy: ' + policy.policy_number 

        });

        return;

      }

      

      const result = await api.post('/extraction/policy-summary', { 

        policyId: policy.id,

        clientId: user.client?.id || user.clientId,

        documentIds: policyDocs.map(d => d.id)

      });

      

      setAiSummary({ 

        open: true, 

        loading: false, 

        content: result.summary || 'Summary generated for ' + policyDocs.length + ' documents.',

        docName: 'Policy: ' + policy.policy_number + ' (' + policyDocs.length + ' documents)'

      });

    } catch (error) {

      setAiSummary({ 

        open: true, 

        loading: false, 

        content: 'Policy Summary:\n\n• Policy Number: ' + policy.policy_number + '\n• Category: ' + (policy.category_name || policy.category || 'N/A') + '\n• Interest/Subject: ' + (policy.subject_matter || 'N/A') + '\n• Insurer: ' + (policy.insurer || 'N/A') + '\n• Broker: ' + (policy.broker_name || 'N/A') + '\n• Annual Premium: $' + (parseFloat(policy.annual_premium) || 0).toLocaleString() + '\n• Policy Year: ' + (policy.policy_year || 'N/A') + '\n• Start Date: ' + (policy.start_date ? new Date(policy.start_date).toLocaleDateString() : 'N/A') + '\n• Expiry Date: ' + (policy.expiry_date ? new Date(policy.expiry_date).toLocaleDateString() : 'N/A') + '\n• Status: ' + (policy.status || 'active') + '\n\nError: ' + error.message,

        docName: 'Policy: ' + policy.policy_number 

      });

    }

  };







  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;



  const totalPremium = (Array.isArray(policies) ? policies : []).reduce((sum, p) => sum + (parseFloat(p.annual_premium_usd || p.annual_premium) || 0), 0);

  const totalSumInsured = (Array.isArray(policies) ? policies : []).reduce((sum, p) => sum + (parseFloat(p.sum_insured) || 0), 0);

  const now = new Date();

  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const in60 = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);

  const in90 = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);



  const renewals30 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp >= now && exp <= in30; });

  const renewals60 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp > in30 && exp <= in60; });

  const renewals90 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp > in60 && exp <= in90; });

  const premiumsDue = policies.filter(p => {

    if (!p.installment_1_due) return false;

    const dueDate = new Date(p.installment_1_due);

    return dueDate >= now && dueDate <= in30;

  });



  const categories = [...new Set(policies.map(p => p.category_name || p.category).filter(Boolean))];

  const vendorNames = [...new Set(vendors.map(v => v.name || v.company_name).filter(Boolean))];



  const filteredPolicies = policies.filter(p => {

    if (filters.category && (p.category_name || p.category) !== filters.category) return false;

    if (filters.policyYear && p.policy_year !== filters.policyYear) return false;

    if (filters.vendor && p.broker_name !== filters.vendor) return false;

    if (filters.subjectMatter && p.subject_matter !== filters.subjectMatter) return false;

    return true;

  });



  const filteredDocs = documents.filter(d => {

    if (filters.category && d.category_name !== filters.category) return false;

    return true;

  });



  const docsByCategory = filteredDocs.reduce((acc, doc) => {

    const cat = doc.category_name || 'Uncategorized';

    // Find the policy to get the policy year

    const policy = policies.find(p => p.id === doc.policy_id);

    const policyYear = policy?.policy_year || 'Unknown Year';

    if (!acc[cat]) acc[cat] = {};

    if (!acc[cat][policyYear]) acc[cat][policyYear] = [];

    acc[cat][policyYear].push(doc);

    return acc;

  }, {});



  // Color palette: Grey, Black, Rusty Orange (#C45C26), Beige (#F5F0E8), Red for alerts

  const colors = {

    rust: '#C45C26',

    rustLight: '#D4794A',

    rustDark: '#A34A1E',

    beige: '#F5F0E8',

    beigeLight: '#FAF8F4',

    charcoal: '#2D2D2D',

    darkGray: '#404040',

    mediumGray: '#6B6B6B',

    lightGray: '#9CA3AF',

    offWhite: '#FAFAF9',

    red: '#DC2626',

    redLight: '#FEE2E2'

  };



  return (

    <div className="min-h-screen" style={{ backgroundColor: colors.beigeLight }}>

      <div className="max-w-full px-8 py-6">

        

        {/* Header */}

        <div className="flex items-start justify-between mb-8">

          <div className="flex items-center gap-4">

            {user?.client?.logo_url ? (

              <img src={user.client.logo_url} alt="Company Logo" className="w-14 h-14 rounded-xl object-cover border-2" style={{ borderColor: '#E5E5E5' }} />

            ) : (

              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold text-white" style={{ backgroundColor: colors.rust }}>

                {(user?.client?.company_name || user?.company_name || 'C').charAt(0).toUpperCase()}

              </div>

            )}

            <div>

              <h1 className="text-3xl font-bold tracking-tight" style={{ color: colors.charcoal }}>Insurance Portfolio</h1>

              <p className="mt-1 font-medium" style={{ color: colors.mediumGray }}>{user?.client?.company_name || user?.company_name || 'Welcome back'}</p>

            </div>

          </div>

          <div className="text-right px-5 py-3 rounded-xl border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

            <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: colors.lightGray }}>Last Synced</p>

            <p className="text-sm font-semibold" style={{ color: colors.darkGray }}>{lastUpdated?.toLocaleString()}</p>

            <button onClick={loadData} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-80" style={{ color: colors.rust }}>

              <RefreshCw className="w-3.5 h-3.5" /> Refresh

            </button>

          </div>

        </div>



        {/* Navigation Tabs */}

        <div className="flex gap-1 mb-8 p-1.5 rounded-xl border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

          {[

            { id: 'policies', label: 'Policy Summary', icon: FileText },

            { id: 'documents', label: 'Documents', icon: FolderOpen },

            { id: 'alerts', label: 'Alerts', icon: Bell },

            { id: 'overview', label: 'Overview', icon: LayoutDashboard },

          ].map(tab => (

            <button key={tab.id} onClick={() => setActiveTab(tab.id)}

              className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition-all duration-200"

              style={activeTab === tab.id 

                ? { backgroundColor: colors.charcoal, color: '#FFFFFF' }

                : { color: colors.mediumGray }

              }>

              <tab.icon className="w-4 h-4" />

              {tab.label}

            </button>

          ))}

          

          {/* Add Policy Button */}

          <button 

            onClick={() => {

              setPolicyForm({

                policy_number: '', policy_year: '', subject_matter: '', category_id: '', 

                insurer: '', sum_insured: '', annual_premium: '', annual_premium_usd: '', prorata_premium: '', prorata_premium_usd: '', deductible_1: '', deductible_2: '', start_date: '', expiry_date: '', 

                status: 'active', notes: '', broker_name: ''

              });

              setPendingDocs([]); setPolicyModal({ open: true, mode: 'add', policy: null });

            }}

            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm text-white transition-all hover:opacity-90 ml-auto"

            style={{ backgroundColor: colors.rust }}>

            <Plus className="w-4 h-4" />

            Add Policy

          </button>

        </div>



        {/* Overview Tab */}

        {activeTab === 'overview' && (

          <div className="space-y-6">

            {/* Key Metrics */}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

              <div className="rounded-xl p-5 border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.lightGray }}>Active Policies</p>

                    <p className="text-3xl font-bold mt-1" style={{ color: colors.charcoal }}>{policies.length}</p>

                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.beige }}>

                    <FileText className="w-5 h-5" style={{ color: colors.rust }} />

                  </div>

                </div>

              </div>

              <div className="rounded-xl p-5 border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.lightGray }}>Annual Premium</p>

                    <p className="text-3xl font-bold mt-1" style={{ color: colors.charcoal }}>${(totalPremium/1000).toFixed(0)}K</p>

                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.beige }}>

                    <DollarSign className="w-5 h-5" style={{ color: colors.rust }} />

                  </div>

                </div>

              </div>

              

              <div className="rounded-xl p-5 border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.lightGray }}>Vendors</p>

                    <p className="text-3xl font-bold mt-1" style={{ color: colors.charcoal }}>{vendors.length}</p>

                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.beige }}>

                    <Users className="w-5 h-5" style={{ color: colors.rust }} />

                  </div>

                </div>

              </div>

              <div className="rounded-xl p-5 border" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.lightGray }}>Documents</p>

                    <p className="text-3xl font-bold mt-1" style={{ color: colors.charcoal }}>{documents.length}</p>

                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.beige }}>

                    <FolderOpen className="w-5 h-5" style={{ color: colors.rust }} />

                  </div>

                </div>

              </div>

            </div>



            {/* Renewal Timeline */}

            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

              <div className="px-6 py-5 border-b" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                <div className="flex items-center gap-3">

                  <div className="p-2.5 rounded-lg" style={{ backgroundColor: colors.rust }}>

                    <Calendar className="w-5 h-5 text-white" />

                  </div>

                  <div>

                    <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>Renewal Timeline</h3>

                    <p className="text-sm" style={{ color: colors.mediumGray }}>Upcoming policy renewals by timeframe</p>

                  </div>

                </div>

              </div>

              <div className="p-6 flex-1 overflow-y-auto" style={{ maxHeight: "65vh", overflowY: "scroll" }}>

                <div className="grid grid-cols-3 gap-4">

                  <div className="rounded-xl p-5 border-2" style={{ 

                    backgroundColor: renewals30.length > 0 ? colors.redLight : colors.beige, 

                    borderColor: renewals30.length > 0 ? colors.red : '#E5E5E5' 

                  }}>

                    <div className="flex items-center justify-between mb-3">

                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: renewals30.length > 0 ? colors.red : colors.lightGray }}>Urgent</span>

                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ 

                        backgroundColor: renewals30.length > 0 ? colors.red : colors.mediumGray, 

                        color: '#FFFFFF' 

                      }}>0-30 days</span>

                    </div>

                    <p className="text-5xl font-black" style={{ color: renewals30.length > 0 ? colors.red : colors.lightGray }}>{renewals30.length}</p>

                    <p className="text-sm mt-2 font-medium" style={{ color: colors.mediumGray }}>policies expiring</p>

                  </div>

                  <div className="rounded-xl p-5 border-2" style={{ 

                    backgroundColor: renewals60.length > 0 ? '#FEF3E2' : colors.beige, 

                    borderColor: renewals60.length > 0 ? colors.rust : '#E5E5E5' 

                  }}>

                    <div className="flex items-center justify-between mb-3">

                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: renewals60.length > 0 ? colors.rust : colors.lightGray }}>Attention</span>

                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ 

                        backgroundColor: renewals60.length > 0 ? colors.rust : colors.mediumGray, 

                        color: '#FFFFFF' 

                      }}>31-60 days</span>

                    </div>

                    <p className="text-5xl font-black" style={{ color: renewals60.length > 0 ? colors.rust : colors.lightGray }}>{renewals60.length}</p>

                    <p className="text-sm mt-2 font-medium" style={{ color: colors.mediumGray }}>policies expiring</p>

                  </div>

                  <div className="rounded-xl p-5 border-2" style={{ 

                    backgroundColor: colors.beige, 

                    borderColor: '#E5E5E5' 

                  }}>

                    <div className="flex items-center justify-between mb-3">

                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.lightGray }}>Planned</span>

                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ 

                        backgroundColor: colors.mediumGray, 

                        color: '#FFFFFF' 

                      }}>61-90 days</span>

                    </div>

                    <p className="text-5xl font-black" style={{ color: renewals90.length > 0 ? colors.charcoal : colors.lightGray }}>{renewals90.length}</p>

                    <p className="text-sm mt-2 font-medium" style={{ color: colors.mediumGray }}>policies expiring</p>

                  </div>

                </div>

              </div>

            </div>



            {/* Two Column Layout */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Premium Due */}

              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="px-6 py-5 border-b" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                  <div className="flex items-center gap-3">

                    <div className="p-2.5 rounded-lg" style={{ backgroundColor: colors.rust }}>

                      <DollarSign className="w-5 h-5 text-white" />

                    </div>

                    <div>

                      <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>Premium Due</h3>

                      <p className="text-sm" style={{ color: colors.mediumGray }}>Payments due within 30 days</p>

                    </div>

                  </div>

                </div>

                <div className="p-6 overflow-y-auto" style={{ maxHeight: "55vh" }}>

                  {premiumsDue.length > 0 ? (

                    <div className="space-y-3">

                      {premiumsDue.slice(0,4).map((p, i) => (

                        <div key={i} className="flex items-center justify-between p-4 rounded-xl border" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                          <div>

                            <p className="font-semibold" style={{ color: colors.charcoal }}>{p.policy_number}</p>

                            <p className="text-sm" style={{ color: colors.mediumGray }}>{p.category_name || p.category}</p>

                          </div>

                          <div className="text-right">

                            <p className="font-bold" style={{ color: colors.rust }}>${parseFloat(p.installment_1_amount || p.annual_premium).toLocaleString()}</p>

                            <p className="text-xs" style={{ color: colors.mediumGray }}>Due {new Date(p.installment_1_due).toLocaleDateString()}</p>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <div className="text-center py-10">

                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.beige }}>

                        <CheckCircle className="w-7 h-7" style={{ color: colors.rust }} />

                      </div>

                      <p className="font-medium" style={{ color: colors.mediumGray }}>No premiums due in the next 30 days</p>

                    </div>

                  )}

                </div>

              </div>



              {/* Action Required */}

              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <div className="px-6 py-5 border-b" style={{ backgroundColor: alerts.length > 0 ? colors.redLight : colors.beige, borderColor: '#E5E5E5' }}>

                  <div className="flex items-center gap-3">

                    <div className="p-2.5 rounded-lg" style={{ backgroundColor: alerts.length > 0 ? colors.red : colors.rust }}>

                      <AlertTriangle className="w-5 h-5 text-white" />

                    </div>

                    <div>

                      <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>Action Required</h3>

                      <p className="text-sm" style={{ color: colors.mediumGray }}>Items needing your attention</p>

                    </div>

                  </div>

                </div>

                <div className="p-6">

                  {alerts.length > 0 ? (

                    <div className="space-y-3">

                      {alerts.slice(0,4).map((alert, i) => (

                        <div key={i} className="flex items-start gap-3 p-4 rounded-xl border" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                          <div className="p-2 rounded-lg" style={{ backgroundColor: colors.redLight }}>

                            <AlertTriangle className="w-4 h-4" style={{ color: colors.red }} />

                          </div>

                          <div className="flex-1 min-w-0">

                            <p className="font-semibold truncate" style={{ color: colors.charcoal }}>{alert.message || alert.title}</p>

                            <p className="text-sm" style={{ color: colors.mediumGray }}>{alert.policy_number} - {alert.days_until_renewal || alert.days} days</p>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <div className="text-center py-10">

                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.beige }}>

                        <CheckCircle className="w-7 h-7" style={{ color: colors.rust }} />

                      </div>

                      <p className="font-medium" style={{ color: colors.mediumGray }}>All caught up! No pending alerts</p>

                    </div>

                  )}

                </div>

              </div>

            </div>



            {/* Summary Bar */}

            <div className="rounded-xl p-6" style={{ backgroundColor: colors.charcoal }}>

              <div className="grid grid-cols-4 gap-8">

                <div className="text-center">

                  <p className="text-sm font-medium" style={{ color: '#9CA3AF' }}>Policy Categories</p>

                  <p className="text-3xl font-black text-white mt-1">{categories.length}</p>

                </div>

                <div className="text-center border-l" style={{ borderColor: '#4B5563' }}>

                  <p className="text-sm font-medium" style={{ color: '#9CA3AF' }}>Active Coverage</p>

                  <p className="text-3xl font-black text-white mt-1">{policies.filter(p => p.status === 'active').length}</p>

                </div>

                <div className="text-center border-l" style={{ borderColor: '#4B5563' }}>

                  <p className="text-sm font-medium" style={{ color: '#9CA3AF' }}>This Year Renewals</p>

                  <p className="text-3xl font-black text-white mt-1">{renewals30.length + renewals60.length + renewals90.length}</p>

                </div>

                <div className="text-center border-l" style={{ borderColor: '#4B5563' }}>

                  <p className="text-sm font-medium" style={{ color: '#9CA3AF' }}>Total Documents</p>

                  <p className="text-3xl font-black text-white mt-1">{documents.length}</p>

                </div>

              </div>

            </div>

          </div>

        )}



        {/* Policy Summary Tab */}

        {activeTab === 'policies' && (

          <div className="space-y-6">

            {/* Filters */}

            <div className="rounded-xl border p-5" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

              <div className="flex flex-wrap items-center gap-4">

                <div className="flex items-center gap-2" style={{ color: colors.mediumGray }}>

                  <Filter className="w-4 h-4" />

                  <span className="text-sm font-semibold">Filters:</span>

                </div>

                <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Categories</option>

                  {categories.map(c => <option key={c} value={c}>{c}</option>)}

                </select>

                <select value={filters.policyYear} onChange={e => setFilters({...filters, policyYear: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Policy Years</option>

                  {policyYears.map(y => <option key={y} value={y}>{y}</option>)}

                </select>

                <select value={filters.vendor} onChange={e => setFilters({...filters, vendor: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Brokers / Vendors</option>

                  {vendorNames.map(v => <option key={v} value={v}>{v}</option>)}

                </select>

                <select value={filters.subjectMatter || ''} onChange={e => setFilters({...filters, subjectMatter: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Interest / Subject Matter</option>

                  {[...new Set(policies.map(p => p.subject_matter).filter(Boolean))].map(s => <option key={s} value={s}>{s}</option>)}

                </select>

                {(filters.category || filters.policyYear || filters.vendor) && (

                  <button onClick={() => setFilters({ category: '', policyYear: '', vendor: '' })}

                    className="px-4 py-2.5 text-sm font-medium rounded-lg transition-colors hover:opacity-80" style={{ color: colors.rust }}>

                    Clear All

                  </button>

                )}

                <div className="ml-auto text-sm font-medium" style={{ color: colors.mediumGray }}>

                  Showing <span className="font-bold" style={{ color: colors.rust }}>{filteredPolicies.length}</span> of {policies.length} policies

                </div>

              </div>

            </div>



            {/* Policy Table */}

            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

              <div className="overflow-x-auto">

                <table className="w-full">

                  <thead>

                    <tr style={{ backgroundColor: colors.beige }}>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Policy Number</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Category</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Interest / Subject Matter</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Policy Year</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Insurer</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Broker / Vendor</th>

                      <th className="text-right px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Annual Premium (Policy Currency)</th>

                      <th className="text-right px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Annual Premium (USD)</th>

                      <th className="text-right px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Pro-rata Premium (Policy Currency)</th>

                      <th className="text-right px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Pro-rata Premium (USD)</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Policy Start</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Policy Expiry</th>

                      <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Status</th>

                      <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>Docs</th>

                      <th className="text-center px-4 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: colors.mediumGray }}>AI Summary</th>

                    </tr>

                  </thead>

                  <tbody>

                    {filteredPolicies.map((p, idx) => (

                      <tr key={p.id} className="border-t" style={{ borderColor: '#E5E5E5', backgroundColor: idx % 2 === 0 ? '#FFFFFF' : colors.beigeLight }}>

                        <td className="px-4 py-4">

                          <button 

                            onClick={() => setPolicyDetail({ open: true, policy: p })}

                            className="font-semibold hover:underline cursor-pointer text-left"

                            style={{ color: colors.rust }}

                          >

                            {p.policy_number}

                          </button>

                        </td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.category_name || p.category}</td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.subject_matter || '-'}</td>

                        <td className="px-4 py-4 text-sm font-medium" style={{ color: colors.darkGray }}>{p.policy_year || '-'}</td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.insurer || '-'}</td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.broker_name || '-'}</td>

                        <td className="px-4 py-4 text-sm text-right font-bold" style={{ color: colors.charcoal }}>{p.currency || 'USD'} {Math.round(parseFloat(p.annual_premium) || 0).toLocaleString()}</td>

                        <td className="px-4 py-4 text-sm text-right font-bold" style={{ color: colors.charcoal }}>$ {Math.round(parseFloat(p.annual_premium_usd) || 0).toLocaleString()}</td>

                        <td className="px-4 py-4 text-sm text-right font-bold" style={{ color: colors.charcoal }}>{p.currency || 'USD'} {Math.round(parseFloat(p.prorata_premium) || 0).toLocaleString()}</td>

                        <td className="px-4 py-4 text-sm text-right font-bold" style={{ color: colors.charcoal }}>$ {Math.round(parseFloat(p.prorata_premium_usd) || 0).toLocaleString()}</td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.start_date ? new Date(p.start_date).toLocaleDateString() : '-'}</td>

                        <td className="px-4 py-4 text-sm" style={{ color: colors.darkGray }}>{p.expiry_date ? new Date(p.expiry_date).toLocaleDateString() : '-'}</td>

                        <td className="px-4 py-4">

                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"

                            style={{ backgroundColor: p.status === 'active' ? '#D1FAE5' : colors.beige, color: p.status === 'active' ? '#065F46' : colors.mediumGray }}>

                            {p.status || 'active'}

                          </span>

                        </td>

                        <td className="px-4 py-4 text-center">

                          {(() => {

                            const docCount = documents.filter(d => d.policy_id === p.id).length;

                            return (

                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold"

                                style={{ 

                                  backgroundColor: docCount > 0 ? '#D1FAE5' : colors.beige, 

                                  color: docCount > 0 ? '#065F46' : colors.mediumGray 

                                }}>

                                {docCount}

                              </span>

                            );

                          })()}

                        </td>

                        <td className="px-4 py-4 text-center">

                          {(() => {

                            const docCount = documents.filter(d => d.policy_id === p.id).length;

                            return (

                              <button 

                                onClick={() => getPolicySummary(p)} 

                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"

                                style={{ backgroundColor: docCount > 0 ? colors.charcoal : colors.lightGray }}

                                title={docCount > 0 ? "AI Summary of all policy documents" : "No documents available"}

                                disabled={docCount === 0}

                              >

                                <MessageSquare className="w-3.5 h-3.5" />

                                Summary

                              </button>

                            );

                          })()}

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

              {filteredPolicies.length === 0 && (

                <div className="text-center py-16">

                  <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: colors.lightGray }} />

                  <p className="font-medium" style={{ color: colors.mediumGray }}>No policies match your filters</p>

                </div>

              )}

            </div>

          </div>

        )}



        {/* Documents Tab */}

        {activeTab === 'documents' && (

          <div className="space-y-6">

            <div className="rounded-xl border p-5" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

              <div className="flex flex-wrap items-center gap-4">

                <div className="flex items-center gap-2" style={{ color: colors.mediumGray }}>

                  <Filter className="w-4 h-4" />

                  <span className="text-sm font-semibold">Filters:</span>

                </div>

                <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Categories</option>

                  {categories.map(c => <option key={c} value={c}>{c}</option>)}

                </select>

                <select value={filters.policyYear || ''} onChange={e => setFilters({...filters, policyYear: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Policy Years</option>

                  {[...new Set(policies.map(p => p.policy_year).filter(Boolean))].sort().reverse().map(year => (

                    <option key={year} value={year}>{year}</option>

                  ))}

                </select>

                <select value={filters.broker || ''} onChange={e => setFilters({...filters, broker: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Brokers / Vendors</option>

                  {[...new Set(policies.map(p => p.broker_name).filter(Boolean))].sort().map(broker => (

                    <option key={broker} value={broker}>{broker}</option>

                  ))}

                </select>

                <select value={filters.subjectMatter || ''} onChange={e => setFilters({...filters, subjectMatter: e.target.value})}

                  className="px-4 py-2.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2" 

                  style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5', color: colors.charcoal }}>

                  <option value="">All Interest / Subject Matter</option>

                  {[...new Set(policies.map(p => p.interest_subject_matter || p.subject_matter).filter(Boolean))].sort().map(sm => (

                    <option key={sm} value={sm}>{sm}</option>

                  ))}

                </select>

                <div className="ml-auto text-sm font-medium" style={{ color: colors.mediumGray }}>

                  <span className="font-bold" style={{ color: colors.rust }}>{documents.length}</span> documents total

                </div>

              </div>

            </div>



            {Object.entries(docsByCategory).length > 0 ? (

              <div className="space-y-4">

                {Object.entries(docsByCategory).map(([category, yearGroups]) => {

                  const totalDocs = Object.values(yearGroups).reduce((sum, docs) => sum + docs.length, 0);

                  return (

                  <div key={category} className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                    <div className="px-6 py-4 border-b" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                      <div className="flex items-center gap-3">

                        <div className="p-2 rounded-lg" style={{ backgroundColor: colors.rust }}>

                          <FolderOpen className="w-4 h-4 text-white" />

                        </div>

                        <span className="font-bold" style={{ color: colors.charcoal }}>{category}</span>

                        <span className="text-sm font-medium" style={{ color: colors.mediumGray }}>({totalDocs} files)</span>

                      </div>

                    </div>

                    <div>

                      {Object.entries(yearGroups).sort((a, b) => b[0].localeCompare(a[0])).map(([policyYear, docs]) => (

                        <div key={policyYear}>

                          <div className="px-6 py-3 border-t flex items-center gap-2" style={{ backgroundColor: '#F9F6F1', borderColor: '#E5E5E5' }}>

                            <FolderOpen className="w-4 h-4" style={{ color: colors.rust }} />

                            <span className="font-semibold text-sm" style={{ color: colors.charcoal }}>{policyYear}</span>

                            <span className="text-xs" style={{ color: colors.mediumGray }}>({docs.length} files)</span>

                          </div>

                          {docs.map((doc, idx) => (

                        <div key={doc.id} className="flex items-center justify-between px-6 py-4 border-t transition-colors hover:bg-opacity-50 ml-6" 

                          style={{ borderColor: '#E5E5E5', backgroundColor: idx % 2 === 0 ? '#FFFFFF' : colors.beigeLight }}>

                          <div className="flex items-center gap-4 min-w-0 flex-1">

                            <div className="p-2.5 rounded-lg" style={{ backgroundColor: colors.beige }}>

                              <FileText className="w-5 h-5" style={{ color: colors.mediumGray }} />

                            </div>

                            <div className="min-w-0">

                              <p className="font-semibold truncate" style={{ color: colors.charcoal }}>{doc.original_name || doc.file_name}</p>

                              <p className="text-sm" style={{ color: colors.mediumGray }}>Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}</p>

                            </div>

                          </div>

                          <div className="flex items-center gap-2">

                            {doc.file_type === 'application/pdf' && (

                              <button onClick={() => getAISummary(doc)}

                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"

                                style={{ backgroundColor: colors.charcoal }}>

                                <MessageSquare className="w-4 h-4" />

                                AI Summary

                              </button>

                            )}

                            <button onClick={() => (async () => {

                              try {

                                const res = await fetch('/api/documents/' + doc.id + '/download', {

                                  headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }

                                });

                                const data = await res.json();

                                if (data.download_url) {

                                  window.open(data.download_url, '_blank');

                                } else {

                                  alert('Could not get download URL');

                                }

                              } catch (err) {

                                alert('Error: ' + err.message);

                              }

                            })()}

                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"

                              style={{ backgroundColor: colors.rust }}>

                              <Eye className="w-4 h-4" />

                              View

                            </button>

                            <button onClick={() => (async () => {

                              try {

                                const res = await fetch('/api/documents/' + doc.id + '/download', {

                                  headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }

                                });

                                const data = await res.json();

                                if (data.download_url) {

                                  window.open(data.download_url, '_blank');

                                } else {

                                  alert('Could not get download URL');

                                }

                              } catch (err) {

                                alert('Error: ' + err.message);

                              }

                            })()}

                              className="p-2.5 rounded-lg transition-colors hover:opacity-80" style={{ color: colors.mediumGray }}>

                              <Download className="w-4 h-4" />

                            </button>

                          </div>

                        </div>

                          ))}

                        </div>

                      ))}

                    </div>

                  </div>

                  );

                })}

              </div>

            ) : (

              <div className="rounded-xl border p-16 text-center" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

                <FolderOpen className="w-16 h-16 mx-auto mb-4" style={{ color: colors.lightGray }} />

                <p className="text-lg font-medium" style={{ color: colors.mediumGray }}>No documents found</p>

              </div>

            )}

          </div>

        )}



        {/* Alerts Tab */}

        {activeTab === 'alerts' && (

          <div className="space-y-6">

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

              {[

                { label: 'Renewal Alerts', type: 'renewal', icon: AlertTriangle, urgent: true },

                { label: 'Premium Due', type: 'premium', icon: DollarSign, urgent: false },

                { label: 'Survey Deadlines', type: 'survey', icon: Search, urgent: false },

                { label: 'Warranty Deadlines', type: 'warranty', icon: Clock, urgent: false }

              ].map(item => {

                const count = alerts.filter(a => a.type === item.type).length;

                return (

                  <div key={item.type} className="rounded-xl p-5 border" 

                    style={{ backgroundColor: count > 0 && item.urgent ? colors.redLight : colors.offWhite, borderColor: count > 0 && item.urgent ? colors.red : '#E5E5E5' }}>

                    <div className="flex items-center gap-3 mb-3">

                      <div className="p-2 rounded-lg" style={{ backgroundColor: count > 0 && item.urgent ? colors.red : colors.rust }}>

                        <item.icon className="w-5 h-5 text-white" />

                      </div>

                      <span className="text-sm font-medium" style={{ color: colors.mediumGray }}>{item.label}</span>

                    </div>

                    <p className="text-4xl font-black" style={{ color: count > 0 && item.urgent ? colors.red : colors.charcoal }}>{count}</p>

                  </div>

                );

              })}

            </div>



            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: colors.offWhite, borderColor: '#E5E5E5' }}>

              <div className="px-6 py-5 border-b" style={{ backgroundColor: colors.beige, borderColor: '#E5E5E5' }}>

                <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>All Alerts</h3>

              </div>

              {alerts.length > 0 ? (

                <div>

                  {alerts.map((alert, i) => (

                    <div key={i} className="flex items-start gap-4 px-6 py-5 border-t transition-colors" style={{ borderColor: '#E5E5E5' }}>

                      <div className="p-2.5 rounded-lg" style={{ backgroundColor: alert.type === 'renewal' ? colors.red : colors.rust }}>

                        <AlertTriangle className="w-5 h-5 text-white" />

                      </div>

                      <div className="flex-1">

                        <p className="font-semibold" style={{ color: colors.charcoal }}>{alert.message || alert.title}</p>

                        <p className="text-sm mt-1" style={{ color: colors.mediumGray }}>Policy: {alert.policy_number}</p>

                        <p className="text-sm" style={{ color: colors.lightGray }}>{alert.days_until_renewal || alert.days} days remaining</p>

                      </div>

                      <button onClick={() => {

                          const policy = policies.find(p => p.id === alert.policy_id);

                          if (policy) {

                            setPolicyDetail({ open: true, policy });

                          }

                        }} className="text-sm font-semibold hover:underline" style={{ color: colors.rust }}>

                        View Policy

                      </button>

                    </div>

                  ))}

                </div>

              ) : (

                <div className="text-center py-16">

                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.beige }}>

                    <CheckCircle className="w-8 h-8" style={{ color: colors.rust }} />

                  </div>

                  <p className="text-xl font-semibold" style={{ color: colors.charcoal }}>You're all caught up!</p>

                  <p className="mt-1" style={{ color: colors.mediumGray }}>No pending alerts at this time</p>

                </div>

              )}

            </div>

          </div>

        )}



        {/* AI Summary Modal */}

        {aiSummary.open && (

          <div className="fixed inset-0 z-50 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

            <div className="flex items-center justify-center min-h-screen p-4">

              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '80vh' }}>

                {/* Header */}

                <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E5E5' }}>

                  <div className="flex items-center gap-3">

                    <div className="p-2.5 rounded-lg" style={{ backgroundColor: '#2D2D2D' }}>

                      <MessageSquare className="w-5 h-5 text-white" />

                    </div>

                    <div>

                      <h3 className="font-bold text-lg" style={{ color: '#2D2D2D' }}>AI Document Summary</h3>

                      <p className="text-sm" style={{ color: '#6B6B6B' }}>{aiSummary.docName}</p>

                    </div>

                  </div>

                  <button onClick={() => setAiSummary({ open: false, loading: false, content: '', docName: '' })} 

                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors">

                    <X className="w-6 h-6" style={{ color: '#6B6B6B' }} />

                  </button>

                </div>

                

                {/* Scrollable Content */}

                <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>

                  {aiSummary.loading ? (

                    <div className="flex flex-col items-center justify-center py-16">

                      <RefreshCw className="w-12 h-12 animate-spin mb-4" style={{ color: '#C45C26' }} />

                      <p className="font-semibold" style={{ color: '#2D2D2D' }}>Analyzing documents with AI...</p>

                      <p className="text-sm mt-1" style={{ color: '#6B6B6B' }}>This may take 10-15 seconds</p>

                    </div>

                  ) : (

                    <div className="rounded-xl p-5 text-sm leading-relaxed" style={{ backgroundColor: '#F5F0E8', color: '#404040' }}>

                      {aiSummary.content.split('\n').map((line, idx) => {

                        if (line.startsWith('## ')) return <h2 key={idx} className="text-lg font-bold mt-4 mb-2" style={{ color: '#2D2D2D' }}>{line.replace('## ', '')}</h2>;

                        if (line.startsWith('### ')) return <h3 key={idx} className="text-base font-bold mt-4 mb-2" style={{ color: '#C45C26' }}>{line.replace('### ', '')}</h3>;

                        if (line.startsWith('---')) return <hr key={idx} className="my-3 border-gray-300" />;

                        if (line.startsWith('CONFIDENCE:')) {

                          const level = line.includes('HIGH') ? 'HIGH' : line.includes('MEDIUM') ? 'MEDIUM' : 'LOW';

                          const bgColor = level === 'HIGH' ? '#D1FAE5' : level === 'MEDIUM' ? '#FEF3C7' : '#FEE2E2';

                          const textColor = level === 'HIGH' ? '#065F46' : level === 'MEDIUM' ? '#92400E' : '#991B1B';

                          return <div key={idx} className="mt-4 p-3 rounded-lg font-semibold" style={{ backgroundColor: bgColor, color: textColor }}>{line}</div>;

                        }

                        if (line.startsWith('REASON:')) return <p key={idx} className="text-sm italic mb-2" style={{ color: '#6B6B6B' }}>{line}</p>;

                        if (line.includes('**Critical**') || line.includes('**URGENT**') || line.includes('**High Priority**')) {

                          return <p key={idx} className="py-1 px-2 my-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>{line.replace(/\*\*/g, '')}</p>;

                        }

                        if (line.trim().startsWith('•') || line.trim().startsWith('-')) return <p key={idx} className="ml-4 py-0.5">{line}</p>;

                        if (!line.trim()) return <div key={idx} className="h-2" />;

                        return <p key={idx} className="py-0.5">{line.replace(/\*\*/g, '')}</p>;

                      })}

                    </div>

                  )}

                </div>

                


                {/* Chat Interface */}

                {!aiSummary.loading && (

                  <div className="border-t px-6 py-4" style={{ borderColor: '#E5E5E5' }}>

                    <p className="text-sm font-semibold mb-3" style={{ color: '#2D2D2D' }}>Ask a question about this policy</p>

                    {aiSummary.chatMessages && aiSummary.chatMessages.length > 0 && (

                      <div className="mb-3 max-h-40 overflow-y-auto space-y-2">

                        {aiSummary.chatMessages.map((msg, idx) => (

                          <div key={idx} className={`p-3 rounded-lg text-sm ${msg.role === 'user' ? 'ml-8' : ''}`}

                            style={{ backgroundColor: msg.role === 'user' ? '#E5E5E5' : '#F5F0E8', color: '#2D2D2D' }}>

                            <p className="font-semibold text-xs mb-2 flex items-center gap-1" style={{ color: msg.role === 'user' ? '#6B6B6B' : '#C45C26' }}>

                              {msg.role === 'user' ? 'You' : <><MessageSquare className="w-3 h-3" /> Adam</>}

                            </p>

                            <div className="space-y-1">

                              {msg.content.split('\n').map((line, i) => {

                                if (line.startsWith('## ')) return <h3 key={i} className="text-base font-bold mt-3 mb-1" style={{ color: '#2D2D2D' }}>{line.replace('## ', '')}</h3>;

                                if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-bold mt-2 mb-1" style={{ color: '#C45C26' }}>{line.replace('### ', '').replace(/\*\*/g, '')}</h4>;

                                if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold mt-2">{line.replace(/\*\*/g, '')}</p>;

                                if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) return <p key={i} className="ml-3 py-0.5">{line.replace(/^[\s]*[-*]\s*/, '• ').replace(/\*\*/g, '')}</p>;

                                if (!line.trim()) return <div key={i} className="h-1" />;

                                return <p key={i} className="py-0.5">{line.replace(/\*\*/g, '')}</p>;

                              })}

                            </div>

                          </div>

                        ))}

                      </div>

                    )}

                    <div className="flex gap-2">

                      <input type="text" value={aiSummary.chatInput || ''} onChange={e => setAiSummary({...aiSummary, chatInput: e.target.value})} onKeyPress={e => e.key === 'Enter' && !aiSummary.chatLoading && aiSummary.chatInput?.trim() && handleAIChatSubmit()} placeholder="E.g., What are the key exclusions?" className="flex-1 px-4 py-2.5 border rounded-lg focus:outline-none text-sm" style={{ borderColor: '#E5E5E5' }} disabled={aiSummary.chatLoading} />

                      <button onClick={() => handleAIChatSubmit()} disabled={aiSummary.chatLoading || !aiSummary.chatInput?.trim()} className="px-4 py-2.5 rounded-lg font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#C45C26' }}>{aiSummary.chatLoading ? 'Thinking...' : 'Ask'}</button>

                    </div>

                  </div>

                )}



                {/* Footer */}

                <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ backgroundColor: '#F5F0E8', borderColor: '#E5E5E5' }}>

                  <p className="text-xs" style={{ color: '#6B6B6B' }}>Powered by Claude AI</p>

                  <button onClick={() => setAiSummary({ open: false, loading: false, content: '', docName: '' })} 

                    className="px-6 py-2.5 rounded-lg font-semibold text-white transition-all hover:opacity-90"

                    style={{ backgroundColor: '#2D2D2D' }}>

                    Close

                  </button>

                </div>

              </div>

            </div>

          </div>

        )}



        {/* Policy Details Modal */}

        {policyDetail.open && policyDetail.policy && (

          <div className="fixed inset-0 z-50 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

            <div className="flex items-center justify-center min-h-screen p-4">

              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>

                {/* Header */}

                <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E5E5', backgroundColor: '#2D2D2D' }}>

                  <div className="flex items-center gap-4">

                    <div className="p-2.5 rounded-xl" style={{ backgroundColor: '#C45C26' }}>

                      <FileText className="w-6 h-6 text-white" />

                    </div>

                    <div>

                      <h3 className="font-bold text-xl text-white">Policy Details</h3>

                      <p className="text-sm text-gray-400">{policyDetail.policy.policy_number}</p>

                    </div>

                  </div>

                  <button onClick={() => setPolicyDetail({ open: false, policy: null })} 

                    className="p-2 rounded-lg hover:bg-white/10 transition-colors">

                    <X className="w-6 h-6 text-white" />

                  </button>

                </div>

                

                {/* Content */}

                <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>

                  {/* Status Badge */}

                  <div className="flex items-center gap-3 mb-6">

                    <span className="px-4 py-2 rounded-full text-sm font-bold"

                      style={{ 

                        backgroundColor: policyDetail.policy.status === 'active' ? '#D1FAE5' : '#FEE2E2',

                        color: policyDetail.policy.status === 'active' ? '#065F46' : '#991B1B'

                      }}>

                      {policyDetail.policy.status === 'active' ? '● Active' : '● ' + (policyDetail.policy.status || 'Inactive')}

                    </span>

                    <span className="text-sm" style={{ color: '#6B6B6B' }}>

                      {documents.filter(d => d.policy_id === policyDetail.policy.id).length} documents

                    </span>

                  </div>



                  {/* Info Grid */}

                  <div className="grid grid-cols-2 gap-4 mb-6">

                    <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Number</p>

                      <p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_number}</p>

                    </div>

                    <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Category</p>

                      <p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.category_name || '-'}</p>

                    </div>

                  </div>



                  {/* Coverage */}

                  <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Coverage Details</h4>

                  <div className="grid grid-cols-2 gap-4 mb-6">

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Sum Insured ({policyDetail.policy.currency || 'USD'})</p>

                      <p className="text-xl font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.currency || 'USD'} {parseFloat(policyDetail.policy.sum_insured || 0).toLocaleString()}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Annual Premium ({policyDetail.policy.currency || 'USD'})</p>

                      <p className="text-xl font-bold" style={{ color: '#C45C26' }}>{policyDetail.policy.currency || 'USD'} {parseFloat(policyDetail.policy.annual_premium || 0).toLocaleString()}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Annual Premium (USD)</p>

                      <p className="text-xl font-bold" style={{ color: '#C45C26' }}>$ {parseFloat(policyDetail.policy.annual_premium_usd || 0).toLocaleString()}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Insurer</p>

                      <p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.insurer || '-'}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Broker</p>

                      <p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.broker_name || '-'}</p>

                    </div>

                  </div>



                  {/* Dates */}

                  <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Important Dates</h4>

                  <div className="grid grid-cols-3 gap-4 mb-6">

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Year</p>

                      <p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_year || '-'}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Start Date</p>

                      <p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.start_date ? new Date(policyDetail.policy.start_date).toLocaleDateString() : '-'}</p>

                    </div>

                    <div className="p-4 rounded-xl border" style={{ borderColor: policyDetail.policy.expiry_date ? '#E5E5E5' : '#FEE2E2' }}>

                      <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Expiry Date</p>

                      <p className="font-semibold" style={{ color: policyDetail.policy.expiry_date ? '#2D2D2D' : '#DC2626' }}>{policyDetail.policy.expiry_date ? new Date(policyDetail.policy.expiry_date).toLocaleDateString() : 'Not Set ⚠️'}</p>

                    </div>

                  </div>



                  {/* Subject Matter */}

                  <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Additional Info</h4>

                  <div className="p-4 rounded-xl border mb-6" style={{ borderColor: '#E5E5E5' }}>

                    <p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Interest / Subject Matter</p>

                    <p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.subject_matter || '-'}</p>

                  </div>



                  {/* Documents */}

                  <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Attached Documents</h4>

                  {(() => {

                    const pDocs = documents.filter(d => d.policy_id === policyDetail.policy.id);

                    if (pDocs.length === 0) return <p className="text-sm p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8', color: '#6B6B6B' }}>No documents attached</p>;

                    return pDocs.map(doc => (

                      <div key={doc.id} className="flex items-center justify-between p-3 mb-2 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>

                        <div>

                          <p className="font-medium text-sm" style={{ color: '#2D2D2D' }}>{doc.original_name || doc.file_name}</p>

                          <p className="text-xs" style={{ color: '#6B6B6B' }}>Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}</p>

                        </div>

                        <button onClick={() => (async () => {

                              try {

                                const res = await fetch('/api/documents/' + doc.id + '/download', {

                                  headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }

                                });

                                const data = await res.json();

                                if (data.download_url) {

                                  window.open(data.download_url, '_blank');

                                } else {

                                  alert('Could not get download URL');

                                }

                              } catch (err) {

                                alert('Error: ' + err.message);

                              }

                            })()} className="px-3 py-1.5 rounded text-xs font-semibold" style={{ backgroundColor: '#C45C26', color: '#fff' }}>View</button>

                      </div>

                    ));

                  })()}

                </div>

                

                {/* Footer */}

                <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ backgroundColor: '#F5F0E8', borderColor: '#E5E5E5' }}>

                  <div className="flex items-center gap-3">

                    <button onClick={() => { setPolicyDetail({ open: false, policy: null }); getPolicySummary(policyDetail.policy); }} className="px-4 py-2 rounded-lg font-semibold text-sm text-white flex items-center gap-2" style={{ backgroundColor: '#C45C26' }}>

                      <MessageSquare className="w-4 h-4" /> AI Summary

                    </button>

                    <button onClick={() => { 

                      setPolicyForm({

                        policy_number: policyDetail.policy.policy_number || '',

                        policy_year: policyDetail.policy.policy_year || '',

                        subject_matter: policyDetail.policy.subject_matter || '',

                        category_id: policyDetail.policy.category_id || '',

                        insurer: policyDetail.policy.insurer || '',

                        sum_insured: policyDetail.policy.sum_insured || '',

                        annual_premium: policyDetail.policy.annual_premium || '',

                        annual_premium_usd: policyDetail.policy.annual_premium_usd || (policyDetail.policy.annual_premium && policyDetail.policy.exchange_rate ? (parseFloat(policyDetail.policy.annual_premium) / parseFloat(policyDetail.policy.exchange_rate || 1)).toFixed(2) : ''),

                        prorata_premium: policyDetail.policy.prorata_premium || '',

                        prorata_premium_usd: policyDetail.policy.prorata_premium_usd || (policyDetail.policy.prorata_premium && policyDetail.policy.exchange_rate ? (parseFloat(policyDetail.policy.prorata_premium) / parseFloat(policyDetail.policy.exchange_rate || 1)).toFixed(2) : ''),

                        start_date: policyDetail.policy.start_date ? policyDetail.policy.start_date.split('T')[0] : '',

                        expiry_date: policyDetail.policy.expiry_date ? policyDetail.policy.expiry_date.split('T')[0] : '',

                        status: policyDetail.policy.status || 'active',

                        notes: policyDetail.policy.notes || '',

                        broker_name: policyDetail.policy.broker_name || '',

                        num_installments: policyDetail.policy.num_installments || '1',

                        installment_premium: policyDetail.policy.installment_premium || '',

                        installment_due_1: policyDetail.policy.installment_due_1 ? policyDetail.policy.installment_due_1.split('T')[0] : '',

                        installment_due_2: policyDetail.policy.installment_due_2 ? policyDetail.policy.installment_due_2.split('T')[0] : '',

                        installment_due_3: policyDetail.policy.installment_due_3 ? policyDetail.policy.installment_due_3.split('T')[0] : '',

                        installment_due_4: policyDetail.policy.installment_due_4 ? policyDetail.policy.installment_due_4.split('T')[0] : '',

                        survey_deadline: policyDetail.policy.survey_deadline ? policyDetail.policy.survey_deadline.split('T')[0] : '',

                        subjectivity_deadline: policyDetail.policy.subjectivity_deadline ? policyDetail.policy.subjectivity_deadline.split('T')[0] : '',

                        installment_status_1: policyDetail.policy.installment_status_1 || 'outstanding',

                        installment_status_2: policyDetail.policy.installment_status_2 || 'outstanding',

                        installment_status_3: policyDetail.policy.installment_status_3 || 'outstanding',

                        installment_status_4: policyDetail.policy.installment_status_4 || 'outstanding',

                        survey_status: policyDetail.policy.survey_status || 'outstanding',

                        subjectivity_status: policyDetail.policy.subjectivity_status || 'outstanding',

                        currency: policyDetail.policy.currency || 'USD',

                        exchange_rate: policyDetail.policy.exchange_rate || '1'

                      });

                      setPolicyModal({ open: true, mode: 'edit', policy: policyDetail.policy });

                      setPolicyDetail({ open: false, policy: null });

                    }} className="px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 border" style={{ borderColor: '#2D2D2D', color: '#2D2D2D' }}>

                      <Edit className="w-4 h-4" /> Edit Policy

                    </button>

                  </div>

                  <button onClick={() => setPolicyDetail({ open: false, policy: null })} className="px-6 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#2D2D2D' }}>Close</button>

                </div>

              </div>

            </div>

          </div>

        )}



        {/* Add/Edit Policy Modal */}

        {policyModal.open && (

          <div className="fixed inset-0 z-50 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

            <div className="flex items-center justify-center min-h-screen p-4">

              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>

                {/* Header */}

                <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E5E5' }}>

                  <h3 className="font-bold text-xl" style={{ color: '#2D2D2D' }}>

                    {policyModal.mode === 'add' ? 'Add New Policy' : 'Edit Policy'}

                  </h3>

                  <button onClick={() => setPolicyModal({ open: false, mode: 'add', policy: null })} className="p-2 rounded-lg hover:bg-gray-100">

                    <X className="w-5 h-5" style={{ color: '#6B6B6B' }} />

                  </button>

                </div>

                

                {/* Form */}

                <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '65vh' }}>

                  <div className="grid grid-cols-2 gap-4">

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Policy Number *</label>

                      <input type="text" value={policyForm.policy_number} onChange={e => setPolicyForm({...policyForm, policy_number: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="e.g., POL-2026-001" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Policy Year</label>

                      <select value={policyForm.policy_year} onChange={e => setPolicyForm({...policyForm, policy_year: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="">Select Policy Year</option>

                        {policyYears.map(y => <option key={y} value={y}>{y}</option>)}

                      </select>

                    </div>

                    <div className="col-span-2">

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Interest / Subject Matter Insured</label>

                      <input type="text" value={policyForm.subject_matter} onChange={e => setPolicyForm({...policyForm, subject_matter: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="Description of insured interest" />

                    </div>

                    <div className="col-span-2">

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Category *</label>

                      <select value={policyForm.category_id} onChange={e => setPolicyForm({...policyForm, category_id: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="">Select Category</option>

                        {allCategories.map(c => (

                          <option key={c.id} value={c.id} style={{ fontWeight: !c.parent_id ? 'bold' : 'normal' }}>

                            {c.parent_id ? '    ' + c.name : '▸ ' + c.name}

                          </option>

                        ))}

                      </select>

                    </div>

                    <div className="col-span-2">

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Insurer</label>

                      <input type="text" value={policyForm.insurer} onChange={e => setPolicyForm({...policyForm, insurer: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="Insurance company name" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Policy Currency</label>

                      <select value={policyForm.currency} onChange={e => setPolicyForm({...policyForm, currency: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Exchange Rate (1 USD = X {policyForm.currency})</label>

                      <input type="number" step="0.000001" value={policyForm.exchange_rate} onChange={e => {

                        const rate = parseFloat(e.target.value) || 1;

                        const updatedForm = {...policyForm, exchange_rate: e.target.value};

                        if (policyForm.annual_premium) updatedForm.annual_premium_usd = (parseFloat(policyForm.annual_premium) / rate).toFixed(2);

                        if (policyForm.prorata_premium) updatedForm.prorata_premium_usd = (parseFloat(policyForm.prorata_premium) / rate).toFixed(2);

                        setPolicyForm(updatedForm);

                      }}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="1" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Sum Insured / Limits 1 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.sum_insured} onChange={e => setPolicyForm({...policyForm, sum_insured: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Sum Insured / Limits 2 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.sum_insured_2 || ''} onChange={e => setPolicyForm({...policyForm, sum_insured_2: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Sum Insured / Limits 3 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.sum_insured_3 || ''} onChange={e => setPolicyForm({...policyForm, sum_insured_3: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Turnover ({policyForm.currency})</label>

                      <input type="number" value={policyForm.turnover || ''} onChange={e => setPolicyForm({...policyForm, turnover: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Annual Premium ({policyForm.currency})</label>

                      <input type="number" value={policyForm.annual_premium} onChange={e => { const val = e.target.value; const usd = val && policyForm.exchange_rate ? (parseFloat(val) / parseFloat(policyForm.exchange_rate)).toFixed(2) : ''; setPolicyForm({...policyForm, annual_premium: val, annual_premium_usd: usd}); }}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Annual Premium in USD Equivalent</label>

                      <input type="number" value={policyForm.annual_premium_usd || ''} readOnly

                        className="w-full px-4 py-2.5 border rounded-lg bg-gray-100" style={{ borderColor: '#E5E5E5' }} placeholder="Auto-calculated" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Pro-rata / Invoice Premium ({policyForm.currency || 'USD'})</label>

                      <input type="number" value={policyForm.prorata_premium || ''} onChange={e => { const val = e.target.value; const usd = val && policyForm.exchange_rate ? (parseFloat(val) / parseFloat(policyForm.exchange_rate)).toFixed(2) : ''; setPolicyForm({...policyForm, prorata_premium: val, prorata_premium_usd: usd}); }}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Pro-rata / Invoice Premium in USD Equivalent</label>

                      <input type="number" value={policyForm.prorata_premium_usd || ''} readOnly

                        className="w-full px-4 py-2.5 border rounded-lg bg-gray-100" style={{ borderColor: '#E5E5E5' }} placeholder="Auto-calculated" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Premium Rate 1 (%)</label>

                      <input type="number" step="0.0001" value={policyForm.premium_rate_1 || ''} onChange={e => setPolicyForm({...policyForm, premium_rate_1: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Premium Rate 2 (%)</label>

                      <input type="number" step="0.0001" value={policyForm.premium_rate_2 || ''} onChange={e => setPolicyForm({...policyForm, premium_rate_2: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Premium Rate 3 (%)</label>

                      <input type="number" step="0.0001" value={policyForm.premium_rate_3 || ''} onChange={e => setPolicyForm({...policyForm, premium_rate_3: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Deposit Premium ({policyForm.currency})</label>

                      <input type="number" value={policyForm.deposit_premium || ''} onChange={e => setPolicyForm({...policyForm, deposit_premium: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Deductible 1 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.deductible_1 || ''} onChange={e => setPolicyForm({...policyForm, deductible_1: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Deductible 2 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.deductible_2 || ''} onChange={e => setPolicyForm({...policyForm, deductible_2: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Deductible 3 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.deductible_3 || ''} onChange={e => setPolicyForm({...policyForm, deductible_3: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Deductible 4 ({policyForm.currency})</label>

                      <input type="number" value={policyForm.deductible_4 || ''} onChange={e => setPolicyForm({...policyForm, deductible_4: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Start Date</label>

                      <input type="date" value={policyForm.start_date} onChange={e => setPolicyForm({...policyForm, start_date: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Expiry Date</label>

                      <input type="date" value={policyForm.expiry_date} onChange={e => setPolicyForm({...policyForm, expiry_date: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Charter Party Start Date</label>

                      <input type="date" value={policyForm.charter_party_start_date || ''} onChange={e => setPolicyForm({...policyForm, charter_party_start_date: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Charter Party Expiry Date</label>

                      <input type="date" value={policyForm.charter_party_expiry_date || ''} onChange={e => setPolicyForm({...policyForm, charter_party_expiry_date: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Status</label>

                      <select value={policyForm.status} onChange={e => setPolicyForm({...policyForm, status: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="active">Active</option>

                        <option value="expired">Expired</option>

                        <option value="cancelled">Cancelled</option>

                        <option value="pending">Pending</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Broker / Vendor</label>

                      <input type="text" value={policyForm.broker_name} onChange={e => setPolicyForm({...policyForm, broker_name: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="Broker name" />

                    </div>

                    {/* Premium Installments Section */}

                    <div className="col-span-2 border-t pt-4 mt-2" style={{ borderColor: '#E5E5E5' }}>

                      <h4 className="font-semibold mb-3" style={{ color: '#C45C26' }}>Premium Installments</h4>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Number of Installments</label>

                      <select value={policyForm.num_installments} onChange={e => setPolicyForm({...policyForm, num_installments: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="1">1</option>

                        <option value="2">2</option>

                        <option value="3">3</option>

                        <option value="4">4</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Installment Premium ({policyForm.currency})</label>

                      <input type="number" value={policyForm.installment_premium} onChange={e => setPolicyForm({...policyForm, installment_premium: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} placeholder="0" />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>1st Installment Due</label>

                      <input type="date" value={policyForm.installment_due_1} onChange={e => setPolicyForm({...policyForm, installment_due_1: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>1st Installment Status</label>

                      <select value={policyForm.installment_status_1} onChange={e => setPolicyForm({...policyForm, installment_status_1: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="paid">Paid</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>2nd Installment Due</label>

                      <input type="date" value={policyForm.installment_due_2} onChange={e => setPolicyForm({...policyForm, installment_due_2: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>2nd Installment Status</label>

                      <select value={policyForm.installment_status_2} onChange={e => setPolicyForm({...policyForm, installment_status_2: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="paid">Paid</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>3rd Installment Due</label>

                      <input type="date" value={policyForm.installment_due_3} onChange={e => setPolicyForm({...policyForm, installment_due_3: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>3rd Installment Status</label>

                      <select value={policyForm.installment_status_3} onChange={e => setPolicyForm({...policyForm, installment_status_3: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="paid">Paid</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>4th Installment Due</label>

                      <input type="date" value={policyForm.installment_due_4} onChange={e => setPolicyForm({...policyForm, installment_due_4: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>4th Installment Status</label>

                      <select value={policyForm.installment_status_4} onChange={e => setPolicyForm({...policyForm, installment_status_4: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="paid">Paid</option>

                      </select>

                    </div>

                    

                    {/* Deadlines Section */}

                    <div className="col-span-2 border-t pt-4 mt-2" style={{ borderColor: '#E5E5E5' }}>

                      <h4 className="font-semibold mb-3" style={{ color: '#C45C26' }}>Deadlines & Compliance</h4>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Survey Deadline</label>

                      <input type="date" value={policyForm.survey_deadline} onChange={e => setPolicyForm({...policyForm, survey_deadline: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Survey Status</label>

                      <select value={policyForm.survey_status} onChange={e => setPolicyForm({...policyForm, survey_status: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="completed">Completed</option>

                      </select>

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Subjectivity Deadline</label>

                      <input type="date" value={policyForm.subjectivity_deadline} onChange={e => setPolicyForm({...policyForm, subjectivity_deadline: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }} />

                    </div>

                    <div>

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Subjectivity Status</label>

                      <select value={policyForm.subjectivity_status} onChange={e => setPolicyForm({...policyForm, subjectivity_status: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2" style={{ borderColor: '#E5E5E5' }}>

                        <option value="outstanding">Outstanding</option>

                        <option value="completed">Completed</option>

                      </select>

                    </div>

                    

                    <div className="col-span-2">

                      <label className="block text-sm font-medium mb-1" style={{ color: '#2D2D2D' }}>Notes</label>

                      <textarea value={policyForm.notes} onChange={e => setPolicyForm({...policyForm, notes: e.target.value})}

                        className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 h-24" style={{ borderColor: '#E5E5E5' }} placeholder="Additional notes..." />

                    </div>

                  </div>


                

                {/* Document Upload Section - Only in Edit Mode */}

                {(policyModal.mode === 'add' || policyModal.policy) && (

                  <div className="px-6 py-4 border-t" style={{ borderColor: '#E5E5E5', backgroundColor: '#FAFAFA' }}>

                    <h4 className="text-md font-semibold mb-3" style={{ color: '#C45C26' }}>Policy Documents</h4>

                    

                    <div className="flex items-center gap-3">

                      <input 

                        type="file" 

                        id="policyDocUpload" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"

                        multiple

                        onChange={async (e) => {

                          const files = e.target.files;

                          if (!files.length) return;

                          if (policyModal.mode === 'add') {

                            const fileList = Array.from(files);

                            setPendingDocs(prev => [...prev, ...fileList]);

                            setTimeout(() => { e.target.value = ''; }, 100);

                            return;

                          }

                          const formData = new FormData();

                          formData.append('file', files[0]);

                          formData.append('client_id', user.client?.id || user.clientId);

                          formData.append('policy_id', policyModal.policy.id);

                          formData.append('category_id', policyModal.policy.category_id);

                          try {

                            await fetch('/api/documents/upload', {

                              method: 'POST',

                              headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },

                              body: formData

                            });

                            // Document uploaded - list refreshes automatically

                            e.target.value = '';

                            const docsRes = await api.get('/documents?client_id=' + (user.client?.id || user.clientId));

                            setDocuments(docsRes.documents || docsRes || []);
                            setUploading(false);

                          } catch (err) {

                            setUploading(false);
                            alert('Upload failed: ' + err.message);

                          }

                        }}

                        className="hidden"

                      />

                      <label htmlFor="policyDocUpload" className="px-4 py-2 rounded-lg font-semibold cursor-pointer flex items-center gap-2" style={{ backgroundColor: '#F5F0E8', color: '#C45C26', border: '1px solid #C45C26' }}>

                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>

                        Upload Documents

                      </label>

                      <span className="text-sm text-gray-500">Attach policy documents, endorsements, invoices. </span><span className="text-sm" style={{ color: '#C45C26' }}> It may take a few seconds for documents to upload.</span>

                    </div>

                    

                    {/* Existing documents for Edit mode */}

                    {policyModal.mode === 'edit' && policyModal.policy && (

                      <div className="mt-3 p-3 rounded border" style={{ backgroundColor: '#fff', borderColor: '#E5E5E5' }}>

                        <p className="text-sm font-semibold mb-2" style={{ color: '#C45C26' }}>Attached Documents</p>

                        {documents.filter(d => d.policy_id === policyModal.policy.id).length === 0 ? (

                          <p className="text-sm text-gray-400 italic">No documents attached to this policy.</p>

                        ) : (

                          <div className="max-h-32 overflow-y-auto">

                            {documents.filter(d => d.policy_id === policyModal.policy.id).map(doc => (

                              <div key={doc.id} className="flex items-center justify-between p-2 mb-1 rounded" style={{ backgroundColor: '#F5F5F5' }}>

                                <div className="flex items-center gap-2 flex-1 min-w-0">

                                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#C45C26' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>

                                  <span className="text-sm truncate">{doc.original_name || doc.file_name}</span>

                                </div>

                                <div className="flex items-center gap-1">

                                  <button onClick={() => (async () => {

                              try {

                                const res = await fetch('/api/documents/' + doc.id + '/download', {

                                  headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }

                                });

                                const data = await res.json();

                                if (data.download_url) {

                                  window.open(data.download_url, '_blank');

                                } else {

                                  alert('Could not get download URL');

                                }

                              } catch (err) {

                                alert('Error: ' + err.message);

                              }

                            })()} className="p-1 rounded hover:bg-gray-200" title="View">

                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>

                                  </button>

                                  <button onClick={async () => { if (!confirm('Delete this document?')) return; try { await api.delete('/documents/' + doc.id); const docsRes = await api.get('/documents?client_id=' + (user.client?.id || user.clientId)); setDocuments(docsRes.documents || docsRes || []);
                            } catch (err) { alert('Delete failed'); } }} className="p-1 rounded hover:bg-red-100" style={{ color: '#EF4444' }} title="Delete">

                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>

                                  </button>

                                </div>

                              </div>

                            ))}

                          </div>

                        )}

                      </div>

                    )}

                    

                    {/* Selected documents list - always visible in Add mode */}

                    {policyModal.mode === 'add' && (

                      <div className="mt-3 p-3 rounded border" style={{ backgroundColor: '#fff', borderColor: '#E5E5E5' }}>

                        <p className="text-sm font-semibold mb-2" style={{ color: '#C45C26' }}>Selected Documents {pendingDocs.length > 0 && `(${pendingDocs.length})`}</p>

                        {pendingDocs.length === 0 ? (

                          <p className="text-sm text-gray-400 italic">No documents selected yet. Click "Upload Documents" to add files.</p>

                        ) : (

                          <div className="max-h-32 overflow-y-auto">

                            {pendingDocs.map((file, idx) => (

                              <div key={idx} className="flex items-center justify-between p-2 mb-1 rounded" style={{ backgroundColor: '#F5F5F5' }}>

                                <div className="flex items-center gap-2 flex-1 min-w-0">

                                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#C45C26' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>

                                  <span className="text-sm truncate">{file.name}</span>

                                </div>

                                <button onClick={() => setPendingDocs(prev => prev.filter((_, i) => i !== idx))} className="p-1 rounded hover:bg-red-100 flex-shrink-0 ml-2" style={{ color: '#EF4444' }} title="Remove">

                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>

                                </button>

                              </div>

                            ))}

                          </div>

                        )}

                      </div>

                    )}

                  </div>

                )}


                </div>

                

                {/* Footer */}

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: '#E5E5E5' }}>

                  <button onClick={() => setPolicyModal({ open: false, mode: 'add', policy: null })} 

                    className="px-5 py-2.5 rounded-lg font-semibold" style={{ color: '#6B6B6B' }}>

                    Cancel

                  </button>

                  {policyModal.mode === 'edit' && (

                    <button onClick={async () => {

                      if (!confirm('Are you sure you want to delete this policy? This action cannot be undone.')) return;

                      try {

                        await api.delete('/policies/' + policyModal.policy.id);

                        setPolicyModal({ open: false, mode: 'add', policy: null });

                        const res = await api.get('/policies?client_id=' + (user.client?.id || user.clientId));

                        setPolicies(Array.isArray(res?.policies) ? res.policies : Array.isArray(res) ? res : []);

                        alert('Policy deleted successfully');

                      } catch (err) {

                        alert('Error deleting policy: ' + (err.message || 'Unknown error'));

                      }

                    }} className="px-5 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#DC2626' }}>

                      Delete Policy

                    </button>

                  )}

                  <button onClick={async () => {

                    if (!policyForm.policy_number || !policyForm.category_id) {

                      alert('Please fill in required fields');

                      return;

                    }

                    try {

                      const payload = {

                        ...policyForm,

                        client_id: user.client?.id || user.clientId

                      };

                      if (policyModal.mode === 'add') {

                        const newPolicy = await api.post('/policies', payload);

                        console.log('New policy response:', newPolicy);

                        const policyId = newPolicy?.policyId || newPolicy?.policy?.id || newPolicy?.id || newPolicy?.insertId;

                        // Upload pending documents

                        if (pendingDocs.length > 0 && policyId) {

                          console.log('Uploading', pendingDocs.length, 'docs to policy', policyId);

                          for (const file of pendingDocs) {

                            const formData = new FormData();

                            formData.append('file', file);

                            formData.append('client_id', user.client?.id || user.clientId);

                            formData.append('policy_id', policyId);

                            formData.append('category_id', payload.category_id);

                            const uploadRes = await fetch('/api/documents/upload', {

                              method: 'POST',

                              headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },

                              body: formData

                            });

                            console.log('Upload result:', await uploadRes.json());

                          }

                          // Refresh documents

                          const docsRes = await api.get('/documents?client_id=' + (user.client?.id || user.clientId));

                          setDocuments(docsRes.documents || docsRes || []);

                        }

                        setPendingDocs([]);

                      } else {

                        await api.put('/policies/' + policyModal.policy.id, payload);

                      }

                      setPolicyModal({ open: false, mode: 'add', policy: null });

                      // Refresh policies

                      const res = await api.get('/policies?client_id=' + (user.client?.id || user.clientId));

      // Fetch categories

      try {

        const catRes = await api.get('/policies/categories/list');

        setAllCategories(catRes.categories || catRes.data || catRes || []);

      } catch (e) { console.log('Categories fetch error:', e); }

                      setPolicies(Array.isArray(res?.policies) ? res.policies : Array.isArray(res) ? res : []);

                    } catch (err) {

                      if (err.message && err.message.includes('upload')) {

                        alert('Policy saved but document upload failed: ' + err.message);

                      } else {

                        alert('Error: ' + (err.message || 'Failed to save policy'));

                      }

                    }

                  }} className="px-6 py-2.5 rounded-lg font-semibold text-white" style={{ backgroundColor: '#C45C26' }}>

                    {policyModal.mode === 'add' ? 'Create' : 'Save Changes'}

                  </button>

                </div>

              </div>

            </div>

          </div>

        )}

      </div>

    </div>

  );

}

// Vendor Dashboard
// ============================================
function VendorDashboard() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadAssignments();
  }, []);
  
  const loadAssignments = async () => {
    try {
      const data = await api.get('/vendors/my-assignments');
      setAssignments(data.byClient || []);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    } finally {
      setLoading(false);
    }
  };
  


  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;
  
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Welcome, {user?.name}</h1>
      <p className="text-slate-500">{user?.vendor?.company_name}</p>
      
      {/* Assigned Clients */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">My Assigned Clients</h3>
        
        {assignments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignments.map((client) => (
              <Link
                key={client.client_id}
                to={`/clients/${client.client_id}`}
                className="p-4 border border-slate-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="font-medium text-slate-800">{client.client_name}</h4>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Assigned Categories:</p>
                  <div className="flex flex-wrap gap-1">
                    {client.categories.map((cat) => (
                      <span key={cat.category_id} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                        {cat.category_name}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">No clients assigned yet</p>
        )}
      </div>
    </div>
  );
}

// ============================================
// Dashboard Router
// ============================================
function Dashboard() {
  const { user } = useAuth();
  
  if (user?.role === 'admin') return <AdminDashboard />;
  if (user?.role === 'client') return <ClientDashboard />;
  if (user?.role === 'vendor') return <VendorDashboard />;
  
  return <div>Unknown role</div>;
}

// ============================================
// Clients List (Admin)
// ============================================
function ClientsList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  useEffect(() => {
    loadClients();
  }, []);
  
  const loadClients = async () => {
    try {
      const data = await api.get('/admin/clients');
      setClients(data.clients || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };
  


  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Clients</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Client
        </button>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Company</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Contact</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Policies</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Vendors</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                      {client.logo ? (
                        <img src={client.logo} alt={client.company_name} className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-blue-600" />
                      )}
                        <img src={client.logo} alt={client.company_name} className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{client.company_name}</p>
                      <p className="text-sm text-slate-500">{client.user_email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-600">{client.contact_person || '-'}</td>
                <td className="px-6 py-4 text-slate-600">{client.policy_count || 0}</td>
                <td className="px-6 py-4 text-slate-600">{client.vendor_count || 0}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${client.user_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {client.user_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link to={`/clients/${client.id}`} className="text-blue-600 hover:text-blue-800">
                      <Eye className="w-5 h-5" />
                    </Link>
                    <Link to={`/clients/${client.id}/edit`} className="text-[#888888] hover:text-slate-600">
                      <Edit className="w-5 h-5" />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {showModal && (
        <CreateUserModal type="client" onClose={() => setShowModal(false)} onSuccess={loadClients} />
      )}
    </div>
  );
}

// ============================================
// Client Detail
// ============================================



// Self-contained Users Tab for ClientDetail

function UsersTabContent({ clientId, clientName, colors }) {

  const [clientUsers, setClientUsers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });

  const [saving, setSaving] = useState(false);



  useEffect(() => { loadUsers(); }, [clientId]);



  const loadUsers = async () => {

    try {

      setLoading(true);

      const data = await api.get("/admin/clients/" + clientId + "/users");

      setClientUsers(data.users || data || []);

    } catch (err) { console.error("Failed to load users:", err); }

    finally { setLoading(false); }

  };



  const handleAdd = async (e) => {

    e.preventDefault();

    if (!formData.name || !formData.email) { alert("Name and email required"); return; }

    setSaving(true);

    try {

      await api.post("/admin/clients/" + clientId + "/users", formData);

      setFormData({ name: '', email: '', password: '', phone: '' });

      setShowForm(false);

      loadUsers();

    } catch (err) { alert(err.message || "Failed to add user"); }

    finally { setSaving(false); }

  };



  return (

    <div className="bg-white rounded-xl shadow-sm border p-6">

      <div className="flex justify-between items-center mb-6">

        <h3 className="text-lg font-semibold" style={{ color: colors.charcoal }}>Users - {clientName}</h3>

        {!showForm && <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.rust }}><Plus className="w-4 h-4" /> Add User</button>}

      </div>

      {showForm && (

        <form onSubmit={handleAdd} className="mb-6 p-4 rounded-lg border space-y-3" style={{ borderColor: '#E5E5E5', backgroundColor: '#FAF8F5' }}>

          <h4 className="font-medium" style={{ color: colors.charcoal }}>Add New User to {clientName}</h4>

          <div className="grid grid-cols-2 gap-3">

            <input type="text" placeholder="Full Name *" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="px-3 py-2 border rounded-lg" required />

            <input type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="px-3 py-2 border rounded-lg" required />

            <input type="password" placeholder="Password (auto-generated if empty)" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="px-3 py-2 border rounded-lg" />

            <input type="text" placeholder="Phone (optional)" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="px-3 py-2 border rounded-lg" />

          </div>

          <div className="flex gap-2">

            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">Cancel</button>

            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.rust }}>{saving ? "Adding..." : "Add User"}</button>

          </div>

        </form>

      )}

      {loading ? <p className="text-center py-8 text-slate-500">Loading users...</p> : clientUsers.length === 0 ? (

        <div className="text-center py-8"><Users className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-slate-500">No users found for this client</p></div>

      ) : (

        <div className="space-y-3">

          {clientUsers.map(u => (

            <div key={u.id} className="flex items-center justify-between p-4 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>

              <div className="flex items-center gap-3">

                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: colors.rust }}>{(u.name || '?').charAt(0).toUpperCase()}</div>

                <div>

                  <p className="font-medium" style={{ color: colors.charcoal }}>{u.name}</p>

                  <p className="text-sm text-slate-500">{u.email}</p>

                </div>

              </div>

              <div className="flex items-center gap-2">

                <span className={"px-2 py-1 rounded-full text-xs " + (u.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>{u.is_active !== false ? "Active" : "Inactive"}</span>

                {u.phone && <span className="text-sm text-slate-500">{u.phone}</span>}

              </div>

            </div>

          ))}

        </div>

      )}

    </div>

  );

}



function ClientDetail() {

  const { id } = useParams();

  const { user } = useAuth();

  const [client, setClient] = useState(null);

  const [alerts, setAlerts] = useState([]);

  const [policies, setPolicies] = useState([]);

  const [vendors, setVendors] = useState([]);

  const [documents, setDocuments] = useState([]);

  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('policies');

  const [policyDetail, setPolicyDetail] = useState({ open: false, policy: null });

  const [filters, setFilters] = useState({ category: '', policyYear: '', vendor: '', subjectMatter: '' });

  const [lastUpdated, setLastUpdated] = useState(null);

  const [allCategories, setAllCategories] = useState([]);

  const [policyModal, setPolicyModal] = useState({ open: false, mode: 'add', policy: null });

  const policyYears = ['2024-2025', '2025-2026', '2026-2027', '2027-2028', '2028-2029', '2029-2030'];



  useEffect(() => { loadData(); }, [id]);



  const loadData = async () => {

    try {

      const clientId = id;

      const [clientData, alertsData, policiesData, vendorsData, docsData, catsData] = await Promise.all([

        api.get(`/clients/${clientId}`),

        api.get(`/policies/alerts/${clientId}`).catch(() => ({ alerts: [] })),

        api.get(`/policies?client_id=${clientId}`),

        api.get(`/clients/${clientId}/vendors`).catch(() => ({ vendors: [] })),

        api.get(`/documents?client_id=${clientId}`).catch(() => ({ documents: [] })),

        api.get('/policies/categories/list').catch(() => [])

      ]);

      setClient(clientData.client);

      setAlerts(alertsData.alerts || []);

      setPolicies(Array.isArray(policiesData?.policies) ? policiesData.policies : Array.isArray(policiesData) ? policiesData : []);

      setVendors(vendorsData.vendors || []);

      setDocuments(docsData.documents || docsData || []);

      setAllCategories(catsData.categories || catsData || []);

      setLastUpdated(new Date());

    } catch (error) {

      console.error('Failed to load data:', error);

    } finally {

      setLoading(false);

    }

  };



  if (loading) return <LoadingScreen />;

  if (!client) return <div className="p-8 text-center text-slate-500">Client not found</div>;



  const totalPremium = (Array.isArray(policies) ? policies : []).reduce((sum, p) => sum + (parseFloat(p.annual_premium_usd || p.annual_premium) || 0), 0);

  const now = new Date();

  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const in60 = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);

  const in90 = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

  const renewals30 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp >= now && exp <= in30; });

  const renewals60 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp > in30 && exp <= in60; });

  const renewals90 = policies.filter(p => { const exp = new Date(p.expiry_date); return exp > in60 && exp <= in90; });



  const categories = [...new Set(policies.map(p => p.category_name || p.category).filter(Boolean))];

  const vendorNames = [...new Set(vendors.map(v => v.name || v.company_name).filter(Boolean))];

  const subjectMatters = [...new Set(policies.map(p => p.subject_matter).filter(Boolean))];



  const filteredPolicies = policies.filter(p => {

    if (filters.category && (p.category_name || p.category) !== filters.category) return false;

    if (filters.policyYear && p.policy_year !== filters.policyYear) return false;

    if (filters.vendor && p.broker_name !== filters.vendor) return false;

    if (filters.subjectMatter && p.subject_matter !== filters.subjectMatter) return false;

    return true;

  });



  const docsByCategory = documents.reduce((acc, doc) => {

    const cat = doc.category_name || 'Uncategorized';

    const policy = policies.find(p => p.id === doc.policy_id);

    const policyYear = policy?.policy_year || 'Unknown Year';

    if (!acc[cat]) acc[cat] = {};

    if (!acc[cat][policyYear]) acc[cat][policyYear] = [];

    acc[cat][policyYear].push(doc);

    return acc;

  }, {});



  const colors = { rust: '#C45C26', rustDark: '#A34A1E', beige: '#F5F0E8', beigeLight: '#FAF8F4', charcoal: '#2D2D2D', darkGray: '#404040', mediumGray: '#6B6B6B', lightGray: '#9CA3AF', offWhite: '#FAFAF9' };



  const tabs = [

    { id: 'policies', label: 'Policy Summary', icon: FileText },

    { id: 'documents', label: 'Documents', icon: FolderOpen },

    { id: 'alerts', label: 'Alerts', icon: Bell },

    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },

  ];



  return (

    <div className="space-y-6">

      <div className="flex items-center justify-between">

        <div className="flex items-center gap-4">

          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: colors.rust }}>{client.company_name?.charAt(0)?.toUpperCase() || 'C'}</div>

          <div>

            <h1 className="text-2xl font-bold" style={{ color: colors.charcoal }}>Insurance Portfolio</h1>

            <p style={{ color: colors.mediumGray }}>{client.company_name}</p>

          </div>

        </div>

        <div className="flex items-center gap-4">

          <div className="text-right px-4 py-2 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>

            <p className="text-xs" style={{ color: colors.mediumGray }}>LAST SYNCED</p>

            <p className="text-sm font-medium" style={{ color: colors.charcoal }}>{lastUpdated?.toLocaleDateString('en-GB')}, {lastUpdated?.toLocaleTimeString()}</p>

            <button onClick={loadData} className="text-sm flex items-center gap-1 mt-1" style={{ color: colors.rust }}><RefreshCw className="w-3 h-3" /> Refresh</button>

          </div>

          <Link to={`/clients/${id}/edit`} className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: colors.beige, color: colors.charcoal }}><Edit className="w-4 h-4" /> Edit Client</Link>

        </div>

      </div>



      <div className="flex items-center justify-between border-b" style={{ borderColor: '#E5E5E5' }}>

        <div className="flex">

          {tabs.map((tab) => (

            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${activeTab === tab.id ? 'text-white rounded-t-lg' : ''}`} style={activeTab === tab.id ? { backgroundColor: colors.charcoal } : { color: colors.mediumGray }}>

              <tab.icon className="w-4 h-4" /> {tab.label}

            </button>

          ))}

        </div>

        <button onClick={() => setPolicyModal({ open: true, mode: 'add', policy: null })} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: colors.rust }}><Plus className="w-4 h-4" /> Add Policy</button>

      </div>



      {activeTab === 'policies' && (

        <div className="space-y-4">

          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: colors.beigeLight }}>

            <Filter className="w-4 h-4" style={{ color: colors.mediumGray }} /><span style={{ color: colors.mediumGray }}>Filters:</span>

            <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E5E5' }}><option value="">All Categories</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>

            <select value={filters.policyYear} onChange={e => setFilters({...filters, policyYear: e.target.value})} className="px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E5E5' }}><option value="">All Policy Years</option>{policyYears.map(y => <option key={y} value={y}>{y}</option>)}</select>

            <select value={filters.vendor} onChange={e => setFilters({...filters, vendor: e.target.value})} className="px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E5E5' }}><option value="">All Brokers / Vendors</option>{vendorNames.map(v => <option key={v} value={v}>{v}</option>)}</select>

            <select value={filters.subjectMatter || ''} onChange={e => setFilters({...filters, subjectMatter: e.target.value})} className="px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E5E5' }}><option value="">All Interest / Subject Matter</option>{subjectMatters.map(s => <option key={s} value={s}>{s}</option>)}</select>

            <span className="ml-auto text-sm" style={{ color: colors.mediumGray }}>Showing <span style={{ color: colors.rust }}>{filteredPolicies.length}</span> of {policies.length} policies</span>

          </div>

          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E5E5' }}>

            <table className="w-full">

              <thead style={{ backgroundColor: colors.beigeLight }}>

                <tr>{['POLICY NUMBER', 'CATEGORY', 'INTEREST / SUBJECT MATTER', 'POLICY YEAR', 'INSURER', 'BROKER / VENDOR', 'ANNUAL PREMIUM (POLICY CURRENCY)', 'ANNUAL PREMIUM (USD)', 'PRO-RATA PREMIUM (POLICY CURRENCY)', 'PRO-RATA PREMIUM (USD)', 'POLICY START', 'POLICY EXPIRY', 'STATUS', 'DOCS', 'AI SUMMARY'].map(h => (<th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase" style={{ color: colors.mediumGray, minWidth: h.includes('PREMIUM') ? '160px' : undefined }}>{h}</th>))}</tr>

              </thead>

              <tbody className="divide-y" style={{ borderColor: '#E5E5E5' }}>

                {filteredPolicies.map(policy => (

                  <tr key={policy.id} className="hover:bg-slate-50">

                    <td className="px-4 py-3"><button onClick={() => setPolicyDetail({ open: true, policy })} className="font-medium text-left hover:underline" style={{ color: colors.rust }}>{policy.policy_number}</button></td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.category_name || policy.category}</td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.subject_matter || '-'}</td>

                    <td className="px-4 py-3 text-sm text-center" style={{ color: colors.darkGray }}>{policy.policy_year || '-'}</td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.insurer || '-'}</td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.broker_name || '-'}</td>

                    <td className="px-4 py-3 text-sm font-medium" style={{ color: colors.charcoal }}>{policy.currency || 'USD'} {Math.round(parseFloat(policy.annual_premium) || 0).toLocaleString()}</td>

                    <td className="px-4 py-3 text-sm font-medium" style={{ color: colors.charcoal }}>$ {Math.round(parseFloat(policy.annual_premium_usd) || 0).toLocaleString()}</td>

                    <td className="px-4 py-3 text-sm font-medium" style={{ color: colors.charcoal }}>{policy.currency || 'USD'} {Math.round(parseFloat(policy.prorata_premium) || 0).toLocaleString()}</td>

                    <td className="px-4 py-3 text-sm font-medium" style={{ color: colors.charcoal }}>$ {Math.round(parseFloat(policy.prorata_premium_usd) || 0).toLocaleString()}</td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.start_date ? new Date(policy.start_date).toLocaleDateString('en-GB') : '-'}</td>

                    <td className="px-4 py-3 text-sm" style={{ color: colors.darkGray }}>{policy.expiry_date ? new Date(policy.expiry_date).toLocaleDateString('en-GB') : '-'}</td>

                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${policy.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{policy.status}</span></td>

                    <td className="px-4 py-3 text-center"><span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">{documents.filter(d => d.policy_id === policy.id).length}</span></td>

                    <td className="px-4 py-3"><button className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white" style={{ backgroundColor: colors.charcoal }}><MessageSquare className="w-3 h-3" /> Summary</button></td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>

      )}



      {activeTab === 'documents' && (

        <div className="space-y-4">

          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: colors.beigeLight }}>

            <Filter className="w-4 h-4" style={{ color: colors.mediumGray }} /><span style={{ color: colors.mediumGray }}>Filters:</span>

            <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: '#E5E5E5' }}><option value="">All Categories</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>

            <span className="ml-auto text-sm" style={{ color: colors.rust }}>{documents.length} documents total</span>

          </div>

          <div className="space-y-4">

            {Object.entries(docsByCategory).map(([category, years]) => (

              <div key={category} className="rounded-xl border" style={{ borderColor: '#E5E5E5' }}>

                <div className="flex items-center gap-3 p-4" style={{ backgroundColor: colors.beigeLight }}><FolderOpen className="w-5 h-5" style={{ color: colors.rust }} /><span className="font-semibold" style={{ color: colors.charcoal }}>{category}</span><span className="text-sm" style={{ color: colors.mediumGray }}>({Object.values(years).flat().length} files)</span></div>

                {Object.entries(years).map(([year, docs]) => (

                  <div key={year} className="border-t" style={{ borderColor: '#E5E5E5' }}>

                    <div className="flex items-center gap-2 px-6 py-2" style={{ backgroundColor: colors.offWhite }}><FolderOpen className="w-4 h-4" style={{ color: colors.mediumGray }} /><span className="text-sm font-medium" style={{ color: colors.darkGray }}>{year}</span><span className="text-xs" style={{ color: colors.lightGray }}>({docs.length} files)</span></div>

                    {docs.map(doc => (

                      <div key={doc.id} className="flex items-center justify-between px-8 py-3 border-t" style={{ borderColor: '#E5E5E5' }}>

                        <div className="flex items-center gap-3"><FileText className="w-5 h-5" style={{ color: colors.lightGray }} /><div><p className="font-medium" style={{ color: colors.charcoal }}>{doc.original_name || doc.file_name}</p><p className="text-xs" style={{ color: colors.lightGray }}>Uploaded {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</p></div></div>

                        <div className="flex items-center gap-2">

                          <button className="flex items-center gap-1 px-3 py-1.5 rounded text-sm text-white" style={{ backgroundColor: colors.charcoal }}><MessageSquare className="w-3 h-3" /> AI Summary</button>

                          <button className="flex items-center gap-1 px-3 py-1.5 rounded text-sm text-white" style={{ backgroundColor: colors.rust }}><Eye className="w-3 h-3" /> View</button>

                          <button className="p-1.5 rounded hover:bg-slate-100"><Download className="w-4 h-4" style={{ color: colors.mediumGray }} /></button>

                        </div>

                      </div>

                    ))}

                  </div>

                ))}

              </div>

            ))}

            {Object.keys(docsByCategory).length === 0 && <div className="text-center py-12" style={{ color: colors.mediumGray }}>No documents uploaded yet</div>}

          </div>

        </div>

      )}



      {activeTab === 'alerts' && (

        <div className="space-y-6">

          <div className="grid grid-cols-4 gap-4">

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5" style={{ color: colors.rust }} /><span style={{ color: colors.mediumGray }}>Renewal Alerts</span></div><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>{renewals30.length + renewals60.length + renewals90.length}</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><div className="flex items-center gap-2 mb-2"><DollarSign className="w-5 h-5" style={{ color: colors.rust }} /><span style={{ color: colors.mediumGray }}>Premium Due</span></div><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>0</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><div className="flex items-center gap-2 mb-2"><Search className="w-5 h-5" style={{ color: colors.rust }} /><span style={{ color: colors.mediumGray }}>Survey Deadlines</span></div><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>0</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><div className="flex items-center gap-2 mb-2"><Clock className="w-5 h-5" style={{ color: colors.rust }} /><span style={{ color: colors.mediumGray }}>Warranty Deadlines</span></div><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>0</p></div>

          </div>

          <div className="p-6 rounded-xl" style={{ backgroundColor: colors.beigeLight }}>

            <h3 className="font-semibold mb-4" style={{ color: colors.charcoal }}>All Alerts</h3>

            {alerts.length === 0 ? (<div className="text-center py-8"><CheckCircle className="w-12 h-12 mx-auto mb-2" style={{ color: colors.rust, opacity: 0.5 }} /><p className="font-medium" style={{ color: colors.charcoal }}>You're all caught up!</p><p style={{ color: colors.mediumGray }}>No pending alerts at this time</p></div>) : (<div className="space-y-2">{alerts.map((a, i) => <div key={i} className="p-3 bg-white rounded-lg">{a.message || a.subject}</div>)}</div>)}

          </div>

        </div>

      )}



      {activeTab === 'overview' && (

        <div className="space-y-6">

          <div className="grid grid-cols-4 gap-4">

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs uppercase" style={{ color: colors.rust }}>Active Policies</p><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>{policies.filter(p => p.status === 'active').length}</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs uppercase" style={{ color: colors.rust }}>Annual Premium</p><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>${totalPremium >= 1000 ? (totalPremium/1000).toFixed(0) + 'K' : totalPremium.toLocaleString()}</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs uppercase" style={{ color: colors.rust }}>Vendors</p><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>{vendors.length}</p></div>

            <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs uppercase" style={{ color: colors.rust }}>Documents</p><p className="text-2xl font-bold" style={{ color: colors.charcoal }}>{documents.length}</p></div>

          </div>

          <div className="p-6 rounded-xl" style={{ backgroundColor: colors.beigeLight }}>

            <div className="flex items-center gap-2 mb-4"><Calendar className="w-5 h-5" style={{ color: colors.rust }} /><h3 className="font-semibold" style={{ color: colors.charcoal }}>Renewal Timeline</h3></div>

            <p className="text-sm mb-4" style={{ color: colors.mediumGray }}>Upcoming policy renewals by timeframe</p>

            <div className="grid grid-cols-3 gap-4">

              <div className="p-4 rounded-xl border-2" style={{ borderColor: '#DC2626' }}><div className="flex justify-between items-center mb-2"><span className="text-xs uppercase" style={{ color: '#DC2626' }}>URGENT</span><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.charcoal, color: 'white' }}>0-30 days</span></div><p className="text-3xl font-bold" style={{ color: colors.rust }}>{renewals30.length}</p><p className="text-sm" style={{ color: colors.mediumGray }}>policies expiring</p></div>

              <div className="p-4 rounded-xl border-2" style={{ borderColor: '#F59E0B' }}><div className="flex justify-between items-center mb-2"><span className="text-xs uppercase" style={{ color: '#F59E0B' }}>ATTENTION</span><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.charcoal, color: 'white' }}>31-60 days</span></div><p className="text-3xl font-bold" style={{ color: '#F59E0B' }}>{renewals60.length}</p><p className="text-sm" style={{ color: colors.mediumGray }}>policies expiring</p></div>

              <div className="p-4 rounded-xl border-2" style={{ borderColor: '#10B981' }}><div className="flex justify-between items-center mb-2"><span className="text-xs uppercase" style={{ color: '#10B981' }}>PLANNED</span><span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.charcoal, color: 'white' }}>61-90 days</span></div><p className="text-3xl font-bold" style={{ color: '#10B981' }}>{renewals90.length}</p><p className="text-sm" style={{ color: colors.mediumGray }}>policies expiring</p></div>

            </div>

          </div>

          <div className="grid grid-cols-2 gap-6">

            <div className="p-6 rounded-xl" style={{ backgroundColor: colors.beigeLight }}><div className="flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5" style={{ color: colors.rust }} /><h3 className="font-semibold" style={{ color: colors.charcoal }}>Premium Due</h3></div><p className="text-sm mb-4" style={{ color: colors.mediumGray }}>Payments due within 30 days</p><div className="text-center py-6"><CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: colors.rust, opacity: 0.5 }} /><p style={{ color: colors.mediumGray }}>No premiums due in the next 30 days</p></div></div>

            <div className="p-6 rounded-xl" style={{ backgroundColor: colors.beigeLight }}><div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5" style={{ color: colors.rust }} /><h3 className="font-semibold" style={{ color: colors.charcoal }}>Action Required</h3></div><p className="text-sm mb-4" style={{ color: colors.mediumGray }}>Items needing your attention</p><div className="text-center py-6"><CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: colors.rust, opacity: 0.5 }} /><p style={{ color: colors.mediumGray }}>All caught up! No pending alerts</p></div></div>

          </div>

          <div className="p-4 rounded-xl" style={{ backgroundColor: colors.charcoal }}>

            <div className="grid grid-cols-4 gap-4 text-center text-white">

              <div><p className="text-xs opacity-70">Policy Categories</p><p className="text-2xl font-bold">{categories.length}</p></div>

              <div><p className="text-xs opacity-70">Active Coverage</p><p className="text-2xl font-bold">{policies.filter(p => p.status === 'active').length}</p></div>

              <div><p className="text-xs opacity-70">This Year Renewals</p><p className="text-2xl font-bold">{renewals30.length + renewals60.length + renewals90.length}</p></div>

              <div><p className="text-xs opacity-70">Total Documents</p><p className="text-2xl font-bold">{documents.length}</p></div>

            </div>

          </div>

        </div>

      )}

      {activeTab === 'users' && (

        <UsersTabContent clientId={id} clientName={client?.company_name} colors={colors} />

      )}




      {/* Policy Detail Modal */}

      {policyDetail.open && policyDetail.policy && (

        <div className="fixed inset-0 z-50 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

          <div className="flex items-center justify-center min-h-screen p-4">

            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>

              <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E5E5', backgroundColor: '#2D2D2D' }}>

                <div className="flex items-center gap-4">

                  <div className="p-2.5 rounded-xl" style={{ backgroundColor: '#C45C26' }}><FileText className="w-6 h-6 text-white" /></div>

                  <div><h3 className="font-bold text-xl text-white">Policy Details</h3><p className="text-sm text-gray-400">{policyDetail.policy.policy_number}</p></div>

                </div>

                <button onClick={() => setPolicyDetail({ open: false, policy: null })} className="p-2 rounded-lg hover:bg-white/10 transition-colors"><X className="w-6 h-6 text-white" /></button>

              </div>

              <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>

                <div className="flex items-center gap-3 mb-6">

                  <span className="px-4 py-2 rounded-full text-sm font-bold" style={{ backgroundColor: policyDetail.policy.status === 'active' ? '#D1FAE5' : '#FEE2E2', color: policyDetail.policy.status === 'active' ? '#065F46' : '#991B1B' }}>{policyDetail.policy.status === 'active' ? '● Active' : '● ' + (policyDetail.policy.status || 'Inactive')}</span>

                  <span className="text-sm" style={{ color: '#6B6B6B' }}>{documents.filter(d => d.policy_id === policyDetail.policy.id).length} documents</span>

                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">

                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Number</p><p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_number}</p></div>

                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Category</p><p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.category_name || '-'}</p></div>

                </div>

                <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Coverage Details</h4>

                <div className="grid grid-cols-2 gap-4 mb-6">

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Sum Insured</p><p className="text-xl font-bold" style={{ color: '#2D2D2D' }}>${parseFloat(policyDetail.policy.sum_insured || 0).toLocaleString()}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Annual Premium</p><p className="text-xl font-bold" style={{ color: '#C45C26' }}>${parseFloat(policyDetail.policy.annual_premium || 0).toLocaleString()}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Insurer</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.insurer || '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Broker</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.broker_name || '-'}</p></div>

                </div>

                <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Important Dates</h4>

                <div className="grid grid-cols-3 gap-4 mb-6">

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Year</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_year || '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Start Date</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.start_date ? new Date(policyDetail.policy.start_date).toLocaleDateString() : '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Expiry Date</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.expiry_date ? new Date(policyDetail.policy.expiry_date).toLocaleDateString() : '-'}</p></div>

                </div>

              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ borderColor: '#E5E5E5' }}>

                <div className="flex items-center gap-3">

                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#C45C26' }}><MessageSquare className="w-4 h-4" /> AI Summary</button>

                  <button onClick={() => { setPolicyDetail({ open: false, policy: null }); window.location.href = `/policies/${policyDetail.policy.id}/edit`; }} className="flex items-center gap-2 px-4 py-2 rounded-lg border" style={{ borderColor: '#E5E5E5', color: '#2D2D2D' }}><Edit className="w-4 h-4" /> Edit Policy</button>

                </div>

                <button onClick={() => setPolicyDetail({ open: false, policy: null })} className="px-6 py-2 rounded-lg text-white" style={{ backgroundColor: '#2D2D2D' }}>Close</button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>

  );

}




function ClientEdit() {

  const { id } = useParams();

  const navigate = useNavigate();

  const [client, setClient] = useState(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({

    company_name: '',

    contact_person: '',

    contact_email: '',

    contact_phone: '',

    google_sheet_id: '',

    google_sheet_name: ''

  });

  const [logo, setLogo] = useState(null);

  const [uploading, setUploading] = useState(false);



  useEffect(() => {

    loadClient();

  }, [id]);



  const loadClient = async () => {

    try {

      const data = await api.get(`/admin/clients/${id}`);

      setClient(data.client);

      setFormData({

        company_name: data.client.company_name || '',

        contact_person: data.client.contact_person || '',

        contact_email: data.client.contact_email || '',

        contact_phone: data.client.contact_phone || '',

        google_sheet_id: data.client.google_sheet_id || '',

        google_sheet_name: data.client.google_sheet_name || 'Policies'

      });

      setLogo(data.client.logo);

    } catch (error) {

      console.error('Failed to load client:', error);

    } finally {

      setLoading(false);

    }

  };



  const handleSubmit = async (e) => {

    e.preventDefault();

    setSaving(true);

    try {

      await api.put(`/admin/clients/${id}`, formData);

      navigate(`/clients/${id}`);

    } catch (error) {

      console.error('Failed to update client:', error);

      alert('Failed to update client');

    } finally {

      setSaving(false);

    }

  };



  const handleLogoUpload = async (e) => {

    const file = e.target.files[0];

    if (!file) return;

    

    setUploading(true);

    try {

      const formData = new FormData();

      formData.append('logo', file);

      const response = await api.upload(`/admin/clients/${id}/logo`, formData);

      if (response.logo) {

        setLogo(response.logo);

      }

    } catch (error) {

      console.error('Failed to upload logo:', error);

      alert('Failed to upload logo');

    } finally {

      setUploading(false);

    }

  };





  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;



  return (

    <div className="max-w-2xl mx-auto space-y-6">

      <div className="flex items-center gap-4">

        <button onClick={() => navigate(`/clients/${id}`)} className="text-slate-500 hover:text-slate-700">

          <ChevronLeft className="w-6 h-6" />

        </button>

        <h1 className="text-2xl font-bold text-slate-800">Edit Client</h1>

      </div>



      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-6">

        {/* Logo Upload */}

        <div className="flex items-center gap-6 pb-6 border-b border-slate-200">

          <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">

            {logo ? (

              <img src={logo} alt="Logo" className="w-full h-full object-contain" />

            ) : (

              <Building2 className="w-10 h-10 text-[#888888]" />

            )}

          </div>

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-2">Company Logo</label>

            <label className="cursor-pointer inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors">

              <Upload className="w-4 h-4" />

              {uploading ? 'Uploading...' : 'Upload Logo'}

              <input

                type="file"

                accept="image/*"

                onChange={handleLogoUpload}

                className="hidden"

                disabled={uploading}

              />

            </label>

            <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>

          </div>

        </div>



        <div>

          <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>

          <input

            type="text"

            value={formData.company_name}

            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}

            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

            required

          />

        </div>



        <div className="grid grid-cols-2 gap-4">

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>

            <input

              type="text"

              value={formData.contact_person}

              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}

              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

            />

          </div>

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>

            <input

              type="email"

              value={formData.contact_email}

              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}

              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

            />

          </div>

        </div>



        <div>

          <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>

          <input

            type="tel"

            value={formData.contact_phone}

            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}

            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

          />

        </div>



        <div className="border-t pt-6">

          <h3 className="font-medium text-slate-800 mb-4">Google Sheets Integration</h3>

          <div className="space-y-4">

            <div>

              <label className="block text-sm font-medium text-slate-700 mb-1">Google Sheet ID</label>

              <input

                type="text"

                value={formData.google_sheet_id}

                onChange={(e) => setFormData({ ...formData, google_sheet_id: e.target.value })}

                placeholder="e.g., 1y0YEOaZ8QREb7gO9o3BuGsEPHN1kPQ6w8Ij26T05J1A"

                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

              />

              <p className="text-xs text-slate-500 mt-1">The ID from your Google Sheet URL</p>

            </div>

            <div>

              <label className="block text-sm font-medium text-slate-700 mb-1">Sheet Tab Name</label>

              <input

                type="text"

                value={formData.google_sheet_name}

                onChange={(e) => setFormData({ ...formData, google_sheet_name: e.target.value })}

                placeholder="Policies"

                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

              />

              <p className="text-xs text-slate-500 mt-1">The tab name in your spreadsheet (default: Policies)</p>

            </div>

            {formData.google_sheet_id && client?.contact_email && (

              <div className="bg-slate-50 p-4 rounded-lg">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="font-medium text-slate-800">Share Google Sheet with Client</p>

                    <p className="text-sm text-slate-500">Will share with: {client.contact_email}</p>

                  </div>

                  <button

                    type="button"

                    onClick={async () => {

                      const newShareState = !client.share_google_sheet;

                      try {

                        await api.post('/admin/clients/' + client.id + '/share-sheet', { share: newShareState });

                        setClient({ ...client, share_google_sheet: newShareState });

                        alert(newShareState ? 'Google Sheet shared successfully!' : 'Google Sheet access removed');

                      } catch (err) { alert(err.message || 'Failed to update sharing'); }

                    }}

                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${client.share_google_sheet ? 'bg-green-500' : 'bg-slate-300'}`}

                  >

                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${client.share_google_sheet ? 'translate-x-6' : 'translate-x-1'}`} />

                  </button>

                </div>

                {client.share_google_sheet && client.sheet_shared_at && (

                  <p className="text-xs text-green-600 mt-2">Shared on: {new Date(client.sheet_shared_at).toLocaleDateString()}</p>

                )}

              </div>

            )}

          </div>

        </div>



        <div className="flex gap-3 pt-4">

          <button

            type="button"

            onClick={() => navigate(`/clients/${id}`)}

            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"

          >

            Cancel

          </button>

          <button

            type="submit"

            disabled={saving}

            className="flex-1 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg disabled:opacity-50"

          >

            {saving ? 'Saving...' : 'Save Changes'}

          </button>

        </div>

      </form>

    </div>

  );

}



// ============================================
// Client Policies Tab
// ============================================
function ClientPolicies({ clientId, categories }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadPolicies();
  }, [clientId]);
  
  const loadPolicies = async () => {
    try {
      const data = await api.get(`/policies?client_id=${clientId}`);
      setPolicies(data.policies || []);
    } catch (error) {
      console.error('Failed to load policies:', error);
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleSync = async () => {
    try {
      const data = await api.get(`/policies/sync/${clientId}`);
      if (data.success) {
        alert(`Synced ${data.synced} policies from Database`);
        loadPolicies();
      } else {
        alert(data.message || "Sync failed");
      }
    } catch (error) {
      console.error("Sync failed:", error);
      alert("Failed to sync from Database");
    }
  };
  
  if (loading) return <div className="text-center py-8">Loading policies...</div>;
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">Policies ({policies.length})</h3>
        <button onClick={handleSync} className="flex items-center gap-2 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg">
          <RefreshCw className="w-4 h-4" />
          Sync from Database
        </button>
      </div>
      <PolicyTable policies={policies} />
    </div>
  );
}

// ============================================



// ============================================

// Extraction Modal Component

// ============================================

function ExtractionModal({ isOpen, onClose, documentId, documentName, clientId, onSyncComplete }) {

  const [extracting, setExtracting] = useState(false);

  const [extracted, setExtracted] = useState(null);

  const [editedData, setEditedData] = useState({});

  const [confidence, setConfidence] = useState({});

  const [warnings, setWarnings] = useState([]);

  const [error, setError] = useState(null);

  const [syncing, setSyncing] = useState(false);



  const fieldLabels = {

    policyNumber: 'Policy Number',

    category: 'Category',

    subCategory: 'Sub Category',

    insurer: 'Insurer',

    interestSubjectMatter: 'Interest/Subject Matter',

    sumInsured: 'Sum Insured',

    currency: 'Currency',

    annualPremium: 'Annual Premium',

    startDate: 'Start Date',

    expiryDate: 'Expiry Date',

    brokerName: 'Broker',

    assuredName: 'Assured Name',

    coAssured: 'Co-Assured',

    deductible: 'Deductible',

    territorialLimits: 'Territorial Limits',

    specialConditions: 'Special Conditions',

    remarks: 'Remarks'

  };



  const categoryOptions = [

    'Fire', 'Marine Cargo', 'Marine Hull', 'Motor', 

    'Workmen Compensation', 'Group Medical', 'Group Personal Accident',

    'Professional Indemnity', 'Directors & Officers', 'Cyber',

    'Public Liability', 'Product Liability', 'Money Insurance',

    'Fidelity Guarantee', 'Other'

  ];



  const currencyOptions = ['USD', 'AED', 'INR', 'EUR', 'GBP', 'SAR'];



  useEffect(() => {

    if (isOpen && documentId) {

      runExtraction();

    }

  }, [isOpen, documentId]);



  const runExtraction = async () => {

    setExtracting(true);

    setError(null);

    setExtracted(null);

    

    try {

      const result = await api.post('/extraction/extract-document', { documentId, clientId });

      setExtracted(result.data);

      setEditedData(result.data.extracted || {});

      setConfidence(result.data.confidence || {});

      setWarnings(result.data.warnings || []);

    } catch (err) {

      setError(err.message || 'Extraction failed');

    } finally {

      setExtracting(false);

    }

  };



  const handleFieldChange = (field, value) => {

    setEditedData(prev => ({ ...prev, [field]: value }));

  };



  const handleApproveAndSync = async () => {

    setSyncing(true);

    setError(null);



    try {

      const result = await api.post('/extraction/approve-and-sync', {

        documentId,

        clientId,

        extractedData: editedData,

        createNew: true

      });



      if (onSyncComplete) onSyncComplete(result.data);

      onClose();

    } catch (err) {

      setError(err.message || 'Sync failed');

    } finally {

      setSyncing(false);

    }

  };



  const getConfidenceColor = (score) => {

    if (score >= 90) return 'text-green-600';

    if (score >= 70) return 'text-yellow-600';

    if (score >= 50) return 'text-orange-500';

    return 'text-red-500';

  };



  const getConfidenceBg = (score) => {

    if (score >= 90) return 'bg-green-500';

    if (score >= 70) return 'bg-yellow-500';

    if (score >= 50) return 'bg-orange-500';

    return 'bg-red-500';

  };



  if (!isOpen) return null;



  return (

    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">

      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}

        <div className="flex items-center justify-between p-5 border-b">

          <div>

            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">

              <FileText className="w-5 h-5" />

              Extract Policy Data

            </h2>

            <p className="text-sm text-slate-500 mt-1">{documentName}</p>

          </div>

          <button onClick={onClose} className="text-[#888888] hover:text-slate-600 text-2xl">&times;</button>

        </div>



        {/* Body */}

        <div className="flex-1 overflow-auto p-5">

          {/* Loading */}

          {extracting && (

            <div className="flex flex-col items-center justify-center py-16">

              <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />

              <p className="text-slate-600">AI is reading the document...</p>

              <p className="text-sm text-[#888888] mt-1">This may take 10-15 seconds</p>

            </div>

          )}



          {/* Error */}

          {error && (

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">

              <p className="text-red-700 flex items-center gap-2">

                <AlertTriangle className="w-5 h-5" />

                {error}

              </p>

              <button onClick={runExtraction} className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium">

                Retry Extraction

              </button>

            </div>

          )}



          {/* Warnings */}

          {warnings.length > 0 && (

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">

              <p className="font-medium text-yellow-800 mb-2">Notes:</p>

              <ul className="text-sm text-yellow-700 list-disc list-inside">

                {warnings.map((w, i) => <li key={i}>{w}</li>)}

              </ul>

            </div>

          )}



          {/* Confidence Banner */}

          {extracted && !extracting && (

            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg mb-6">

              <span className="text-slate-600">Overall Confidence:</span>

              <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getConfidenceBg(extracted.overallConfidence)}`}>

                {extracted.overallConfidence}%

              </span>

              <span className="text-slate-500 ml-auto">

                Type: {extracted.documentType}

              </span>

            </div>

          )}



          {/* Fields */}

          {extracted && !extracting && (

            <div className="space-y-4">

              {Object.entries(fieldLabels).map(([field, label]) => (

                <div key={field} className="flex flex-wrap items-center gap-3">

                  <label className="w-40 text-sm font-medium text-slate-700 flex items-center gap-2">

                    {label}

                    {confidence[field] > 0 && (

                      <span className={`text-xs font-semibold ${getConfidenceColor(confidence[field])}`}>

                        {confidence[field]}%

                      </span>

                    )}

                  </label>

                  

                  {field === 'category' ? (

                    <select

                      value={editedData[field] || ''}

                      onChange={(e) => handleFieldChange(field, e.target.value)}

                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C45C26] focus:border-blue-500"

                    >

                      <option value="">Select Category</option>

                      {categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}

                    </select>

                  ) : field === 'currency' ? (

                    <select

                      value={editedData[field] || 'USD'}

                      onChange={(e) => handleFieldChange(field, e.target.value)}

                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C45C26] focus:border-blue-500"

                    >

                      {currencyOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}

                    </select>

                  ) : field === 'specialConditions' || field === 'remarks' || field === 'interestSubjectMatter' ? (

                    <textarea

                      value={editedData[field] || ''}

                      onChange={(e) => handleFieldChange(field, e.target.value)}

                      rows={2}

                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C45C26] focus:border-blue-500"

                    />

                  ) : (

                    <input

                      type={['startDate', 'expiryDate'].includes(field) ? 'date' : 

                            ['sumInsured', 'annualPremium', 'deductible'].includes(field) ? 'number' : 'text'}

                      value={editedData[field] || ''}

                      onChange={(e) => handleFieldChange(field, e.target.value)}

                      className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26] focus:border-blue-500 ${

                        confidence[field] < 70 && editedData[field] ? 'border-orange-300' : 'border-slate-300'

                      }`}

                    />

                  )}

                </div>

              ))}

            </div>

          )}

        </div>



        {/* Footer */}

        {extracted && !extracting && (

          <div className="flex justify-end gap-3 p-5 border-t bg-slate-50">

            <button

              onClick={onClose}

              className="px-5 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"

            >

              Cancel

            </button>

            <button

              onClick={handleApproveAndSync}

              disabled={syncing}

              className="px-5 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"

            >

              {syncing ? (

                <>

                  <RefreshCw className="w-4 h-4 animate-spin" />

                  Syncing...

                </>

              ) : (

                <>

                  <CheckCircle className="w-4 h-4" />

                  Approve & Save Policy

                </>

              )}

            </button>

          </div>

        )}

      </div>

    </div>

  );

}



// Client Documents Tab
// ============================================
function ClientDocuments({ clientId, categories }) {
  const [extractionModal, setExtractionModal] = useState({ open: false, doc: null });
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadFolders();
  }, [clientId]);
  
  const loadFolders = async () => {
    try {
      const data = await api.get(`/documents/folders/${clientId}`);
      setFolders(data.folders || []);
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadDocuments = async (categoryId) => {
    setSelectedCategory(categoryId);
    try {
      const data = await api.get(`/documents?client_id=${clientId}&category_id=${categoryId}`);
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };
  
  const handleUpload = async (e) => {
    if (!selectedCategory) return;
    
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    formData.append('category_id', selectedCategory);
    
    try {
      await api.upload('/documents/upload', formData);
      loadDocuments(selectedCategory);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };
  
  if (loading) return <div className="text-center py-8">Loading documents...</div>;
  
  return (
    <div className="grid grid-cols-4 gap-6">
      {/* Folder List */}
      <div className="col-span-1 space-y-2">
        <h4 className="font-medium text-slate-700 mb-3">Folders</h4>
        {folders.map((folder) => (
          <button
            key={folder.category_id}
            onClick={() => loadDocuments(folder.category_id)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
              selectedCategory === folder.category_id
                ? 'bg-blue-100 text-blue-700'
                : 'hover:bg-slate-100 text-slate-600'
            }`}
          >
            <FolderOpen className="w-5 h-5" />
            <div className="flex-1">
              <p className="font-medium">{folder.category_name}</p>
              <p className="text-sm opacity-70">{folder.document_count} files</p>
            </div>
          </button>
        ))}
      </div>
      
      {/* Document List */}
      <div className="col-span-3">
        {selectedCategory ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-slate-700">
                {folders.find(f => f.category_id === selectedCategory)?.category_name} Documents
              </h4>
              {(user?.role === 'admin' || folders.find(f => f.category_id === selectedCategory)?.can_upload) && (
                <label className="flex items-center gap-2 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Upload
                  <input type="file" className="hidden" onChange={handleUpload} />
                </label>
              )}
            </div>
            
            {documents.length > 0 ? (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#888888]" />
                      <div>
                        <p className="font-medium text-slate-700">{doc.original_name}</p>
                        <p className="text-sm text-slate-500">
                          Uploaded by {doc.uploaded_by_name} • {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">

                      {doc.file_type === 'application/pdf' && (

                        <button

                          onClick={() => setExtractionModal({ open: true, doc })}

                          className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"

                        >

                          <RefreshCw className="w-4 h-4" />

                          Sync Info

                        </button>

                      )}

                      <button

                        onClick={() => (async () => {

                              try {

                                const res = await fetch('/api/documents/' + doc.id + '/download', {

                                  headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }

                                });

                                const data = await res.json();

                                if (data.download_url) {

                                  window.open(data.download_url, '_blank');

                                } else {

                                  alert('Could not get download URL');

                                }

                              } catch (err) {

                                alert('Error: ' + err.message);

                              }

                            })()}

                        className="text-blue-600 hover:text-blue-800"

                      >

                        <Download className="w-5 h-5" />

                      </button>

                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No documents in this folder</p>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">Select a folder to view documents</p>
        )}
      </div>

      {/* Extraction Modal */}

      <ExtractionModal

        isOpen={extractionModal.open}

        onClose={() => setExtractionModal({ open: false, doc: null })}

        documentId={extractionModal.doc?.id}

        documentName={extractionModal.doc?.original_name}

        clientId={clientId}

        onSyncComplete={() => { setExtractionModal({ open: false, doc: null }); if (selectedCategory) loadDocuments(selectedCategory); }}

      />


    </div>
  );
}

// ============================================
// Client Vendors Tab (Assignment Management)
// ============================================
function ClientVendors({ clientId, vendors, onUpdate }) {
  const { user } = useAuth();
  const [allVendors, setAllVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'client') {
      loadVendorsAndCategories();
    }
  }, []);
  
  const loadVendorsAndCategories = async () => {
    try {
      const [vendorsData, categoriesData] = await Promise.all([
        api.get('/vendors'),
        api.get('/policies/categories/list')
      ]);
      setAllVendors(vendorsData.vendors || []);
      setCategories(categoriesData.categories || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };
  
  // Group vendors by category
  const vendorsByCategory = {};
  vendors.forEach((v) => {
    if (!vendorsByCategory[v.category_name]) {
      vendorsByCategory[v.category_name] = [];
    }
    vendorsByCategory[v.category_name].push(v);
  });
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">Vendor Assignments</h3>
        {(user?.role === 'admin' || user?.role === 'client') && (
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Assign Vendor
          </button>
        )}
      </div>
      
      {Object.keys(vendorsByCategory).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(vendorsByCategory).map(([category, categoryVendors]) => (
            <div key={category} className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-medium text-slate-700 mb-3">{category}</h4>
              <div className="space-y-2">
                {categoryVendors.map((v) => (
                  <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{v.company_name}</p>
                        <div className="flex gap-2 text-xs">
                          {v.can_view && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">View</span>}
                          {v.can_edit && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">Edit</span>}
                          {v.can_upload && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Upload</span>}
                          {v.can_delete && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">Delete</span>}
                        </div>
                      </div>
                    </div>
                    {(user?.role === 'admin' || user?.role === 'client') && (
                      <button className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">No vendors assigned yet</p>
      )}
      
      {showAssignModal && (
        <AssignVendorModal
          clientId={clientId}
          vendors={allVendors}
          categories={categories}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            setShowAssignModal(false);
            onUpdate();
          }}
        />
      )}
    </div>
  );
}

// ============================================
// Client Activity Tab
// ============================================
function ClientActivity({ clientId }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadActivity();
  }, [clientId]);
  
  const loadActivity = async () => {
    try {
      const data = await api.get(`/clients/${clientId}/activity`);
      setActivity(data.activity || []);
    } catch (error) {
      console.error('Failed to load activity:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return <div className="text-center py-8">Loading activity...</div>;
  
  return (
    <div className="space-y-3">
      {activity.map((item, i) => (
        <div key={i} className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-lg">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-slate-500" />
          </div>
          <div className="flex-1">
            <p className="text-slate-700">
              <span className="font-medium">{item.user_name || 'System'}</span>
              {' '}{item.action?.replace(/_/g, ' ')}
            </p>
            <p className="text-sm text-slate-500">{new Date(item.created_at).toLocaleString()}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Shared Components
// ============================================
function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600'
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-500 text-sm">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function AlertItem({ alert }) {
  const urgencyColors = {
    low: 'bg-green-50 border-green-200 text-green-700',
    medium: 'bg-amber-50 border-amber-200 text-amber-700',
    high: 'bg-orange-50 border-orange-200 text-orange-700',
    critical: 'bg-red-50 border-red-200 text-red-700'
  };
  
  return (
    <div className={`p-4 rounded-lg border ${urgencyColors[alert.urgency]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">{alert.message}</p>
          <p className="text-sm opacity-70">Policy: {alert.policy_number}</p>
        </div>
        {alert.date && (
          <span className="text-sm">{new Date(alert.date).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );
}

function PolicyTable({ policies }) {
  const navigate = useNavigate();

  if (policies.length === 0) {
    return <p className="text-slate-500 text-center py-8">No policies found</p>;
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Policy #</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Interest / Subject Matter</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Category</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Insurer</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Annual Premium (Policy Currency)</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Annual Premium (USD)</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Pro-rata Premium (Policy Currency)</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Pro-rata Premium (USD)</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Renewal</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Status</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {policies.map((policy) => (
            <tr key={policy.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setPolicyDetail({ open: true, policy })}>
              <td className="px-4 py-3 font-medium text-slate-800">{policy.policy_number}</td>
              <td className="px-4 py-3 text-slate-600">{policy.interest_subject_matter || '-'}</td>
              <td className="px-4 py-3 text-slate-600">{policy.category_name}</td>
              <td className="px-4 py-3 text-slate-600">{policy.insurer || '-'}</td>
              <td className="px-4 py-3 text-slate-600">{policy.currency || 'USD'} {parseFloat(policy.annual_premium || 0).toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-600">$ {parseFloat(policy.annual_premium_usd || 0).toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-600">{policy.currency || 'USD'} {parseFloat(policy.prorata_premium || 0).toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-600">$ {parseFloat(policy.prorata_premium_usd || 0).toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-600">
                {policy.renewal_date ? new Date(policy.renewal_date).toLocaleDateString() : '-'}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  policy.status === 'active' ? 'bg-green-100 text-green-700' :
                  policy.status === 'expired' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {policy.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/policies/${policy.id}`); }} className="text-blue-600 hover:text-blue-800">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); }} className="text-red-600 hover:text-red-800">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Modals
// ============================================
function CreateUserModal({ type, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    google_sheet_id: '',
    google_sheet_name: 'Policies',
    role: type
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await api.post('/admin/users', formData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800">Add New {type === 'client' ? 'Client' : 'Vendor'}</h3>
          <button onClick={onClose} className="text-[#888888] hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">{error}</div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password (optional)</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              placeholder="Auto-generated if empty"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
            />
          </div>



          {type === 'client' && (

            <>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Google Sheet ID</label>

                <input

                  type="text"

                  value={formData.google_sheet_id}

                  onChange={(e) => setFormData({ ...formData, google_sheet_id: e.target.value })}

                  placeholder="e.g., 1y0YEOaZ8QREb7gO9o3BuGsEPHN1kPQ6w8Ij26T05J1A"

                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

                />

                <p className="text-xs text-slate-500 mt-1">From the Google Sheet URL</p>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Sheet Tab Name</label>

                <input

                  type="text"

                  value={formData.google_sheet_name}

                  onChange={(e) => setFormData({ ...formData, google_sheet_name: e.target.value })}

                  placeholder="Policies"

                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"

                />

                <p className="text-xs text-slate-500 mt-1">Tab name (default: Policies)</p>

              </div>

            </>

          )}


          
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignVendorModal({ clientId, vendors, categories, onClose, onSuccess }) {



// ============================================

// Vendor Users Modal - Manage multiple users per vendor

// ============================================

function VendorUsersModal({ vendor, onClose, onUpdate }) {

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [showAddForm, setShowAddForm] = useState(false);

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });



  useEffect(() => {

    loadUsers();

  }, [vendor.id]);



  const loadUsers = async () => {

    try {

      setLoading(true);

      const data = await api.get(`/admin/vendors/${vendor.id}/users`);

      setUsers(data.users || []);

    } catch (err) {

      console.error('Failed to load users:', err);

    } finally {

      setLoading(false);

    }

  };



  const handleAddUser = async (e) => {

    e.preventDefault();

    if (!formData.name || !formData.email) {

      alert('Name and email are required');

      return;

    }

    try {

      setSaving(true);

      await api.post(`/admin/vendors/${vendor.id}/users`, formData);

      setFormData({ name: '', email: '', password: '', phone: '' });

      setShowAddForm(false);

      loadUsers();

      if (onUpdate) onUpdate();

    } catch (err) {

      alert(err.message || 'Failed to add user');

    } finally {

      setSaving(false);

    }

  };



  const handleDeleteUser = async (userId) => {

    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {

      await api.delete(`/admin/vendors/${vendor.id}/users/${userId}`);

      loadUsers();

      if (onUpdate) onUpdate();

    } catch (err) {

      alert(err.message || 'Failed to delete user');

    }

  };



  return (

    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">

        <div className="flex items-center justify-between mb-6">

          <div>

            <h3 className="text-xl font-bold text-slate-800">Manage Users</h3>

            <p className="text-sm text-slate-500">{vendor.company_name}</p>

          </div>

          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">

            <X className="w-6 h-6" />

          </button>

        </div>



        {loading ? (

          <div className="text-center py-8 text-slate-500">Loading users...</div>

        ) : (

          <>

            <div className="space-y-3 mb-6">

              {users.length === 0 ? (

                <p className="text-center text-slate-500 py-4">No users yet. Add the first user below.</p>

              ) : (

                users.map(user => (

                  <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">

                    <div>

                      <p className="font-medium text-slate-800">{user.name}</p>

                      <p className="text-sm text-slate-500">{user.email}</p>

                      {user.phone && <p className="text-xs text-slate-400">{user.phone}</p>}

                    </div>

                    <div className="flex items-center gap-2">

                      <span className={`px-2 py-1 rounded-full text-xs ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>

                        {user.is_active ? 'Active' : 'Inactive'}

                      </span>

                      <button onClick={() => handleDeleteUser(user.id)} className="p-1 text-red-500 hover:bg-red-100 rounded" title="Delete user">

                        <Trash2 className="w-4 h-4" />

                      </button>

                    </div>

                  </div>

                ))

              )}

            </div>



            {showAddForm ? (

              <form onSubmit={handleAddUser} className="border-t pt-4 space-y-3">

                <h4 className="font-medium text-slate-700">Add New User</h4>

                <input type="text" placeholder="Full Name *" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" required />

                <input type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" required />

                <input type="password" placeholder="Password (auto-generated if empty)" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" />

                <input type="text" placeholder="Phone (optional)" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" />

                <div className="flex gap-2">

                  <button type="button" onClick={() => setShowAddForm(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-slate-50">Cancel</button>

                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-[#A34A1E] disabled:opacity-50">{saving ? 'Adding...' : 'Add User'}</button>

                </div>

              </form>

            ) : (

              <button onClick={() => setShowAddForm(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-[#C45C26] hover:text-[#C45C26]">

                <Plus className="w-5 h-5" /> Add User

              </button>

            )}

          </>

        )}



        <div className="mt-6 pt-4 border-t">

          <button onClick={onClose} className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Close</button>

        </div>

      </div>

    </div>

  );

}



  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [permissions, setPermissions] = useState({
    can_view: true,
    can_edit: true,
    can_upload: true,
    can_delete: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await api.post(`/clients/${clientId}/vendors`, {
        vendor_id: selectedVendor,
        category_id: selectedCategory,
        ...permissions
      });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800">Assign Vendor</h3>
          <button onClick={onClose} className="text-[#888888] hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">{error}</div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vendor</label>
            <select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              required
            >
              <option value="">Select Vendor</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.company_name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Policy Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
              required
            >
              <option value="">Select Category</option>
              {categories.map(c => (
                <option key={c.id} value={c.id} style={{ fontWeight: !c.parent_id ? 'bold' : 'normal' }}>{c.parent_id ? '    ' + c.name : '▸ ' + c.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Permissions</label>
            <div className="space-y-2">
              {Object.entries({ can_view: 'View', can_edit: 'Edit', can_upload: 'Upload', can_delete: 'Delete' }).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={permissions[key]}
                    onChange={(e) => setPermissions({ ...permissions, [key]: e.target.checked })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-slate-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// Placeholder Pages
// ============================================
function VendorsList() {

  const [vendors, setVendors] = useState([]);

  const [clients, setClients] = useState([]);

  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showUsersModal, setShowUsersModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);

  const [selectedVendor, setSelectedVendor] = useState(null);

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({

    company_name: "", contact_person: "", contact_email: "", contact_phone: "", 

    address: "", license_number: "", logo_url: "", is_active: true

  });

  const [assignData, setAssignData] = useState({ client_id: "", category_ids: [], can_view: true, can_edit: true, can_upload: true, can_delete: false });



  useEffect(() => { 

    loadVendors(); 

    loadClients();

    loadCategories();

  }, []);



  const loadVendors = async () => {

    try {

      setLoading(true);

      const data = await api.get("/admin/vendors");

      setVendors(data.vendors || []);

    } catch (err) { console.error(err); }

    finally { setLoading(false); }

  };



  const loadClients = async () => {

    try {

      const data = await api.get("/admin/clients");

      setClients(data.clients || []);

    } catch (err) { console.error(err); }

  };



  const loadCategories = async () => {

    try {

      const data = await api.get("/policies/categories");

      setCategories(data.categories || []);

    } catch (err) { console.error(err); }

  };



  const filteredVendors = vendors.filter(v => 

    !searchTerm || 

    v.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||

    v.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||

    v.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())

  );



  const resetForm = () => {

    setFormData({ company_name: "", contact_person: "", contact_email: "", contact_phone: "", address: "", license_number: "", logo_url: "", is_active: true });

    setSelectedVendor(null);

  };



  const handleSave = async () => {

    if (!formData.company_name) { alert("Company name is required"); return; }

    try {

      setSaving(true);

      if (selectedVendor) {

        await api.put("/admin/vendors/" + selectedVendor.id, formData);

      } else {

        const result = await api.post("/admin/vendors", formData);

        if (result.temp_password) {

          alert("Vendor created! Temporary password: " + result.temp_password);

        }

      }

      setShowModal(false);

      resetForm();

      loadVendors();

    } catch (err) { alert(err.message || "Failed to save vendor"); }

    finally { setSaving(false); }

  };



  const handleEdit = (vendor) => {

    setSelectedVendor(vendor);

    setFormData({

      company_name: vendor.company_name || "",

      contact_person: vendor.contact_person || "",

      contact_email: vendor.contact_email || "",

      contact_phone: vendor.contact_phone || "",

      address: vendor.address || "",

      license_number: vendor.license_number || "",

      logo_url: vendor.logo_url || "",

      is_active: vendor.is_active !== false

    });

    setShowModal(true);

  };



  const handleDelete = async (id) => {

    if (!window.confirm("Are you sure you want to delete this vendor?")) return;

    try {

      await api.delete("/admin/vendors/" + id);

      loadVendors();

    } catch (err) { alert(err.message || "Failed to delete vendor"); }

  };



  const openAssignModal = (vendor) => {

    setSelectedVendor(vendor);

    setAssignData({ client_id: "", category_ids: [], can_view: true, can_edit: true, can_upload: true, can_delete: false });

    setShowAssignModal(true);

  };



  const handleAssign = async () => {

    if (!assignData.client_id || assignData.category_ids.length === 0) { alert("Please select client and at least one category"); return; }

    try {

      setSaving(true);

      // Assign each selected category

      for (const category_id of assignData.category_ids) {

        await api.post("/admin/vendors/" + selectedVendor.id + "/assign", { ...assignData, category_id });

      }

      setShowAssignModal(false);

      loadVendors();

      alert("Client assigned successfully to " + assignData.category_ids.length + " categories");

    } catch (err) { alert(err.message || "Failed to assign client"); }

    finally { setSaving(false); }

  };



  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-600" /></div>;



  return (

    <div className="space-y-6">

      <div className="flex justify-between items-center">

        <div>

          <h1 className="text-2xl font-bold text-slate-800">Vendors</h1>

          <p className="text-slate-500">Manage insurance brokers and their client assignments</p>

        </div>

        <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700">

          <Plus className="w-5 h-5" /> Add Vendor

        </button>

      </div>



      <div className="bg-white rounded-xl shadow-sm border p-4">

        <div className="relative">

          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" />

          <input type="text" placeholder="Search vendors by name, contact, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26] focus:border-blue-500" />

        </div>

      </div>



      {filteredVendors.length === 0 ? (

        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">

          <Building2 className="w-16 h-16 text-[#aaaaaa] mx-auto mb-4" />

          <h3 className="text-lg font-medium text-slate-600 mb-2">No vendors found</h3>

          <p className="text-slate-500 mb-4">Get started by adding your first vendor</p>

          <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700">

            <Plus className="w-5 h-5" /> Add Vendor

          </button>

        </div>

      ) : (

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {filteredVendors.map(vendor => (

            <div key={vendor.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">

              <div className="p-6">

                <div className="flex items-start justify-between mb-4">

                  <div className="flex items-center gap-3">

                    {vendor.logo_url ? (

                      <img src={vendor.logo_url} alt={vendor.company_name} className="w-12 h-12 rounded-xl object-cover" />

                    ) : (

                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">

                        <Building2 className="w-6 h-6 text-white" />

                      </div>

                    )}

                    <div>

                      <h3 className="font-semibold text-slate-800">{vendor.company_name}</h3>

                      <p className="text-sm text-slate-500">{vendor.contact_person || "No contact person"}</p>

                    </div>

                  </div>

                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${vendor.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>

                    {vendor.is_active ? "Active" : "Inactive"}

                  </span>

                </div>



                <div className="space-y-2 mb-4">

                  {vendor.contact_email && (

                    <div className="flex items-center gap-2 text-sm text-slate-600">

                      <Mail className="w-4 h-4 text-[#888888]" />

                      <span>{vendor.contact_email}</span>

                    </div>

                  )}

                  {vendor.contact_phone && (

                    <div className="flex items-center gap-2 text-sm text-slate-600">

                      <User className="w-4 h-4 text-[#888888]" />

                      <span>{vendor.contact_phone}</span>

                    </div>

                  )}

                  {vendor.license_number && (

                    <div className="flex items-center gap-2 text-sm text-slate-600">

                      <FileText className="w-4 h-4 text-[#888888]" />

                      <span>License: {vendor.license_number}</span>

                    </div>

                  )}

                </div>



                <div className="py-3 border-t border-slate-100">

                  <p className="text-xs font-medium text-slate-500 mb-2">{vendor.assignment_count || 0} assignment(s)</p>

                  {vendor.assignments && vendor.assignments.length > 0 ? (

                    <div className="space-y-1 max-h-24 overflow-y-auto">

                      {vendor.assignments.map((a, idx) => (

                        <div key={idx} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1 rounded">

                          <span className="font-medium text-slate-700">{a.client_name}</span>

                          <span className="text-slate-500">{a.category_name}</span>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <p className="text-xs text-[#888888]">No clients assigned</p>

                  )}

                </div>



                <div className="flex gap-2 pt-3 border-t border-slate-100">

                  <button onClick={() => { setSelectedVendor(vendor); setShowUsersModal(true); }} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg" title="Manage Users"><Users className="w-4 h-4" /></button>
                  <button onClick={() => openAssignModal(vendor)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg">

                    <Users className="w-4 h-4" /> Assign

                  </button>

                  <button onClick={() => handleEdit(vendor)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">

                    <Edit className="w-4 h-4" /> Edit

                  </button>

                  <button onClick={() => handleDelete(vendor.id)} className="flex items-center justify-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">

                    <Trash2 className="w-4 h-4" />

                  </button>

                </div>

              </div>

            </div>

          ))}

        </div>

      )}



      {/* Add/Edit Vendor Modal */}

      {showModal && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="p-6 border-b">

              <h2 className="text-xl font-bold text-slate-800">{selectedVendor ? "Edit Vendor" : "Add New Vendor"}</h2>

            </div>

            <div className="p-6 space-y-4">

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>

                <input type="text" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="ABC Insurance Brokers" />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>

                <input type="text" value={formData.contact_person} onChange={e => setFormData({...formData, contact_person: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="John Smith" />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>

                <input type="email" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="john@example.com" />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>

                <input type="text" value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="+971 50 123 4567" />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">License Number</label>

                <input type="text" value={formData.license_number} onChange={e => setFormData({...formData, license_number: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="BRK-2024-001" />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Logo</label>

                <input type="file" accept="image/*" onChange={async (e) => {

                  const file = e.target.files[0];

                  if (file && selectedVendor) {

                    const formDataUpload = new FormData();

                    formDataUpload.append('logo', file);

                    try {

                      const response = await fetch('/api/admin/vendors/' + selectedVendor.id + '/logo', {

                        method: 'POST',

                        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },

                        body: formDataUpload

                      });

                      const result = await response.json();

                      if (result.success) {

                        setFormData({...formData, logo_url: result.logo_url});

                      } else {

                        alert(result.message || 'Upload failed');

                      }

                    } catch (err) { alert('Upload failed'); }

                  } else if (file && !selectedVendor) {

                    alert('Please save the vendor first, then edit to add logo');

                  }

                }} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" />

                {formData.logo_url && <img src={formData.logo_url} alt="Logo preview" className="mt-2 w-16 h-16 rounded-lg object-cover border" />}

                {!selectedVendor && <p className="text-xs text-slate-500 mt-1">Save vendor first to upload logo</p>}

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>

                <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} rows={2} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="Office address..." />

              </div>

              <div className="flex items-center gap-2">

                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" />

                <label htmlFor="is_active" className="text-sm text-slate-700">Active</label>

              </div>

            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">

              <button onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">Cancel</button>

              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">

                {saving ? "Saving..." : (selectedVendor ? "Update Vendor" : "Create Vendor")}

              </button>

            </div>

          </div>

        </div>

      )}



      {/* Assign Client Modal */}

      {showAssignModal && selectedVendor && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">

            <div className="p-6 border-b">

              <h2 className="text-xl font-bold text-slate-800">Assign Client to {selectedVendor.company_name}</h2>

            </div>

            <div className="p-6 space-y-4">

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>

                <select value={assignData.client_id} onChange={e => setAssignData({...assignData, client_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]">

                  <option value="">Select a client...</option>

                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}

                </select>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Categories * (select multiple)</label>

                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">

                  {categories.map(c => (

                    <label key={c.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">

                      <input 

                        type="checkbox" 

                        checked={assignData.category_ids.includes(c.id)} 

                        onChange={e => {

                          if (e.target.checked) {

                            setAssignData({...assignData, category_ids: [...assignData.category_ids, c.id]});

                          } else {

                            setAssignData({...assignData, category_ids: assignData.category_ids.filter(id => id !== c.id)});

                          }

                        }} 

                        className="w-4 h-4 text-blue-600 rounded" 

                      />

                      <span className="text-sm">{c.name}</span>

                    </label>

                  ))}

                </div>

                <p className="text-xs text-slate-500 mt-1">{assignData.category_ids.length} selected</p>

              </div>

              <div className="space-y-2">

                <label className="block text-sm font-medium text-slate-700">Permissions</label>

                <div className="grid grid-cols-2 gap-2">

                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assignData.can_view} onChange={e => setAssignData({...assignData, can_view: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" /> Can View</label>

                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assignData.can_edit} onChange={e => setAssignData({...assignData, can_edit: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" /> Can Edit</label>

                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assignData.can_upload} onChange={e => setAssignData({...assignData, can_upload: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" /> Can Upload</label>

                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assignData.can_delete} onChange={e => setAssignData({...assignData, can_delete: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" /> Can Delete</label>

                </div>

              </div>

            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">

              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">Cancel</button>

              <button onClick={handleAssign} disabled={saving} className="px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">

                {saving ? "Assigning..." : "Assign Client"}

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );

}



function PolicyDetail() {

  const { id } = useParams();

  const navigate = useNavigate();

  const { user } = useAuth();

  const [policy, setPolicy] = useState(null);

  const [documents, setDocuments] = useState([]);

  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);

  const [formData, setFormData] = useState({});



  useEffect(() => {

    loadPolicy();

  }, [id]);



  const loadPolicy = async () => {

    try {

      const data = await api.get(`/policies/${id}`);

      setPolicy(data.policy);

      setDocuments(data.documents || []);

      setFormData(data.policy);

    } catch (error) {

      console.error('Failed to load policy:', error);

    } finally {

      setLoading(false);

    }

  };



  const handleSave = async () => {

    try {

      await api.put("/policies/" + id, formData);

      setPolicy(formData);

      setEditing(false);

    } catch (error) {

      alert('Failed to update policy');

    }

  };



  const formatCurrency = (val) => val ? `$${parseFloat(val).toLocaleString()}` : 'N/A';

  const formatDate = (val) => val ? new Date(val).toLocaleDateString() : 'N/A';





  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;

  if (!policy) return <div className="p-6">Policy not found</div>;



  const DetailField = ({ label, value, field, type = 'text', isTextarea = false }) => (

    <div className="py-3 border-b border-slate-100">

      <label className="text-sm text-slate-500 block mb-1">{label}</label>

      {editing ? (

        isTextarea ? (

          <textarea

            value={formData[field] || ''}

            onChange={(e) => setFormData({...formData, [field]: e.target.value})}

            className="w-full border border-slate-300 rounded px-3 py-2 text-slate-800"

            rows="3"

          />

        ) : (

          <input

            type={type}

            value={formData[field] || ''}

            onChange={(e) => setFormData({...formData, [field]: e.target.value})}

            className="w-full border border-slate-300 rounded px-3 py-2 text-slate-800"

          />

        )

      ) : (

        <p className="text-slate-800 font-medium">{value || 'N/A'}</p>

      )}

    </div>

  );



  return (

    <div className="space-y-6">

      <div className="flex items-center justify-between">

        <div className="flex items-center gap-4">

          <button onClick={() => navigate('/policies')} className="text-slate-500 hover:text-slate-700">

            <ChevronLeft className="w-6 h-6" />

          </button>

          <h1 className="text-2xl font-bold text-slate-800">Policy: {policy.policy_number}</h1>

          <span className={`px-3 py-1 rounded-full text-sm font-medium ${

            policy.status === 'active' ? 'bg-green-100 text-green-800' :

            policy.status === 'expired' ? 'bg-red-100 text-red-800' :

            'bg-yellow-100 text-yellow-800'

          }`}>{policy.status}</span>

        </div>

        <div className="flex gap-2">

          {editing ? (

            <>

              <button onClick={() => setEditing(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>

              <button onClick={handleSave} className="px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700">Save</button>

            </>

          ) : (

            <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700">

              <Edit className="w-4 h-4" /> Edit Policy

            </button>

          )}

        </div>

      </div>



      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Main Details */}

        <div className="lg:col-span-2 space-y-6">

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Policy Information</h3>

            <div className="grid grid-cols-2 gap-x-6">

              <DetailField label="Policy Number" value={policy.policy_number} field="policy_number" />

              <DetailField label="Interest / Subject Matter Insured" value={policy.interest_subject_matter} field="interest_subject_matter" />

              <DetailField label="Category" value={policy.category_name} field="category_name" />

              <DetailField label="Client" value={policy.client_name} field="client_name" />

              <DetailField label="Insurer" value={policy.insurer} field="insurer" />

              <DetailField label="Status" value={policy.status} field="status" />

              <DetailField label="Sum Insured" value={formatCurrency(policy.sum_insured)} field="sum_insured" type="number" />

            </div>

          </div>



          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Premium & Installments</h3>

            <div className="grid grid-cols-2 gap-x-6">

              <DetailField label="Annual Premium" value={formatCurrency(policy.annual_premium)} field="annual_premium" type="number" />

              <DetailField label="Number of Installments" value={policy.num_installments} field="num_installments" type="number" />

              <DetailField label="Installment Premium" value={formatCurrency(policy.installment_premium)} field="installment_premium" type="number" />

              <DetailField label="1st Installment Due Date" value={formatDate(policy.installment_1_due_date)} field="installment_1_due_date" type="date" />

              <DetailField label="2nd Installment Due Date" value={formatDate(policy.installment_2_due_date)} field="installment_2_due_date" type="date" />

              <DetailField label="3rd Installment Due Date" value={formatDate(policy.installment_3_due_date)} field="installment_3_due_date" type="date" />

              <DetailField label="4th Installment Due Date" value={formatDate(policy.installment_4_due_date)} field="installment_4_due_date" type="date" />

            </div>

          </div>

          {/* Installment Status */}

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Installment Status</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

              <div>

                <p className="text-sm text-slate-500 mb-1">1st Installment</p>

                <StatusBadge status={policy.installment_1_status} type="installment" />

              </div>

              <div>

                <p className="text-sm text-slate-500 mb-1">2nd Installment</p>

                <StatusBadge status={policy.installment_2_status} type="installment" />

              </div>

              <div>

                <p className="text-sm text-slate-500 mb-1">3rd Installment</p>

                <StatusBadge status={policy.installment_3_status} type="installment" />

              </div>

              <div>

                <p className="text-sm text-slate-500 mb-1">4th Installment</p>

                <StatusBadge status={policy.installment_4_status} type="installment" />

              </div>

            </div>

          </div>



          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Dates</h3>

            <div className="grid grid-cols-2 gap-x-6">

              <DetailField label="Start Date" value={formatDate(policy.start_date)} field="start_date" type="date" />

              <DetailField label="Renewal/Expiry Date" value={formatDate(policy.renewal_date)} field="renewal_date" type="date" />

              <DetailField label="Survey Deadline" value={formatDate(policy.survey_deadline)} field="survey_deadline" type="date" />

              <div>

                <p className="text-sm text-slate-500 mb-1">Survey Status</p>

                <StatusBadge status={policy.survey_status} type="survey" />

              </div>

              <div>

                <p className="text-sm text-slate-500 mb-1">Survey Compliance</p>

                <StatusBadge status={policy.survey_compliance_status} type="compliance" />

              </div>

            </div>

          </div>



          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Coverage Details</h3>

            <DetailField label="Subject Matter Insured" value={policy.subject_matter_insured} field="subject_matter_insured" isTextarea />

            <DetailField label="Assured(s)" value={policy.assureds} field="assureds" isTextarea />

            <DetailField label="Reinsured" value={policy.reinsured} field="reinsured" isTextarea />

            <DetailField label="Reinsurers" value={policy.reinsurers} field="reinsurers" isTextarea />

            <DetailField label="Risk Summary" value={policy.risk_summary} field="risk_summary" isTextarea />

          </div>



          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Terms & Conditions</h3>

            <DetailField label="Warranties" value={policy.warranties} field="warranties" isTextarea />

            <DetailField label="Subjectivities" value={policy.subjectivities} field="subjectivities" isTextarea />

            <DetailField label="Exclusions" value={policy.exclusions} field="exclusions" isTextarea />

          </div>

        </div>



        {/* Sidebar - Documents */}

        <div className="space-y-6">

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <div className="flex items-center justify-between mb-4">

              <h3 className="text-lg font-semibold text-slate-800">Documents</h3>

              <label className="text-blue-600 hover:text-blue-700 text-sm font-medium cursor-pointer">

                + Upload

                <input

                  type="file"

                  className="hidden"

                  onChange={async (e) => {

                    const file = e.target.files[0];

                    if (!file) return;

                    try {

                      const formData = new FormData();

                      formData.append('file', file);

                      formData.append('policy_id', id);

                      formData.append('client_id', policy.client_id);

                      formData.append('category_id', policy.category_id);

                      await api.upload('/documents/upload', formData);

                      loadPolicy();

                    } catch (err) {

                      console.error('Upload failed:', err);

                      alert('Failed to upload document');

                    }

                    e.target.value = '';

                  }}

                />

              </label>

            </div>

            {documents.length > 0 ? (

              <div className="space-y-2">

                {documents.map(doc => (

                  <div key={doc.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded">

                    <FileText className="w-5 h-5 text-[#888888]" />

                    <div className="flex-1">

                      <p className="text-sm font-medium text-slate-700">{doc.original_name}</p>

                      <p className="text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleDateString()}</p>

                    </div>

                    <button 

                      onClick={async () => {

                        const newName = prompt('Rename document:', doc.original_name);

                        if (newName && newName !== doc.original_name) {

                          try {

                            await api.put(`/documents/${doc.id}/rename`, { name: newName });

                            loadPolicy();

                          } catch (err) {

                            alert('Failed to rename');

                          }

                        }

                      }}

                      className="text-[#888888] hover:text-slate-600 mr-2"

                      title="Rename"

                    >

                      <Edit className="w-4 h-4" />

                    </button>

                    <button 

                      onClick={async () => {

                        try {

                          const response = await api.get(`/documents/${doc.id}/download`);

                          window.open(response.download_url, '_blank');

                        } catch (err) {

                          alert('Failed to download');

                        }

                      }}

                      className="text-blue-600 hover:text-blue-700"

                      title="Download"

                    >

                      <Download className="w-4 h-4" />

                    </button>

                  </div>

                ))}

              </div>

            ) : (

              <p className="text-slate-500 text-sm">No documents uploaded</p>

            )}

          </div>



          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            <h3 className="text-lg font-semibold text-slate-800 mb-4">Activity</h3>

            <div className="text-sm text-slate-500">

              <p>Created: {formatDate(policy.created_at)}</p>

              <p>Last Updated: {formatDate(policy.updated_at)}</p>

              {policy.last_synced_at && <p>Last Synced: {formatDate(policy.last_synced_at)}</p>}

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}



function PoliciesList() {

  const navigate = useNavigate();

  const { user } = useAuth();

  const [policies, setPolicies] = useState([]);

  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);

  const [editingPolicy, setEditingPolicy] = useState(null);

  const [policyDetail, setPolicyDetail] = useState({ open: false, policy: null });

  const [filter, setFilter] = useState({ category: '', status: '' });

  const [formData, setFormData] = useState({

    client_id: '',

    policy_number: '',

    interest_subject_matter: '',

    category_id: '',

    insurer: '',

    sum_insured: '',

    annual_premium: '',

    start_date: '',

    expiry_date: '',

    status: 'active',

    notes: ''

  });

  const [clients, setClients] = useState([]);

  useEffect(() => {

    loadData();

  }, []);

  const loadData = async () => {

    try {

      const [policiesRes, categoriesRes, clientsRes] = await Promise.all([

        api.get('/policies'),

        api.get('/policies/categories'),

        user?.role === 'admin' ? api.get('/admin/clients') : Promise.resolve({ clients: [] })

      ]);

      setPolicies(policiesRes.policies || []);

      setCategories(categoriesRes.categories || []);

      setClients(clientsRes.clients || []);

    } catch (error) {

      console.error('Failed to load policies:', error);

    } finally {

      setLoading(false);

    }

  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    try {

      if (editingPolicy) {

        await api.put("/policies/" + editingPolicy.id, formData);

      } else {

        await api.post('/policies', formData);

      }

      setShowModal(false);

      setEditingPolicy(null);

      setFormData({

        client_id: '', policy_number: '', policy_year: '', category_id: ',', insurer: '', sum_insured: '',

        annual_premium: '', start_date: '', expiry_date: '', status: 'active', notes: ''

      });

      loadData();

    } catch (error) {

      alert(error.message);

    }

  };

  const handleEdit = (policy) => {

    setEditingPolicy(policy);

    setFormData({

      client_id: policy.client_id || '',

      policy_number: policy.policy_number || '',

      category_id: policy.category_id || '',

      insurer: policy.insurer || '',

      sum_insured: policy.sum_insured || '',

      annual_premium: policy.annual_premium || '',

      start_date: policy.start_date?.split('T')[0] || '',

      expiry_date: policy.expiry_date?.split('T')[0] || '',

      status: policy.status || 'active',

      notes: policy.notes || ''

    });

    setShowModal(true);

  };

  const handleDelete = async (id) => {

    if (!window.confirm('Are you sure you want to delete this policy?')) return;

    try {

      await api.delete(`/policies/${id}`);

      loadData();

    } catch (error) {

      alert(error.message);

    }

  };

  const filteredPolicies = policies.filter(p => {

    if (filter.client && p.client_id !== parseInt(filter.client)) return false;
    if (filter.category && p.category_id !== parseInt(filter.category)) return false;

    if (filter.status && p.status !== filter.status) return false;

    return true;

  });

  const getStatusColor = (status) => {

    switch (status) {

      case 'active': return 'bg-green-100 text-green-800';

      case 'expired': return 'bg-red-100 text-red-800';

      case 'pending': return 'bg-yellow-100 text-yellow-800';

      default: return 'bg-gray-100 text-gray-800';

    }

  };

  const mainCategories = categories.filter(c => !c.parent_id);

  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);



  const handleAIChatSubmit = async () => {

    if (!aiSummary.chatInput?.trim()) return;

    

    const userMessage = aiSummary.chatInput.trim();

    const newMessages = [...(aiSummary.chatMessages || []), { role: 'user', content: userMessage }];

    

    setAiSummary({

      ...aiSummary,

      chatMessages: newMessages,

      chatInput: '',

      chatLoading: true

    });

    

    try {

      const response = await api.post('/ai/chat', {

        question: userMessage,

        context: aiSummary.content,

        policyId: aiSummary.policyId,

        clientId: user.client?.id || user.clientId,

        chatHistory: newMessages.slice(-6) // Last 3 exchanges

      });

      

      const aiResponse = response.answer || response.response || 'Sorry, I could not generate a response.';

      

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: aiResponse }],

        chatLoading: false

      }));

    } catch (error) {

      setAiSummary(prev => ({

        ...prev,

        chatMessages: [...prev.chatMessages, { role: 'assistant', content: 'Error: ' + error.message }],

        chatLoading: false

      }));

    }

  };



  if (loading) return <LoadingScreen />;

  return (

    <div className="space-y-6">

      <div className="flex items-center justify-between">

        <h1 className="text-2xl font-bold text-slate-800">Policies</h1>

        {(user?.role === 'admin' || user?.role === 'client') && (

          <button

            onClick={() => { setEditingPolicy(null); setShowModal(true); }}

            className="flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700"

          >

            <Plus className="w-4 h-4" /> Add Policy

          </button>

        )}

      </div>

      {/* Filters */}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <select
            value={filter.client}
            onChange={(e) => setFilter({ ...filter, client: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2"
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.company_name}</option>
            ))}
          </select>

        <div className="flex gap-4">

          <select

            value={filter.category}

            onChange={(e) => setFilter({ ...filter, category: e.target.value })}

            className="border border-slate-300 rounded-lg px-3 py-2"

          >

            <option value="">All Categories</option>

            {mainCategories.map(cat => (

              <optgroup key={cat.id} label={cat.name}>

                <option value={cat.id}>{cat.name} (All)</option>

                {getSubcategories(cat.id).map(sub => (

                  <option key={sub.id} value={sub.id}>{sub.name}</option>

                ))}

              </optgroup>

            ))}

          </select>

          <select

            value={filter.status}

            onChange={(e) => setFilter({ ...filter, status: e.target.value })}

            className="border border-slate-300 rounded-lg px-3 py-2"

          >

            <option value="">All Status</option>

            <option value="active">Active</option>

            <option value="pending">Pending</option>

            <option value="expired">Expired</option>

          </select>

        </div>

      </div>

      {/* Policies Table */}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        <table className="w-full">

          <thead className="bg-slate-50 border-b border-slate-200">

            <tr>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Policy #</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Client</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Category</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Interest / Subject Matter</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Insurer</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Sum Insured</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Annual Premium (Policy Currency)</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Annual Premium (USD)</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Pro-rata Premium (Policy Currency)</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Pro-rata Premium (USD)</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Expiry</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Status</th>

              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Actions</th>

            </tr>

          </thead>

          <tbody className="divide-y divide-slate-200">

            {filteredPolicies.length === 0 ? (

              <tr>

                <td colSpan="9" className="px-6 py-8 text-center text-slate-500">

                  No policies found

                </td>

              </tr>

            ) : (

              filteredPolicies.map(policy => (
                <tr key={policy.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setPolicyDetail({ open: true, policy })}>
                  <td className="px-6 py-4 font-medium text-slate-800">{policy.policy_number}</td>
                  <td className="px-6 py-4 text-slate-600">{policy.client_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-slate-600">{policy.category_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-slate-600">{policy.interest_subject_matter || '-'}</td>
                  <td className="px-6 py-4 text-slate-600">{policy.insurer || 'N/A'}</td>

                  <td className="px-6 py-4 text-slate-600">${parseFloat(policy.sum_insured || 0).toLocaleString()}</td>

                  <td className="px-6 py-4 text-slate-600">{policy.currency || 'USD'} {Math.round(parseFloat(policy.annual_premium) || 0).toLocaleString()}</td>

                  <td className="px-6 py-4 text-slate-600">$ {Math.round(parseFloat(policy.annual_premium_usd) || 0).toLocaleString()}</td>

                  <td className="px-6 py-4 text-slate-600">{policy.currency || 'USD'} {Math.round(parseFloat(policy.prorata_premium) || 0).toLocaleString()}</td>

                  <td className="px-6 py-4 text-slate-600">$ {Math.round(parseFloat(policy.prorata_premium_usd) || 0).toLocaleString()}</td>

                  <td className="px-6 py-4 text-slate-600">

                    {policy.expiry_date ? new Date(policy.expiry_date).toLocaleDateString() : 'N/A'}

                  </td>

                  <td className="px-6 py-4">

                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(policy.status)}`}>

                      {policy.status}

                    </span>

                  </td>

                  <td className="px-6 py-4">

                    <div className="flex gap-2">

                      <button onClick={() => handleEdit(policy)} className="text-blue-600 hover:text-blue-800">

                        <Edit className="w-4 h-4" />

                      </button>

                      {user?.role === 'admin' && (

                        <button onClick={() => handleDelete(policy.id)} className="text-red-600 hover:text-red-800">

                          <Trash2 className="w-4 h-4" />

                        </button>

                      )}

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      {/* Add/Edit Modal */}

      {showModal && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="flex items-center justify-between mb-4">

              <h2 className="text-xl font-bold text-slate-800">

                {editingPolicy ? 'Edit Policy' : 'Add New Policy'}

              </h2>

              <button onClick={() => setShowModal(false)} className="text-[#888888] hover:text-slate-600">

                <X className="w-6 h-6" />

              </button>

            </div>

            <form onSubmit={handleSubmit} className="space-y-4">

              {user?.role === 'admin' && (

                <div>

                  <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>

                  <select

                    value={formData.client_id}

                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}

                    className="w-full border border-slate-300 rounded-lg px-3 py-2"

                    required

                  >

                    <option value="">Select Client</option>

                    {clients.map(client => (

                      <option key={client.id} value={client.id}>{client.company_name}</option>

                    ))}

                  </select>

                </div>

              )}

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Policy Number</label>

                <input

                  type="text"

                  value={formData.policy_number}

                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  required

                />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Policy Year</label>

                <select

                  value={formData.policy_year}

                  onChange={(e) => setFormData({ ...formData, policy_year: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                >

                  <option value="">Select Policy Year</option>

                  <option value="2020-2021">2020-2021</option>

                  <option value="2021-2022">2021-2022</option>

                  <option value="2022-2023">2022-2023</option>

                  <option value="2023-2024">2023-2024</option>

                  <option value="2024-2025">2024-2025</option>

                  <option value="2025-2026">2025-2026</option>

                  <option value="2026-2027">2026-2027</option>

                  <option value="2027-2028">2027-2028</option>

                  <option value="2028-2029">2028-2029</option>

                  <option value="2029-2030">2029-2030</option>

                </select>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Interest / Subject Matter Insured</label>

                <input

                  type="text"

                  value={formData.interest_subject_matter}

                  onChange={(e) => setFormData({ ...formData, interest_subject_matter: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                />

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>

                <select

                  value={formData.category_id}

                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  required

                >

                  <option value="">Select Category</option>

                  {mainCategories.map(cat => (

                    <optgroup key={cat.id} label={cat.name}>

                      {getSubcategories(cat.id).length > 0 ? (

                        getSubcategories(cat.id).map(sub => (

                          <option key={sub.id} value={sub.id}>{sub.name}</option>

                        ))

                      ) : (

                        <option value={cat.id}>{cat.name}</option>

                      )}

                    </optgroup>

                  ))}

                </select>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Insurer</label>

                <input

                  type="text"

                  value={formData.insurer}

                  onChange={(e) => setFormData({ ...formData, insurer: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                />

              </div>

              <div className="grid grid-cols-2 gap-4">

                <div>

                  <label className="block text-sm font-medium text-slate-700 mb-1">Sum Insured ($)</label>

                  <input

                    type="number"

                    value={formData.sum_insured}

                    onChange={(e) => setFormData({ ...formData, sum_insured: e.target.value })}

                    className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-slate-700 mb-1">Annual Premium ($)</label>

                  <input

                    type="number"

                    value={formData.annual_premium}

                    onChange={(e) => setFormData({ ...formData, annual_premium: e.target.value })}

                    className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  />

                </div>

              </div>

              <div className="grid grid-cols-2 gap-4">

                <div>

                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>

                  <input

                    type="date"

                    value={formData.start_date}

                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}

                    className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>

                  <input

                    type="date"

                    value={formData.expiry_date}

                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}

                    className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  />

                </div>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>

                <select

                  value={formData.status}

                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                >

                  <option value="active">Active</option>

                  <option value="pending">Pending</option>

                  <option value="expired">Expired</option>

                </select>

              </div>

              <div>

                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>

                <textarea

                  value={formData.notes}

                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}

                  className="w-full border border-slate-300 rounded-lg px-3 py-2"

                  rows="3"

                />

              </div>

              <div className="flex gap-3 pt-4">

                <button

                  type="button"

                  onClick={() => setShowModal(false)}

                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"

                >

                  Cancel

                </button>

                <button

                  type="submit"

                  className="flex-1 px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700"

                >

                  {editingPolicy ? 'Update' : 'Create'}

                </button>

              </div>

            </form>

          </div>

        </div>

      )}



      {/* Policy Detail Modal */}

      {policyDetail.open && policyDetail.policy && (

        <div className="fixed inset-0 z-50 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

          <div className="flex items-center justify-center min-h-screen p-4">

            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>

              <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#E5E5E5', backgroundColor: '#2D2D2D' }}>

                <div className="flex items-center gap-4">

                  <div className="p-2.5 rounded-xl" style={{ backgroundColor: '#C45C26' }}><FileText className="w-6 h-6 text-white" /></div>

                  <div><h3 className="font-bold text-xl text-white">Policy Details</h3><p className="text-sm text-gray-400">{policyDetail.policy.policy_number}</p></div>

                </div>

                <button onClick={() => setPolicyDetail({ open: false, policy: null })} className="p-2 rounded-lg hover:bg-white/10 transition-colors"><X className="w-6 h-6 text-white" /></button>

              </div>

              <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>

                <div className="flex items-center gap-3 mb-6">

                  <span className="px-4 py-2 rounded-full text-sm font-bold" style={{ backgroundColor: policyDetail.policy.status === 'active' ? '#D1FAE5' : '#FEE2E2', color: policyDetail.policy.status === 'active' ? '#065F46' : '#991B1B' }}>{policyDetail.policy.status === 'active' ? '● Active' : '● ' + (policyDetail.policy.status || 'Inactive')}</span>

                  <span className="text-sm" style={{ color: '#6B6B6B' }}>{policyDetail.policy.client_name || 'Unknown Client'}</span>

                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">

                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Number</p><p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_number}</p></div>

                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5F0E8' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Category</p><p className="text-lg font-bold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.category_name || '-'}</p></div>

                </div>

                <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Coverage Details</h4>

                <div className="grid grid-cols-2 gap-4 mb-6">

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Sum Insured</p><p className="text-xl font-bold" style={{ color: '#2D2D2D' }}>${parseFloat(policyDetail.policy.sum_insured || 0).toLocaleString()}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Annual Premium</p><p className="text-xl font-bold" style={{ color: '#C45C26' }}>${parseFloat(policyDetail.policy.annual_premium || 0).toLocaleString()}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Insurer</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.insurer || '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Interest / Subject Matter</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.interest_subject_matter || policyDetail.policy.subject_matter || '-'}</p></div>

                </div>

                <h4 className="font-bold mb-3" style={{ color: '#C45C26' }}>Important Dates</h4>

                <div className="grid grid-cols-3 gap-4 mb-6">

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Policy Year</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.policy_year || '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Start Date</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.start_date ? new Date(policyDetail.policy.start_date).toLocaleDateString() : '-'}</p></div>

                  <div className="p-4 rounded-xl border" style={{ borderColor: '#E5E5E5' }}><p className="text-xs font-bold uppercase mb-1" style={{ color: '#6B6B6B' }}>Expiry Date</p><p className="font-semibold" style={{ color: '#2D2D2D' }}>{policyDetail.policy.expiry_date ? new Date(policyDetail.policy.expiry_date).toLocaleDateString() : '-'}</p></div>

                </div>

              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ borderColor: '#E5E5E5' }}>

                <div className="flex items-center gap-3">

                  <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#C45C26' }}><MessageSquare className="w-4 h-4" /> AI Summary</button>

                  <button onClick={() => { setPolicyDetail({ open: false, policy: null }); navigate(`/clients/${policyDetail.policy.client_id}`); }} className="flex items-center gap-2 px-4 py-2 rounded-lg border" style={{ borderColor: '#E5E5E5', color: '#2D2D2D' }}><Building2 className="w-4 h-4" /> View Client</button>

                </div>

                <button onClick={() => setPolicyDetail({ open: false, policy: null })} className="px-6 py-2 rounded-lg text-white" style={{ backgroundColor: '#2D2D2D' }}>Close</button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>

  );

}


function DocumentsList() {

  const [documents, setDocuments] = useState([]);

  const [clients, setClients] = useState([]);

  const [categories, setCategories] = useState([]);

  const [policies, setPolicies] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [filterClient, setFilterClient] = useState("");

  const [filterCategory, setFilterCategory] = useState("");

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [uploading, setUploading] = useState(false);

  const [uploadData, setUploadData] = useState({ client_id: "", category_id: "", policy_id: "", description: "" });

  const [selectedFile, setSelectedFile] = useState(null);



  useEffect(() => { loadDocuments(); loadClients(); loadCategories(); }, []);



  useEffect(() => { if (uploadData.client_id) loadPolicies(uploadData.client_id); }, [uploadData.client_id]);



  const loadDocuments = async () => {

    try { setLoading(true); const data = await api.get("/documents"); setDocuments(data.documents || []); }

    catch (err) { console.error(err); } finally { setLoading(false); }

  };



  const loadClients = async () => {

    try { const data = await api.get("/admin/clients"); setClients(data.clients || []); } catch (err) { console.error(err); }

  };



  const loadCategories = async () => {

    try { const data = await api.get("/policies/categories"); setCategories(data.categories || []); } catch (err) { console.error(err); }

  };



  const loadPolicies = async (clientId) => {

    try { const data = await api.get("/policies?client_id=" + clientId); setPolicies(data.policies || []); } catch (err) { console.error(err); }

  };



  const filteredDocuments = documents.filter(doc => {

    const search = searchTerm.toLowerCase();

    const matchesSearch = !searchTerm || 

      doc.original_name?.toLowerCase().includes(search) || 

      doc.description?.toLowerCase().includes(search) ||

      doc.client_name?.toLowerCase().includes(search) ||

      doc.category_name?.toLowerCase().includes(search) ||

      doc.policy_number?.toLowerCase().includes(search);

    const matchesClient = !filterClient || doc.client_id == filterClient;

    const matchesCategory = !filterCategory || doc.category_id == filterCategory;

    return matchesSearch && matchesClient && matchesCategory;

  });



  const handleUpload = async () => {

    if (!selectedFile || !uploadData.client_id || !uploadData.category_id) { alert("Please select a file, client, and category"); return; }

    try {

      setUploading(true);

      const formData = new FormData();

      formData.append("file", selectedFile);

      formData.append("client_id", uploadData.client_id);

      formData.append("category_id", uploadData.category_id);

      if (uploadData.policy_id) formData.append("policy_id", uploadData.policy_id);

      if (uploadData.description) formData.append("description", uploadData.description);

      const response = await fetch("/api/documents/upload", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("token") }, body: formData });

      const result = await response.json();

      if (result.success) { setShowUploadModal(false); setSelectedFile(null); setUploadData({ client_id: "", category_id: "", policy_id: "", description: "" }); loadDocuments(); alert("Document uploaded successfully"); }

      else { alert(result.message || "Upload failed"); }

    } catch (err) { alert("Upload failed: " + err.message); } finally { setUploading(false); }

  };



  const handleDownload = async (doc) => {

    try {

      const response = await api.get("/documents/" + doc.id + "/download");

      if (response.download_url) {

        window.open(response.download_url, "_blank");

      } else {

        alert("Download URL not available");

      }

    } catch (err) { alert("Download failed"); }

  };



  const handleDelete = async (id) => {

    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try { await api.delete("/documents/" + id); loadDocuments(); } catch (err) { alert("Delete failed"); }

  };



  const formatFileSize = (bytes) => {

    if (!bytes) return "0 B";

    const k = 1024; const sizes = ["B", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];

  };



  const getFileIcon = (fileType) => {

    if (fileType?.includes("pdf")) return "PDF";

    if (fileType?.includes("image")) return "IMG";

    if (fileType?.includes("word") || fileType?.includes("doc")) return "DOC";

    if (fileType?.includes("excel") || fileType?.includes("sheet")) return "XLS";

    return "FILE";

  };



  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-600" /></div>;



  return (

    <div className="space-y-6">

      <div className="flex justify-between items-center">

        <div><h1 className="text-2xl font-bold text-slate-800">Documents</h1><p className="text-slate-500">Manage all uploaded documents across clients and policies</p></div>

        <button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Upload className="w-5 h-5" /> Upload Document</button>

      </div>



      <div className="bg-white rounded-xl shadow-sm border p-4">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div className="relative md:col-span-2"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" /><input type="text" placeholder="Search by file name, client, category, policy..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">All Clients</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}</select>

          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">All Categories</option>{categories.map(c => <option key={c.id} value={c.id} style={{ fontWeight: !c.parent_id ? 'bold' : 'normal' }}>{c.parent_id ? '    ' + c.name : '▸ ' + c.name}</option>)}</select>

        </div>

      </div>



      <div className="bg-white rounded-xl shadow-sm border">

        <div className="p-4 border-b"><p className="text-sm text-slate-500">{filteredDocuments.length} document(s) found</p></div>

        {filteredDocuments.length === 0 ? (

          <div className="p-12 text-center"><FileText className="w-16 h-16 text-[#aaaaaa] mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-600 mb-2">No documents found</h3><p className="text-slate-500 mb-4">Upload your first document to get started</p><button onClick={() => setShowUploadModal(true)} className="inline-flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Upload className="w-5 h-5" /> Upload Document</button></div>

        ) : (

          <div className="divide-y">{filteredDocuments.map(doc => (

            <div key={doc.id} className="p-4 hover:bg-slate-50 flex items-center justify-between">

              <div className="flex items-center gap-4 flex-1 min-w-0">

                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-xs font-bold text-slate-600">{getFileIcon(doc.file_type)}</div>

                <div className="min-w-0 flex-1">

                  <h3 className="font-medium text-slate-800 truncate">{doc.original_name}</h3>

                  <div className="flex flex-wrap gap-2 mt-1">

                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{doc.client_name || "Unknown Client"}</span>

                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{doc.category_name || "Uncategorized"}</span>

                    {doc.policy_number && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{doc.policy_number}</span>}

                  </div>

                  <p className="text-xs text-slate-500 mt-1">{formatFileSize(doc.file_size)} - Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}</p>

                </div>

              </div>

              <div className="flex items-center gap-2 ml-4">

                <button onClick={() => handleDownload(doc)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Download"><Download className="w-5 h-5" /></button>

                <button onClick={() => handleDelete(doc.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Deactivate"><Trash2 className="w-5 h-5" /></button>

              </div>

            </div>

          ))}</div>

        )}

      </div>



      {showUploadModal && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">

            <div className="p-6 border-b"><h2 className="text-xl font-bold text-slate-800">Upload Document</h2></div>

            <div className="p-6 space-y-4">

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Client *</label><select value={uploadData.client_id} onChange={e => setUploadData({...uploadData, client_id: e.target.value, policy_id: ""})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">Select a client...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}</select></div>

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Category *</label><select value={uploadData.category_id} onChange={e => setUploadData({...uploadData, category_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">Select a category...</option>{categories.map(c => <option key={c.id} value={c.id} style={{ fontWeight: !c.parent_id ? 'bold' : 'normal' }}>{c.parent_id ? '    ' + c.name : '▸ ' + c.name}</option>)}</select></div>

              {policies.length > 0 && <div><label className="block text-sm font-medium text-slate-700 mb-1">Policy (Optional)</label><select value={uploadData.policy_id} onChange={e => setUploadData({...uploadData, policy_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">No specific policy</option>{policies.map(p => <option key={p.id} value={p.id}>{p.policy_number} - {p.category_name}</option>)}</select></div>}

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Description (Optional)</label><input type="text" value={uploadData.description} onChange={e => setUploadData({...uploadData, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="Brief description" /></div>

              <div><label className="block text-sm font-medium text-slate-700 mb-1">File *</label><div className="border-2 border-dashed rounded-lg p-6 text-center"><input type="file" onChange={e => setSelectedFile(e.target.files[0])} className="hidden" id="doc-file-upload" /><label htmlFor="doc-file-upload" className="cursor-pointer">{selectedFile ? (<div><FileText className="w-12 h-12 text-blue-600 mx-auto mb-2" /><p className="font-medium text-slate-800">{selectedFile.name}</p><p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p></div>) : (<div><Upload className="w-12 h-12 text-[#888888] mx-auto mb-2" /><p className="text-slate-600">Click to select a file</p><p className="text-sm text-slate-500">PDF, DOC, XLS, Images up to 10MB</p></div>)}</label></div></div>

            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3"><button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">Cancel</button><button onClick={handleUpload} disabled={uploading} className="px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{uploading ? "Uploading..." : "Upload"}</button></div>

          </div>

        </div>

      )}

    </div>

  );

}




function UsersList() {

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [filterRole, setFilterRole] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "client", phone: "", company_name: "", is_active: true });
  const [existingClients, setExistingClients] = useState([]);
  const [existingVendors, setExistingVendors] = useState([]);



  useEffect(() => { loadUsers(); loadExistingClients(); loadExistingVendors(); }, []);



  const loadUsers = async () => {

    try { setLoading(true); const data = await api.get("/admin/users"); setUsers(data.users || []); }

    catch (err) { console.error(err); } finally { setLoading(false); }

  };
  const loadExistingClients = async () => {

    try { const data = await api.get("/admin/clients"); setExistingClients(data.clients || []); } catch(e) {}

  };

  const loadExistingVendors = async () => {

    try { const data = await api.get("/admin/vendors"); setExistingVendors(data.vendors || []); } catch(e) {}

  };




  const filteredUsers = users.filter(u => {

    const search = searchTerm.toLowerCase();

    const matchesSearch = !searchTerm || 

      u.name?.toLowerCase().includes(search) || 

      u.email?.toLowerCase().includes(search) || 

      u.company_name?.toLowerCase().includes(search) ||

      u.phone?.toLowerCase().includes(search) ||

      u.role?.toLowerCase().includes(search);

    const matchesRole = !filterRole || u.role === filterRole;

    return matchesSearch && matchesRole;

  });



  const resetForm = () => {

    setFormData({ name: "", email: "", password: "", role: "client", phone: "", company_name: "", is_active: true, existing_client_id: "" });

    setSelectedUser(null);

  };



  const handleSave = async () => {

    if (!formData.name || !formData.email) { alert("Name and email are required"); return; }

    // Password is auto-generated if not provided

    try {

      setSaving(true);

      if (selectedUser) {

        const updateData = { ...formData };

        if (!updateData.password) delete updateData.password;

        await api.put("/admin/users/" + selectedUser.id, updateData);

      } else if (formData.role === 'client' && formData.existing_client_id) {

        await api.post("/admin/clients/" + formData.existing_client_id + "/users", formData);

      } else if (formData.role === 'vendor' && formData.existing_client_id) {

        await api.post("/admin/vendors/" + formData.existing_client_id + "/users", formData);

      } else {

        await api.post("/admin/users", formData);

      }

      setShowModal(false); resetForm(); loadUsers();

    } catch (err) { alert(err.message || "Failed to save user"); } finally { setSaving(false); }

  };



  const handleEdit = (user) => {

    setSelectedUser(user);
    const matchedClient = user.role === "client" ? existingClients.find(c => c.company_name === user.company_name) : null; const matchedVendor = user.role === "vendor" ? existingVendors.find(v => v.company_name === user.company_name) : null; setFormData({ name: user.name || "", email: user.email || "", password: "", role: user.role || "client", phone: user.phone || "", company_name: user.company_name || "", is_active: user.is_active !== false, existing_client_id: String(matchedClient ? matchedClient.id : matchedVendor ? matchedVendor.id : "") });
    setFormData({ name: user.name || "", email: user.email || "", password: "", role: user.role || "client", phone: user.phone || "", company_name: user.company_name || "", is_active: user.is_active !== false, existing_client_id: String(user.client_id || user.vendor_id || "") });

    setShowModal(true);

  };



  const handleDelete = async (id) => {

    if (!window.confirm("Are you sure you want to deactivate this user?")) return;

    try { await api.delete("/admin/users/" + id); loadUsers(); } catch (err) { alert("Failed to delete user"); }

  };



  const getRoleBadge = (role) => {

    const styles = { admin: "bg-red-100 text-red-700", client: "bg-blue-100 text-blue-700", vendor: "bg-purple-100 text-purple-700" };

    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || "bg-slate-100 text-slate-700"}`}>{role}</span>;

  };



  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-600" /></div>;



  return (

    <div className="space-y-6">

      <div className="flex justify-between items-center">

        <div><h1 className="text-2xl font-bold text-slate-800">Users</h1><p className="text-slate-500">Manage system users and their access</p></div>

        <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 bg-[#C45C26] text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Plus className="w-5 h-5" /> Add User</button>

      </div>



      <div className="bg-white rounded-xl shadow-sm border p-4">

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="relative md:col-span-2"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" /><input type="text" placeholder="Search by name, email, company, phone, role..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">All Roles</option><option value="admin">Admin</option><option value="client">Client</option><option value="vendor">Vendor</option></select>

        </div>

      </div>



      <div className="bg-white rounded-xl shadow-sm border">

        <div className="p-4 border-b"><p className="text-sm text-slate-500">{filteredUsers.length} user(s) found</p></div>

        {filteredUsers.length === 0 ? (

          <div className="p-12 text-center"><Users className="w-16 h-16 text-[#aaaaaa] mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-600 mb-2">No users found</h3></div>

        ) : (

          <div className="divide-y">{filteredUsers.map(user => (

            <div key={user.id} className={"p-4 hover:bg-slate-50 flex items-center justify-between" + (!user.is_active ? " opacity-50" : "")}>

              <div className="flex items-center gap-4 flex-1">

                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">{user.name?.charAt(0)?.toUpperCase() || "U"}</div>

                <div className="flex-1">

                  <div className="flex items-center gap-2"><h3 className="font-medium text-slate-800">{user.name}</h3>{getRoleBadge(user.role)}<span className={`px-2 py-0.5 rounded text-xs ${user.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{user.is_active ? "Active" : "Inactive"}</span></div>

                  <p className="text-sm text-slate-500">{user.email}</p>

                  <div className="flex gap-4 text-xs text-[#888888] mt-1">{user.company_name && <span>{user.company_name}</span>}{user.last_login && <span>Last login: {new Date(user.last_login).toLocaleDateString()}</span>}</div>

                </div>

              </div>

              <div className="flex items-center gap-2">

                <button onClick={() => handleEdit(user)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" title="Edit"><Edit className="w-5 h-5" /></button>

                {user.role !== "admin" && <button onClick={() => handleDelete(user.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Deactivate"><Trash2 className="w-5 h-5" /></button>}

              </div>

            </div>

          ))}</div>

        )}

      </div>



      {showModal && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="p-6 border-b"><h2 className="text-xl font-bold text-slate-800">{selectedUser ? "Edit User" : "Add New User"}</h2></div>

            <div className="p-6 space-y-4">

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Name *</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="John Smith" /></div>

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email *</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="john@example.com" /></div>

              <div><label className="block text-sm font-medium text-slate-700 mb-1">"Password (optional - auto-generated)"</label><input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="••••••••" /></div>

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Role *</label><select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value, existing_client_id: ""})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="admin">Admin</option><option value="client">Client</option><option value="vendor">Vendor</option></select></div>

              {formData.role === "client" && existingClients.length > 0 && (

                <div><label className="block text-sm font-medium text-slate-700 mb-1">Assign to Client *</label><select value={formData.existing_client_id} onChange={e => { const client = existingClients.find(c => c.id === parseInt(e.target.value)); setFormData({...formData, existing_client_id: e.target.value, company_name: client ? client.company_name : ""}); }} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">-- Create New Client --</option>{existingClients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}</select><p className="text-xs text-slate-500 mt-1">{formData.existing_client_id ? "User will be added to this existing client" : "A new client record will be created"}</p></div>

              )}

              {formData.role === "vendor" && existingVendors.length > 0 && (

                <div><label className="block text-sm font-medium text-slate-700 mb-1">Assign to Vendor</label><select value={formData.existing_client_id} onChange={e => { const vendor = existingVendors.find(v => v.id === parseInt(e.target.value)); setFormData({...formData, existing_client_id: e.target.value, company_name: vendor ? vendor.company_name : ""}); }} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">-- Create New Vendor --</option>{existingVendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}</select><p className="text-xs text-slate-500 mt-1">{formData.existing_client_id ? "User will be added to this existing vendor" : "A new vendor record will be created"}</p></div>

              )}

              <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone</label><input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="+971 50 123 4567" /></div>

              {!selectedUser && !formData.existing_client_id && <div><label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label><input type="text" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="New Company Name" /></div>}

              <div className="flex items-center gap-2"><input type="checkbox" id="user_active" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 text-blue-600 rounded" /><label htmlFor="user_active" className="text-sm text-slate-700">Active</label></div>

            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3"><button onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg">Cancel</button><button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : (selectedUser ? "Update User" : "Create User")}</button></div>

          </div>

        </div>

      )}

    </div>

  );

}



function AIAssistant() {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const handleSend = async () => {
    if (!query.trim()) return;
    
    setMessages([...messages, { role: 'user', content: query }]);
    setLoading(true);
    
    try {
      const data = await api.post('/ai/query', { query });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response?.answer || 'No response' }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.' }]);
    } finally {
      setLoading(false);
      setQuery('');
    }
  };
  
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: '#C45C26' }}>Adam</h1>
      
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-[600px] flex flex-col">
        <div className="flex-1 p-6 overflow-y-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-slate-500 py-8">
              <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: '#C45C26', opacity: 0.6 }} />
              <p>Ask me anything about your insurance policies!</p>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] p-4 rounded-lg ${
                  msg.role === 'user' ? 'bg-[#C45C26] text-white' : 'bg-[#F5F0E8] text-slate-800 border border-[#C45C26] border-opacity-20'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 p-4 rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-slate-200">
          <div className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask about your policies..."
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#C45C26]"
            />
            <button
              onClick={handleSend}
              disabled={loading || !query.trim()}
              className="bg-[#C45C26] hover:bg-[#A34A1E] text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function SettingsPage() {

  const { user, setUser } = useAuth();

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState("profile");

  const [profileData, setProfileData] = useState({ name: "", email: "", phone: "", company_name: "" });

  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const [message, setMessage] = useState({ type: "", text: "" });



  useEffect(() => {

    if (user) {

      setProfileData({ name: user.name || "", email: user.email || "", phone: user.phone || "", company_name: user.company_name || "" });

    }

  }, [user]);



  const handleProfileSave = async () => {

    try {

      setSaving(true);

      setMessage({ type: "", text: "" });

      const response = await api.put("/auth/profile", profileData);

      if (response.success) {

        setUser({ ...user, ...profileData });

        setMessage({ type: "success", text: "Profile updated successfully" });

      }

    } catch (err) { setMessage({ type: "error", text: err.message || "Failed to update profile" }); }

    finally { setSaving(false); }

  };



  const handlePasswordChange = async () => {

    if (passwordData.new_password !== passwordData.confirm_password) {

      setMessage({ type: "error", text: "New passwords do not match" }); return;

    }

    if (passwordData.new_password.length < 6) {

      setMessage({ type: "error", text: "Password must be at least 6 characters" }); return;

    }

    try {

      setSaving(true);

      setMessage({ type: "", text: "" });

      const response = await api.put("/auth/password", { current_password: passwordData.current_password, new_password: passwordData.new_password });

      if (response.success) {

        setPasswordData({ current_password: "", new_password: "", confirm_password: "" });

        setMessage({ type: "success", text: "Password changed successfully" });

      }

    } catch (err) { setMessage({ type: "error", text: err.message || "Failed to change password" }); }

    finally { setSaving(false); }

  };



  const tabs = [

    { id: "profile", label: "Profile", icon: User },

    { id: "security", label: "Security", icon: Lock },

    { id: "system", label: "System Info", icon: Info }

  ];



  return (

    <div className="space-y-6">

      <div><h1 className="text-2xl font-bold text-slate-800">Settings</h1><p className="text-slate-500">Manage your account and system preferences</p></div>



      {message.text && (

        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>

          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}

          {message.text}

        </div>

      )}



      <div className="flex gap-2 border-b">

        {tabs.map(tab => (

          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>

            <tab.icon className="w-5 h-5" />{tab.label}

          </button>

        ))}

      </div>



      {activeTab === "profile" && (

        <div className="bg-white rounded-xl shadow-sm border p-6">

          <h2 className="text-lg font-semibold text-slate-800 mb-4">Profile Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label><input type="text" value={profileData.name} onChange={e => setProfileData({...profileData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label><input type="email" value={profileData.email} onChange={e => setProfileData({...profileData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone</label><input type="text" value={profileData.phone} onChange={e => setProfileData({...profileData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" placeholder="+971 50 123 4567" /></div>

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Company</label><input type="text" value={profileData.company_name} onChange={e => setProfileData({...profileData, company_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

          </div>

          <div className="mt-6"><button onClick={handleProfileSave} disabled={saving} className="px-6 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : "Save Changes"}</button></div>

        </div>

      )}



      {activeTab === "security" && (

        <div className="bg-white rounded-xl shadow-sm border p-6">

          <h2 className="text-lg font-semibold text-slate-800 mb-4">Change Password</h2>

          <div className="max-w-md space-y-4">

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label><input type="password" value={passwordData.current_password} onChange={e => setPasswordData({...passwordData, current_password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

            <div><label className="block text-sm font-medium text-slate-700 mb-1">New Password</label><input type="password" value={passwordData.new_password} onChange={e => setPasswordData({...passwordData, new_password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

            <div><label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label><input type="password" value={passwordData.confirm_password} onChange={e => setPasswordData({...passwordData, confirm_password: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

          </div>

          <div className="mt-6"><button onClick={handlePasswordChange} disabled={saving} className="px-6 py-2 bg-[#C45C26] text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? "Changing..." : "Change Password"}</button></div>

        </div>

      )}



      {activeTab === "system" && (

        <div className="bg-white rounded-xl shadow-sm border p-6">

          <h2 className="text-lg font-semibold text-slate-800 mb-4">System Information</h2>

          <div className="space-y-4">

            <div className="flex justify-between py-3 border-b"><span className="text-slate-600">Application</span><span className="font-medium">Pi.insure</span></div>

            <div className="flex justify-between py-3 border-b"><span className="text-slate-600">Version</span><span className="font-medium">1.0.0</span></div>

            <div className="flex justify-between py-3 border-b"><span className="text-slate-600">Your Role</span><span className="font-medium capitalize">{user?.role || "N/A"}</span></div>

            <div className="flex justify-between py-3 border-b"><span className="text-slate-600">Account Created</span><span className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</span></div>

            <div className="flex justify-between py-3"><span className="text-slate-600">Last Login</span><span className="font-medium">{user?.last_login ? new Date(user.last_login).toLocaleString() : "N/A"}</span></div>

          </div>

        </div>

      )}

    </div>

  );

}



function ActivityLog() {

  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [filterAction, setFilterAction] = useState("");

  const [filterUser, setFilterUser] = useState("");

  const [limit, setLimit] = useState(100);



  useEffect(() => { loadActivities(); }, [limit]);



  const loadActivities = async () => {

    try { setLoading(true); const data = await api.get("/admin/activity?limit=" + limit); setActivities(data.activity || []); }

    catch (err) { console.error(err); } finally { setLoading(false); }

  };



  const uniqueActions = [...new Set(activities.map(a => a.action))].sort();

  const uniqueUsers = [...new Set(activities.map(a => a.user_name))].filter(Boolean).sort();



  const filteredActivities = activities.filter(a => {

    const search = searchTerm.toLowerCase();

    const matchesSearch = !searchTerm || a.user_name?.toLowerCase().includes(search) || a.action?.toLowerCase().includes(search) || a.entity_type?.toLowerCase().includes(search) || JSON.stringify(a.details)?.toLowerCase().includes(search);

    const matchesAction = !filterAction || a.action === filterAction;

    const matchesUser = !filterUser || a.user_name === filterUser;

    return matchesSearch && matchesAction && matchesUser;

  });



  const getActionStyle = (action) => {

    if (action?.includes("login")) return "bg-green-100 text-green-700";

    if (action?.includes("upload")) return "bg-blue-100 text-blue-700";

    if (action?.includes("download")) return "bg-purple-100 text-purple-700";

    if (action?.includes("create") || action?.includes("add")) return "bg-emerald-100 text-emerald-700";

    if (action?.includes("update") || action?.includes("edit")) return "bg-amber-100 text-amber-700";

    if (action?.includes("delete")) return "bg-red-100 text-red-700";

    return "bg-slate-100 text-slate-700";

  };



  const formatAction = (action) => action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown";



  const formatDetails = (details) => {

    if (!details) return "";

    try { const d = typeof details === "string" ? JSON.parse(details) : details; return Object.entries(d).map(([k, v]) => k + ": " + v).join(", "); }

    catch { return String(details); }

  };



  const formatDate = (date) => {

    if (!date) return "";

    const d = new Date(date);

    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  };



  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-blue-600" /></div>;



  return (

    <div className="space-y-6">

      <div className="flex justify-between items-center">

        <div><h1 className="text-2xl font-bold text-slate-800">Activity Log</h1><p className="text-slate-500">Track all system activities and user actions</p></div>

        <button onClick={loadActivities} className="flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-200"><RefreshCw className="w-5 h-5" /> Refresh</button>

      </div>



      <div className="bg-white rounded-xl shadow-sm border p-4">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" /><input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]" /></div>

          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">All Actions</option>{uniqueActions.map(a => <option key={a} value={a}>{formatAction(a)}</option>)}</select>

          <select value={filterUser} onChange={e => setFilterUser(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="">All Users</option>{uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}</select>

          <select value={limit} onChange={e => setLimit(e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#C45C26]"><option value="50">Last 50</option><option value="100">Last 100</option><option value="200">Last 200</option><option value="500">Last 500</option></select>

        </div>

      </div>



      <div className="bg-white rounded-xl shadow-sm border">

        <div className="p-4 border-b"><p className="text-sm text-slate-500">{filteredActivities.length} activity record(s)</p></div>

        {filteredActivities.length === 0 ? (

          <div className="p-12 text-center"><Activity className="w-16 h-16 text-[#aaaaaa] mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-600 mb-2">No activity found</h3></div>

        ) : (

          <div className="divide-y max-h-[600px] overflow-y-auto">{filteredActivities.map(activity => (

            <div key={activity.id} className="p-4 hover:bg-slate-50">

              <div className="flex items-start gap-4">

                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActionStyle(activity.action)}`}><Activity className="w-5 h-5" /></div>

                <div className="flex-1 min-w-0">

                  <div className="flex items-center gap-2 flex-wrap">

                    <span className="font-medium text-slate-800">{activity.user_name || "System"}</span>

                    <span className={`px-2 py-0.5 rounded text-xs ${getActionStyle(activity.action)}`}>{formatAction(activity.action)}</span>

                    {activity.entity_type && <span className="text-xs text-slate-500">on {activity.entity_type}</span>}

                  </div>

                  {activity.details && <p className="text-sm text-slate-600 mt-1 truncate">{formatDetails(activity.details)}</p>}

                  <div className="flex gap-4 text-xs text-[#888888] mt-2">

                    <span>{formatDate(activity.created_at)}</span>

                    {activity.ip_address && <span>IP: {activity.ip_address.replace("::ffff:", "")}</span>}

                  </div>

                </div>

              </div>

            </div>

          ))}</div>

        )}

      </div>

    </div>

  );

}


// ============================================
// App Component
// ============================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          <Route path="/" element={<Navigate to="/dashboard" />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout><Dashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/clients" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout><ClientsList /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/clients/:id" element={
            <ProtectedRoute>
              <DashboardLayout><ClientDetail /></DashboardLayout>
            </ProtectedRoute>
          } />

          

          <Route path="/clients/:id/edit" element={

            <ProtectedRoute allowedRoles={['admin']}>

              <DashboardLayout><ClientEdit /></DashboardLayout>

            </ProtectedRoute>

          } />

          
          <Route path="/vendors" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout><VendorsList /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/policies/:id" element={

            <ProtectedRoute>

              <DashboardLayout><PolicyDetail /></DashboardLayout>

            </ProtectedRoute>

          } />

          <Route path="/policies" element={
            <ProtectedRoute>
              <DashboardLayout><PoliciesList /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/documents" element={
            <ProtectedRoute>
              <DashboardLayout><DocumentsList /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout><UsersList /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/activity" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout><ActivityLog /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/assistant" element={
            <ProtectedRoute allowedRoles={['client']}>
              <DashboardLayout><AIAssistant /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/my-vendors" element={
            <ProtectedRoute allowedRoles={['client']}>
              <DashboardLayout><ClientVendors clientId={null} vendors={[]} /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/my-clients" element={
            <ProtectedRoute allowedRoles={['vendor']}>
              <DashboardLayout><VendorDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <DashboardLayout><SettingsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
// cache bust Sun Jan 25 08:07:55 UTC 2026
